import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { useTheme, themeOptions, logoOptions } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import DataPagination from "../components/DataPagination";
import api from "../lib/api";
import { toast } from "sonner";
import { 
  Globe, Package, Users, Settings, LogOut, Menu, X, 
  Plus, Trash2, Edit, Copy, DollarSign, TrendingUp, 
  Coins, Bell, Shield, Wrench, Send, Ban, CheckCircle,
  AlertTriangle, RefreshCw, Sparkles, Code, Palette, Image,
  UserPlus, Key, Crown, ShieldCheck, UserX, Download, Link2, Wifi, WifiOff,
  Upload, HelpCircle, FileText, Clock, Database, MessageSquare, Megaphone,
  HeadphonesIcon, AlertCircle, XCircle, User, ChevronRight, ChevronDown, ExternalLink,
  Star, Rocket, Zap, Award, Search, Bot, Activity
} from "lucide-react";

// Badge definitions for admin
const BADGE_OPTIONS = [
  { id: "verified", name_tr: "Onaylı Kullanıcı", name_en: "Verified User", Icon: CheckCircle, color: "emerald" },
  { id: "vip", name_tr: "VIP Üye", name_en: "VIP Member", Icon: Crown, color: "purple" },
  { id: "gold", name_tr: "Altın Üye", name_en: "Gold Member", Icon: Star, color: "yellow" },
  { id: "premium", name_tr: "Premium", name_en: "Premium", Icon: Zap, color: "blue" },
  { id: "pioneer", name_tr: "Öncü", name_en: "Pioneer", Icon: Rocket, color: "orange" },
  { id: "star", name_tr: "Yıldız Müşteri", name_en: "Star Customer", Icon: Sparkles, color: "pink" }
];

const getBadgeColor = (badgeId) => {
  const badge = BADGE_OPTIONS.find(b => b.id === badgeId);
  if (!badge) return "gray";
  return badge.color;
};

// Dynamic Logo component using ThemeContext
const DynamicLogo = ({ size = "default" }) => {
  const { getLogo } = useTheme();
  const logo = getLogo();
  const sizeClasses = { small: "w-8 h-8", default: "w-10 h-10" };
  
  return (
    <div className={`${sizeClasses[size]} relative`}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full" />
      <div 
        className="relative w-full h-full flex items-center justify-center text-black"
        dangerouslySetInnerHTML={{ __html: logo.svg.replace('class="', 'class="w-3/4 h-3/4 ') }}
      />
    </div>
  );
};

// Permission list with labels
const PERMISSION_LABELS = {
  view_sites: "Siteleri Görüntüle",
  manage_sites: "Siteleri Yönet",
  create_orders: "Sipariş Oluştur",
  view_own_orders: "Kendi Siparişlerini Gör",
  manage_orders: "Siparişleri Yönet",
  buy_credits: "Kredi Satın Al",
  manage_packages: "Paketleri Yönet",
  manage_durations: "Süreleri Yönet",
  view_users: "Kullanıcıları Gör",
  manage_users: "Kullanıcıları Yönet",
  ban_users: "Kullanıcıları Banla",
  create_admins: "Admin Oluştur",
  delete_admins: "Admin Sil",
  view_stats: "İstatistikleri Gör",
  manage_settings: "Ayarları Yönet",
  send_notifications: "Bildirim Gönder",
  manage_permissions: "Yetkileri Yönet"
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { currentTheme, currentLogo, setTheme, setLogo, siteContent, updateSiteContent } = useTheme();
  const { language, t } = useLanguage();
  
  // URL'den tab state'i oku
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "overview";
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Tab değişince URL'i güncelle
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const url = new URL(window.location);
    url.searchParams.set("tab", tab);
    window.history.pushState({}, "", url);
  };
  
  const [stats, setStats] = useState(() => {
    try {
      const raw = localStorage.getItem("rootseo_admin_stats");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [sites, setSites] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [durations, setDurations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [seoServices, setSeoServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Site content editing states
  const [editingFeatures, setEditingFeatures] = useState([]);
  const [footerCredit, setFooterCredit] = useState("");
  const [telegramLink, setTelegramLink] = useState("");

  // Admin management states
  const [createAdminDialogOpen, setCreateAdminDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleEditUser, setRoleEditUser] = useState(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsEditUser, setPermissionsEditUser] = useState(null);

  // Forms
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({
    name: "", url: "", category: "", language: "", credit_cost: "1", 
    link_type: "dofollow", placement_method: "php",
    da: "", pa: "", spam_score: "", traffic: "", connector_url: ""
  });

  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: "", credits: "", price: "", bonus_credits: "0",
    is_featured: false, template: "default", description: "",
    product_type: "subscription", days: ""
  });

  const [durationDialogOpen, setDurationDialogOpen] = useState(false);
  const [durationForm, setDurationForm] = useState({ name: "", days: "", credit_multiplier: "1.0" });
  const [editingDuration, setEditingDuration] = useState(null);
  const [durationDeleteConfirmOpen, setDurationDeleteConfirmOpen] = useState(false);
  const [durationToDelete, setDurationToDelete] = useState(null);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ 
    name: "", 
    email: "", 
    credits: "", 
    is_banned: false,
    newPassword: ""
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Badge dialog state
  const [badgeDialogOpen, setBadgeDialogOpen] = useState(false);
  const [badgeUser, setBadgeUser] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState("");
  
  // Subscription dialog state
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  const [subscriptionDays, setSubscriptionDays] = useState("");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const [telegramForm, setTelegramForm] = useState({ bot_token: "", chat_id: "", notifications_enabled: true });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // System settings states (Super Admin only)
  const [systemSettings, setSystemSettings] = useState(null);
  const [nowPaymentsForm, setNowPaymentsForm] = useState({ api_key: "", ipn_secret: "" });
  const [systemTelegramForm, setSystemTelegramForm] = useState({ bot_token: "", chat_id: "", notifications_enabled: true });
  const [savingSystemSettings, setSavingSystemSettings] = useState(false);

  // SEO Services states
  const [seoServiceDialogOpen, setSeoServiceDialogOpen] = useState(false);
  const [editingSeoService, setEditingSeoService] = useState(null);
  const [seoServiceForm, setSeoServiceForm] = useState({
    name: "", service_type: "index", credit_cost: "5", description: "",
    is_auto: true, target_countries: ""
  });

  // SEO API Settings states
  const [seoApiSettings, setSeoApiSettings] = useState(null);
  const [seoApiForm, setSeoApiForm] = useState({
    moz_access_id: "", moz_secret_key: "",
    ahrefs_api_key: "", semrush_api_key: "",
    dataforseo_login: "", dataforseo_password: "",
    google_indexing_api_key: "", indexnow_api_key: ""
  });
  const [connectorSettings, setConnectorSettings] = useState({
    default_upload_path: "",
    connector_filename: "seo-config.php"
  });

  // Bulk add and pending sites states
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [bulkAddMode, setBulkAddMode] = useState("connector"); // connector or webshell
  const [bulkPhpText, setBulkPhpText] = useState("");
  const [bulkJsText, setBulkJsText] = useState("");
  const [bulkAddLoading, setBulkAddLoading] = useState(false);
  const [pendingSites, setPendingSites] = useState([]);
  const [showPendingSites, setShowPendingSites] = useState(false);
  const [pendingPage, setPendingPage] = useState(1); // Sayfalama için
  const [deletingOrders, setDeletingOrders] = useState({});
  const [retryingAllPending, setRetryingAllPending] = useState(false);
  
  // Bulk Add: tek mod (async, arka planda + otomatik bağlantı)
  const [bulkOperations, setBulkOperations] = useState([]);
  const [activeBulkOp, setActiveBulkOp] = useState(null);
  const [bulkOpPolling, setBulkOpPolling] = useState(false);
  
  // Pending sites pagination
  const PENDING_PER_PAGE = 20;
  const pendingTotalPages = Math.ceil(pendingSites.length / PENDING_PER_PAGE);
  const paginatedPendingSites = pendingSites.slice(
    (pendingPage - 1) * PENDING_PER_PAGE,
    pendingPage * PENDING_PER_PAGE
  );

  // Bulk selection states
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  // Package editing states
  const [editingPackage, setEditingPackage] = useState(null);

  // Pagination states
  const [sitesPage, setSitesPage] = useState(1);
  const [sitesTotal, setSitesTotal] = useState(0);
  const [sitesTotalPages, setSitesTotalPages] = useState(1);
  const [sitesSearch, setSitesSearch] = useState("");
  const [sitesSortBy, setSitesSortBy] = useState(() => localStorage.getItem("rootseo_sites_sort_by") || "da_pa");
  const [sitesSortOrder, setSitesSortOrder] = useState("desc");
  const [includeInactiveSites, setIncludeInactiveSites] = useState(false);
  const [sitesStats, setSitesStats] = useState(() => {
    try {
      const raw = localStorage.getItem("rootseo_admin_sites_stats");
      return raw ? JSON.parse(raw) : { total: 0, active: 0, inactive: 0, connected: 0, disconnected: 0, legacy_generation: 0, modern_generation: 0, family_v5: 0, family_v5js: 0 };
    } catch {
      return { total: 0, active: 0, inactive: 0, connected: 0, disconnected: 0, legacy_generation: 0, modern_generation: 0, family_v5: 0, family_v5js: 0 };
    }
  });
  const [deletingInactive, setDeletingInactive] = useState(false);
  const [refreshingConnections, setRefreshingConnections] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });
  const refreshProgressRef = useRef({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });
  const [syncingKeys, setSyncingKeys] = useState(false);
  const [keySyncOperationId, setKeySyncOperationId] = useState(null);
  const [cleaningAllLinks, setCleaningAllLinks] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });

  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [ordersSearch, setOrdersSearch] = useState("");
  
  // Grouped orders by customer
  const [groupedCustomers, setGroupedCustomers] = useState([]);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [customerOrders, setCustomerOrders] = useState({});
  const [customerOrdersPage, setCustomerOrdersPage] = useState({});
  const [customerOrdersTotalPages, setCustomerOrdersTotalPages] = useState({});
  const [customerOrdersSearch, setCustomerOrdersSearch] = useState({});
  const CUSTOMER_ORDERS_PER_PAGE = 20;

  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");

  // Tickets states
  const [tickets, setTickets] = useState([]);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketsTotalPages, setTicketsTotalPages] = useState(1);
  const [ticketsSearch, setTicketsSearch] = useState("");
  const [ticketsStatusFilter, setTicketsStatusFilter] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [ticketReplyMessage, setTicketReplyMessage] = useState("");
  const [sendingTicketReply, setSendingTicketReply] = useState(false);

  // Announcements states
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsPage, setAnnouncementsPage] = useState(1);
  const [announcementsTotal, setAnnouncementsTotal] = useState(0);
  const [announcementsTotalPages, setAnnouncementsTotalPages] = useState(1);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "", title_en: "", content: "", content_en: "",
    type: "info", is_active: true, show_on_dashboard: true, expires_at: ""
  });

  // Support settings states
  const [supportSettings, setSupportSettings] = useState({
    telegram_usernames: [],
    support_email: "",
    working_hours: "09:00 - 18:00",
    response_time: "24 saat içinde"
  });
  const [supportTelegramInput, setSupportTelegramInput] = useState("");

  // Link Check states (replaced refund requests)
  const [linkCheckItems, setLinkCheckItems] = useState([]);
  const [linkCheckStats, setLinkCheckStats] = useState({ total: 0, untested: 0, tested: 0, success: 0, failed_404: 0, failed_permission: 0, failed_connector: 0, fixable: 0, unfixable: 0 });
  const [linkCheckPage, setLinkCheckPage] = useState(1);
  const [linkCheckTotal, setLinkCheckTotal] = useState(0);
  const [linkCheckTotalPages, setLinkCheckTotalPages] = useState(1);
  const [linkCheckStatusFilter, setLinkCheckStatusFilter] = useState("");
  const [linkCheckSearch, setLinkCheckSearch] = useState("");
  const [processingLinkCheck, setProcessingLinkCheck] = useState({});
  const [linkCheckTestingAll, setLinkCheckTestingAll] = useState(false);
  const [linkCheckOperationId, setLinkCheckOperationId] = useState(null);
  const [linkCheckProgress, setLinkCheckProgress] = useState({ progress_percent: 0, processed: 0, total: 0, success: 0, failed: 0, rate_per_second: 0 });
  const [transferringFailed, setTransferringFailed] = useState(false);
  
  // Enhanced order stats
  const [enhancedOrderStats, setEnhancedOrderStats] = useState({ total_orders: 0, active_orders: 0, failed_orders: 0, unique_failed_sites: 0 });

  // Refund stats
  const [refundStats, setRefundStats] = useState({ total_refunded: 0, total_amount: 0 });
  const [expiringSubscriptions, setExpiringSubscriptions] = useState({ threshold_days: [7, 3, 1], items: [], total: 0 });
  const [expiringSubscriptionsPage, setExpiringSubscriptionsPage] = useState(1);
  const [expiringSubscriptionsTotalPages, setExpiringSubscriptionsTotalPages] = useState(1);

  const ITEMS_PER_PAGE = 20;
  const initialFetchDone = useRef(false);
  const initialSitesSearchEffectDone = useRef(false);

  // Load site content when it changes
  useEffect(() => {
    if (siteContent) {
      setEditingFeatures(siteContent.features || []);
      setFooterCredit(siteContent.footer?.credit || "moderated by AnubisROOT-NoxShell");
      setTelegramLink(siteContent.footer?.telegramLink || "");
    }
  }, [siteContent]);

  // Fetch sites with pagination and sorting
  const fetchSites = useCallback(async (
    page = 1,
    search = "",
    sortBy = sitesSortBy,
    sortOrder = sitesSortOrder,
    includeInactive = includeInactiveSites
  ) => {
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE, sort_by: sortBy, sort_order: sortOrder });
      if (search) params.append("search", search);
      if (includeInactive) params.append("include_inactive", "true");
      const res = await api.get(`/sites?${params}`);
      setSites(res.data.sites || []);
      setSitesTotal(res.data.total || 0);
      setSitesTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error("Sites fetch error:", error);
    }
  }, [includeInactiveSites, sitesSortBy, sitesSortOrder]);

  // Fetch site stats with retry
  const fetchSitesStats = useCallback(async (retryCount = 0) => {
    try {
      const res = await api.get("/admin/sites/stats");
      if (res.data && typeof res.data.total === 'number') {
        setSitesStats(res.data);
        localStorage.setItem("rootseo_admin_sites_stats", JSON.stringify(res.data));
      } else if (retryCount < 2) {
        // Retry if data seems invalid
        setTimeout(() => fetchSitesStats(retryCount + 1), 1000);
      }
    } catch (error) {
      console.error("Sites stats fetch error:", error);
      // Retry on error
      if (retryCount < 2) {
        setTimeout(() => fetchSitesStats(retryCount + 1), 1500);
      }
    }
  }, []);

  // Fetch orders grouped by customer
  const fetchGroupedOrders = useCallback(async (page = 1, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search) params.append("search", search);
      const res = await api.get(`/admin/orders/grouped-by-customer?${params}`);
      setGroupedCustomers(res.data.customers || []);
      setOrdersTotal(res.data.total || 0);
      setOrdersTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error("Grouped orders fetch error:", error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "orders" && groupedCustomers.length === 0) {
      fetchGroupedOrders(ordersPage, ordersSearch);
    }
  }, [activeTab, groupedCustomers.length, ordersPage, ordersSearch, fetchGroupedOrders]);

  useEffect(() => {
    if (activeTab !== "orders") return;
    let cancelled = false;
    const loadEnhancedStats = async () => {
      try {
        const res = await api.get("/admin/orders/enhanced-stats");
        if (!cancelled) {
          setEnhancedOrderStats(res.data || { total_orders: 0, active_orders: 0, failed_orders: 0, unique_failed_sites: 0 });
        }
      } catch (error) {
        console.error("Enhanced order stats fetch error:", error);
      }
    };
    loadEnhancedStats();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // Fetch orders for a specific customer
  const fetchCustomerOrders = useCallback(async (userId, page = 1, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit: CUSTOMER_ORDERS_PER_PAGE });
      if (search) params.append("search", search);
      const res = await api.get(`/admin/orders/by-customer/${userId}?${params}`);
      
      setCustomerOrders(prev => ({ ...prev, [userId]: res.data.orders || [] }));
      setCustomerOrdersTotalPages(prev => ({ ...prev, [userId]: res.data.pages || 1 }));
    } catch (error) {
      console.error("Customer orders fetch error:", error);
    }
  }, []);

  // Toggle customer expansion
  const toggleCustomerExpand = async (userId) => {
    const isExpanded = expandedCustomers[userId];
    setExpandedCustomers(prev => ({ ...prev, [userId]: !isExpanded }));
    
    if (!isExpanded && !customerOrders[userId]) {
      setCustomerOrdersPage(prev => ({ ...prev, [userId]: 1 }));
      setCustomerOrdersSearch(prev => ({ ...prev, [userId]: "" }));
      await fetchCustomerOrders(userId, 1, "");
    }
  };

  // Handle customer orders page change
  const handleCustomerOrdersPageChange = async (userId, newPage) => {
    setCustomerOrdersPage(prev => ({ ...prev, [userId]: newPage }));
    const search = customerOrdersSearch[userId] || "";
    await fetchCustomerOrders(userId, newPage, search);
  };

  // Handle customer orders search
  const handleCustomerOrdersSearch = async (userId, searchValue) => {
    setCustomerOrdersSearch(prev => ({ ...prev, [userId]: searchValue }));
    if (window[`customerSearchTimeout_${userId}`]) {
      clearTimeout(window[`customerSearchTimeout_${userId}`]);
    }
    window[`customerSearchTimeout_${userId}`] = setTimeout(async () => {
      setCustomerOrdersPage(prev => ({ ...prev, [userId]: 1 }));
      await fetchCustomerOrders(userId, 1, searchValue);
    }, 300);
  };

  // Legacy fetch orders (keep for compatibility)
  const fetchOrders = useCallback(async (page = 1, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search) params.append("search", search);
      const res = await api.get(`/orders?${params}`);
      setOrders(res.data.orders || []);
      setOrdersTotal(res.data.total || 0);
      setOrdersTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error("Orders fetch error:", error);
    }
  }, []);

  // Fetch users with pagination
  const fetchUsers = useCallback(async (page = 1, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search) params.append("search", search);
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
      setUsersTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error("Users fetch error:", error);
    }
  }, []);

  // Fetch tickets with pagination
  const fetchTickets = useCallback(async (page = 1, search = "", status = "") => {
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      const res = await api.get(`/admin/tickets?${params}`);
      setTickets(res.data.tickets || []);
      setTicketsTotal(res.data.total || 0);
      setTicketsTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error("Tickets fetch error:", error);
    }
  }, []);

  // Fetch announcements with pagination
  const fetchAnnouncements = useCallback(async (page = 1) => {
    try {
      const res = await api.get(`/admin/announcements?page=${page}&limit=${ITEMS_PER_PAGE}`);
      setAnnouncements(res.data.announcements || []);
      setAnnouncementsTotal(res.data.total || 0);
      setAnnouncementsTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error("Announcements fetch error:", error);
    }
  }, []);

  // Fetch support settings
  const fetchSupportSettings = useCallback(async () => {
    try {
      const res = await api.get("/admin/system-settings/support");
      setSupportSettings(res.data);
      setSupportTelegramInput((res.data.telegram_usernames || []).join("\n"));
    } catch (error) {
      console.error("Support settings fetch error:", error);
    }
  }, []);

  const fetchExpiringSubscriptions = useCallback(async (page = 1) => {
    try {
      const res = await api.get(`/admin/subscriptions/expiring?page=${page}&limit=10`);
      setExpiringSubscriptions(res.data || { threshold_days: [7, 3, 1], items: [], total: 0 });
      setExpiringSubscriptionsTotalPages(res.data?.pages || 1);
    } catch (error) {
      console.error("Expiring subscriptions fetch error:", error);
    }
  }, []);

  // Handle search with debounce
  useEffect(() => {
    if (!initialSitesSearchEffectDone.current) {
      initialSitesSearchEffectDone.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetchSites(1, sitesSearch);
      setSitesPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [sitesSearch, fetchSites]);

  useEffect(() => {
    fetchExpiringSubscriptions(expiringSubscriptionsPage);
  }, [expiringSubscriptionsPage, fetchExpiringSubscriptions]);

  useEffect(() => {
    if (activeTab !== "orders") return undefined;
    const timer = setTimeout(() => {
      fetchGroupedOrders(1, ordersSearch);
      setOrdersPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab, ordersSearch, fetchGroupedOrders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1, usersSearch);
      setUsersPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [usersSearch, fetchUsers]);

  // Tickets search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTickets(1, ticketsSearch, ticketsStatusFilter);
      setTicketsPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [ticketsSearch, ticketsStatusFilter, fetchTickets]);

  // Fetch Link Check stats
  const fetchLinkCheckStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/link-check/stats");
      setLinkCheckStats(res.data);
    } catch (error) {
      console.error("Fetch link check stats error:", error);
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    // Hide fullscreen loader immediately; background fetches can take longer.
    setLoading(false);

    const safeGet = async (url, fallback, timeout = 8000) => {
      try {
        const res = await api.get(url, { timeout });
        return res.data;
      } catch {
        return fallback;
      }
    };

    try {
      // Critical first paint data in a single API call.
      const bootstrapData = await safeGet(
        `/admin/bootstrap?page=1&limit=${ITEMS_PER_PAGE}&sort_by=${sitesSortBy}&sort_order=${sitesSortOrder}${includeInactiveSites ? "&include_inactive=true" : ""}`,
        null,
        12000
      );

      if (bootstrapData?.stats && bootstrapData?.sites && bootstrapData?.users) {
        const statsData = bootstrapData.stats;
        const sitesData = bootstrapData.sites;
        const usersData = bootstrapData.users;
        setStats(statsData || {});
        localStorage.setItem("rootseo_admin_stats", JSON.stringify(statsData || {}));
        setSites(sitesData.sites || []);
        setSitesTotal(sitesData.total || 0);
        setSitesTotalPages(sitesData.pages || 1);
        setUsers(usersData.users || []);
        setUsersTotal(usersData.total || 0);
        setUsersTotalPages(usersData.pages || 1);
      } else {
        // Fallback to old flow if bootstrap is unavailable.
        const [statsData, sitesData, usersData] = await Promise.all([
          safeGet("/admin/stats", null, 8000),
          safeGet(`/sites?page=1&limit=${ITEMS_PER_PAGE}&sort_by=${sitesSortBy}&sort_order=${sitesSortOrder}${includeInactiveSites ? "&include_inactive=true" : ""}`, { sites: [], total: 0, pages: 1 }, 8000),
          safeGet(`/admin/users?page=1&limit=${ITEMS_PER_PAGE}`, { users: [], total: 0, pages: 1 }, 8000),
        ]);
        if (statsData) {
          setStats(statsData);
          localStorage.setItem("rootseo_admin_stats", JSON.stringify(statsData));
        }
        setSites(sitesData.sites || []);
        setSitesTotal(sitesData.total || 0);
        setSitesTotalPages(sitesData.pages || 1);
        setUsers(usersData.users || []);
        setUsersTotal(usersData.total || 0);
        setUsersTotalPages(usersData.pages || 1);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      // Always stop full-screen loader early; remaining calls continue in background.
      setLoading(false);
    }

    // Non-critical data: hydrate in background to keep dashboard responsive.
    Promise.all([
      safeGet("/durations", [], 10000).then((data) => setDurations(data || [])),
      safeGet("/packages", [], 10000).then((data) => setPackages(data || [])),
      safeGet("/seo-services", [], 10000).then((data) => setSeoServices(data || [])),
      safeGet("/admin/settings", {}, 10000).then((data) => {
        setSettings(data || {});
        if (data?.telegram) setTelegramForm(data.telegram);
        if (data?.maintenance_mode !== undefined) setMaintenanceMode(data.maintenance_mode);
      }),
      safeGet("/admin/sites/pending", { sites: [] }, 10000).then((data) => setPendingSites(data.sites || [])),
      safeGet(`/admin/tickets?page=1&limit=${ITEMS_PER_PAGE}`, { tickets: [], total: 0, pages: 1 }, 10000).then((data) => {
        setTickets(data.tickets || []);
        setTicketsTotal(data.total || 0);
        setTicketsTotalPages(data.pages || 1);
      }),
      safeGet(`/admin/announcements?page=1&limit=${ITEMS_PER_PAGE}`, { announcements: [], total: 0, pages: 1 }, 10000).then((data) => {
        setAnnouncements(data.announcements || []);
        setAnnouncementsTotal(data.total || 0);
        setAnnouncementsTotalPages(data.pages || 1);
      }),
      safeGet("/admin/sites/stats", { total: 0, active: 0, inactive: 0, connected: 0, disconnected: 0 }, 10000).then((data) => setSitesStats(data)),
      safeGet("/admin/refund-requests/stats", { pending: 0, approved: 0, rejected: 0, pending_credits: 0 }, 10000).then((data) => setRefundStats(data)),
      safeGet("/admin/subscriptions/expiring?page=1&limit=10", { threshold_days: [7, 3, 1], items: [], total: 0, pages: 1 }, 10000).then((data) => {
        setExpiringSubscriptions(data);
        setExpiringSubscriptionsTotalPages(data?.pages || 1);
        setExpiringSubscriptionsPage(1);
      }),
    ])
      .then(() => fetchLinkCheckStats())
      .catch((error) => {
        console.error("Background fetch error:", error);
      });
  }, [fetchLinkCheckStats, includeInactiveSites, sitesSortBy, sitesSortOrder]);

  // Fetch Link Check items
  const fetchLinkCheckItems = useCallback(async (page = 1, status = "", search = "") => {
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      
      const response = await api.get(`/admin/link-check/items?${params}`);
      setLinkCheckItems(response.data.items || []);
      setLinkCheckTotal(response.data.total || 0);
      setLinkCheckTotalPages(response.data.pages || 1);
      
      // Also refresh stats
      fetchLinkCheckStats();
    } catch (error) {
      console.error("Fetch link check items error:", error);
    }
  }, [fetchLinkCheckStats]);

  // Link Check - load when tab is active
  useEffect(() => {
    if (activeTab === "linkcheck") {
      fetchLinkCheckItems(linkCheckPage, linkCheckStatusFilter, linkCheckSearch);
      fetchLinkCheckStats();
    }
  }, [activeTab, fetchLinkCheckItems, fetchLinkCheckStats, linkCheckPage, linkCheckSearch, linkCheckStatusFilter]);

  // Link Check search effect
  useEffect(() => {
    if (activeTab !== "linkcheck") return undefined;
    const timer = setTimeout(() => {
      fetchLinkCheckItems(1, linkCheckStatusFilter, linkCheckSearch);
      setLinkCheckPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab, fetchLinkCheckItems, linkCheckSearch, linkCheckStatusFilter]);

  // Transfer failed orders to link check
  const handleTransferFailedOrders = async (userId = null) => {
    setTransferringFailed(true);
    try {
      const res = await api.post("/admin/link-check/transfer-failed", userId ? { user_id: userId } : {});
      toast.success(res.data.message || (language === "tr" ? "Başarısız siparişler aktarıldı" : "Failed orders transferred"));
      fetchLinkCheckItems(1, linkCheckStatusFilter, linkCheckSearch);
      fetchLinkCheckStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Aktarma başarısız" : "Transfer failed"));
    } finally {
      setTransferringFailed(false);
    }
  };

  // Test all link checks
  const handleTestAllLinkChecks = async () => {
    setLinkCheckTestingAll(true);
    try {
      const res = await api.post("/admin/link-check/test-all");
      toast.success(res.data.message || (language === "tr" ? "Test başlatıldı" : "Testing started"));
      setLinkCheckOperationId(res.data.operation_id);
      // Start polling for operation status
      pollLinkCheckOperation(res.data.operation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Test başlatılamadı" : "Failed to start test"));
      setLinkCheckTestingAll(false);
    }
  };

  // Reset and test all link checks
  const handleResetAndTestAll = async () => {
    const ok = window.confirm(
      language === "tr"
        ? `Tüm ${linkCheckStats.total} öğeyi yeniden test etmek istediğinize emin misiniz? Bu işlem biraz zaman alabilir.`
        : `Are you sure you want to retest all ${linkCheckStats.total} items? This may take some time.`
    );
    if (!ok) return;
    
    setLinkCheckTestingAll(true);
    try {
      const res = await api.post("/admin/link-check/reset-and-test-all");
      toast.success(res.data.message || (language === "tr" ? "Yeniden test başlatıldı" : "Retest started"));
      setLinkCheckOperationId(res.data.operation_id);
      pollLinkCheckOperation(res.data.operation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Test başlatılamadı" : "Failed to start test"));
      setLinkCheckTestingAll(false);
    }
  };

  // Poll link check operation status
  const pollLinkCheckOperation = async (operationId) => {
    try {
      const res = await api.get(`/admin/link-check/operation/${operationId}`);
      
      // Progress bilgisini güncelle
      setLinkCheckProgress({
        progress_percent: res.data.progress_percent || 0,
        processed: res.data.processed || 0,
        total: res.data.total || 0,
        success: res.data.success || 0,
        failed: res.data.failed || 0,
        rate_per_second: res.data.rate_per_second || 0,
        elapsed_seconds: res.data.elapsed_seconds || 0,
        estimated_remaining_seconds: res.data.estimated_remaining_seconds
      });
      
      if (res.data.status === "completed" || res.data.status === "failed") {
        setLinkCheckTestingAll(false);
        setLinkCheckOperationId(null);
        setLinkCheckProgress({ progress_percent: 0, processed: 0, total: 0, success: 0, failed: 0, rate_per_second: 0 });
        fetchLinkCheckItems(linkCheckPage, linkCheckStatusFilter, linkCheckSearch);
        fetchLinkCheckStats();
        toast.success(
          language === "tr" 
            ? `Test tamamlandı! ✓${res.data.success} başarılı, ✗${res.data.failed} başarısız` 
            : `Testing completed! ✓${res.data.success} success, ✗${res.data.failed} failed`
        );
      } else {
        // Continue polling
        setTimeout(() => pollLinkCheckOperation(operationId), 1500);
      }
    } catch (error) {
      setLinkCheckTestingAll(false);
      setLinkCheckOperationId(null);
      setLinkCheckProgress({ progress_percent: 0, processed: 0, total: 0, success: 0, failed: 0, rate_per_second: 0 });
    }
  };

  // Fix single link check item
  const handleFixLinkCheck = async (checkId) => {
    setProcessingLinkCheck(prev => ({ ...prev, [checkId]: "fixing" }));
    try {
      const res = await api.post(`/admin/link-check/${checkId}/fix`);
      if (res.data.success) {
        toast.success(res.data.message || (language === "tr" ? "Düzeltildi" : "Fixed"));
        fetchLinkCheckItems(linkCheckPage, linkCheckStatusFilter, linkCheckSearch);
        fetchLinkCheckStats();
      } else {
        toast.error(res.data.message || (language === "tr" ? "Düzeltme başarısız" : "Fix failed"));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Düzeltme başarısız" : "Fix failed"));
    } finally {
      setProcessingLinkCheck(prev => ({ ...prev, [checkId]: null }));
    }
  };

  // Fix all link checks (connector_ok ones)
  const handleFixAllLinkChecks = async () => {
    const ok = window.confirm(
      language === "tr"
        ? `${linkCheckStats.connector_ok} adet düzeltilebilir linki otomatik düzeltmek istediğinize emin misiniz?\n\nBu işlem biraz zaman alabilir.`
        : `Are you sure you want to auto-fix ${linkCheckStats.connector_ok} fixable links?\n\nThis may take some time.`
    );
    if (!ok) return;
    
    setLinkCheckTestingAll(true);
    try {
      const res = await api.post("/admin/link-check/fix-all");
      toast.success(res.data.message || (language === "tr" ? "Düzeltme başlatıldı" : "Fix started"));
      setLinkCheckOperationId(res.data.operation_id);
      pollLinkCheckOperation(res.data.operation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Düzeltme başlatılamadı" : "Failed to start fix"));
      setLinkCheckTestingAll(false);
    }
  };

  // Delete site permanently from link check
  const handleDeleteSiteFromCheck = async (checkId, siteName) => {
    const ok = window.confirm(
      language === "tr"
        ? `"${siteName}" sitesini sistemden KALICI olarak silmek istediğinize emin misiniz?\n\nBu site artık satın alınamayacak.\nMüşterilerin satın aldığı linkler silinmeyecek ama "site silindi" olarak işaretlenecek.`
        : `Are you sure you want to PERMANENTLY delete "${siteName}" from the system?\n\nThis site will no longer be purchasable.\nCustomers' purchased links won't be deleted but will be marked as "site deleted".`
    );
    if (!ok) return;

    setProcessingLinkCheck(prev => ({ ...prev, [checkId]: "deleting" }));
    try {
      const res = await api.delete(`/admin/link-check/${checkId}/delete-site`);
      toast.success(res.data.message || (language === "tr" ? "Site silindi" : "Site deleted"));
      fetchLinkCheckItems(linkCheckPage, linkCheckStatusFilter, linkCheckSearch);
      fetchLinkCheckStats();
      fetchSitesStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Silme başarısız" : "Delete failed"));
    } finally {
      setProcessingLinkCheck(prev => ({ ...prev, [checkId]: null }));
    }
  };

  // Clear all link checks
  const handleClearAllLinkChecks = async () => {
    const ok = window.confirm(
      language === "tr"
        ? `Tüm link kontrol kayıtlarını (${linkCheckStats.total} adet) temizlemek istediğinize emin misiniz?\n\nBu işlem sadece kontrol kayıtlarını siler, siteler ve siparişler etkilenmez.`
        : `Are you sure you want to clear all link check records (${linkCheckStats.total} items)?\n\nThis only clears check records, sites and orders are not affected.`
    );
    if (!ok) return;

    try {
      const res = await api.delete("/admin/link-check/clear-all");
      toast.success(res.data.message || (language === "tr" ? "Kayıtlar temizlendi" : "Records cleared"));
      fetchLinkCheckItems(1, "", "");
      fetchLinkCheckStats();
      setLinkCheckPage(1);
      setLinkCheckStatusFilter("");
      setLinkCheckSearch("");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Temizleme başarısız" : "Clear failed"));
    }
  };

  // Bulk delete unfixable sites
  const handleBulkDeleteUnfixable = async () => {
    const ok = window.confirm(
      language === "tr"
        ? `Tüm düzeltilemeyen siteleri (${linkCheckStats.unfixable} site) sistemden KALICI olarak silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`
        : `Are you sure you want to PERMANENTLY delete all unfixable sites (${linkCheckStats.unfixable} sites) from the system?\n\nThis cannot be undone!`
    );
    if (!ok) return;

    try {
      const res = await api.delete("/admin/link-check/bulk-delete-sites");
      toast.success(res.data.message || (language === "tr" ? "Siteler silindi" : "Sites deleted"));
      fetchLinkCheckItems(linkCheckPage, linkCheckStatusFilter, linkCheckSearch);
      fetchLinkCheckStats();
      fetchSitesStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Toplu silme başarısız" : "Bulk delete failed"));
    }
  };

  // Refresh all data (used after add/edit/delete operations)
  const refreshAllData = useCallback(async () => {
    const tasks = [
      fetchSites(sitesPage, sitesSearch, sitesSortBy, sitesSortOrder),
      fetchUsers(usersPage, usersSearch),
      api.get("/admin/stats").then(res => setStats(res.data)),
      api.get("/durations").then(res => setDurations(res.data)),
      api.get("/packages").then(res => setPackages(res.data)),
      api.get("/seo-services").then(res => setSeoServices(res.data)),
      api.get("/admin/sites/pending").then(res => setPendingSites(res.data.sites || [])).catch(() => {}),
      api.get("/admin/sites/stats").then(res => setSitesStats(res.data)).catch(() => {})
    ];
    if (activeTab === "orders") {
      tasks.push(fetchGroupedOrders(ordersPage, ordersSearch));
    }
    await Promise.all(tasks);
  }, [
    activeTab,
    fetchGroupedOrders,
    fetchSites,
    fetchUsers,
    ordersPage,
    ordersSearch,
    sitesPage,
    sitesSearch,
    sitesSortBy,
    sitesSortOrder,
    usersPage,
    usersSearch,
  ]);

  const handleDeleteOrder = async (orderId) => {
    const ok = window.confirm(
      language === "tr"
        ? "Bu siparişi silmek istediğinize emin misiniz? (Listeden kaldırılır)"
        : "Are you sure you want to delete this order? (It will be removed from lists)"
    );
    if (!ok) return;

    setDeletingOrders(prev => ({ ...prev, [orderId]: true }));
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success(language === "tr" ? "Sipariş silindi" : "Order deleted");
      // Refresh stats + lists
      await refreshAllData();
      // Defensive local removal
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.deleteError"));
    } finally {
      setDeletingOrders(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Fetch system settings (Super Admin only)
  const fetchSystemSettings = useCallback(async () => {
    try {
      const [settingsRes, seoApiRes, connectorRes, supportRes] = await Promise.all([
        api.get("/admin/system-settings"),
        api.get("/admin/system-settings/seo-api").catch(() => ({ data: null })),
        api.get("/admin/system-settings/connector").catch(() => ({ data: null })),
        api.get("/admin/system-settings/support").catch(() => ({ data: null }))
      ]);
      setSystemSettings(settingsRes.data);
      if (seoApiRes.data) setSeoApiSettings(seoApiRes.data);
      if (connectorRes.data) setConnectorSettings(connectorRes.data);
      if (supportRes.data) {
        setSupportSettings(supportRes.data);
        setSupportTelegramInput((supportRes.data.telegram_usernames || []).join("\n"));
      }
    } catch (error) {
      console.error("System settings fetch error:", error);
    }
  }, []);

  // ============ BOT API STATE & FETCHERS (super_admin only) ============
  const [botApiKeys, setBotApiKeys] = useState([]);
  const [botApiStats, setBotApiStats] = useState(null);
  const [botApiOrders, setBotApiOrders] = useState([]);
  const [botApiOrdersPage, setBotApiOrdersPage] = useState(1);
  const [botApiOrdersTotal, setBotApiOrdersTotal] = useState(0);
  const [botApiFilterUserId, setBotApiFilterUserId] = useState("");
  const [botApiKeyDialogOpen, setBotApiKeyDialogOpen] = useState(false);
  const [botApiKeyForm, setBotApiKeyForm] = useState({ name: "", note: "" });
  const [botApiKeyCreating, setBotApiKeyCreating] = useState(false);
  const [botApiNewKeyResult, setBotApiNewKeyResult] = useState(null);
  const [botApiLoading, setBotApiLoading] = useState(false);

  const fetchBotApiKeys = useCallback(async () => {
    try {
      const res = await api.get("/admin/bot/api-keys");
      setBotApiKeys(res.data?.items || []);
    } catch (error) {
      console.error("Bot API keys fetch error:", error);
    }
  }, []);

  const fetchBotApiStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/bot/stats");
      setBotApiStats(res.data);
    } catch (error) {
      console.error("Bot API stats fetch error:", error);
    }
  }, []);

  const fetchBotApiOrders = useCallback(async (page = 1, telegramUserId = "") => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (telegramUserId) params.set("telegram_user_id", String(telegramUserId));
      const res = await api.get(`/admin/bot/orders?${params.toString()}`);
      setBotApiOrders(res.data?.items || []);
      setBotApiOrdersTotal(res.data?.total || 0);
      setBotApiOrdersPage(page);
    } catch (error) {
      console.error("Bot API orders fetch error:", error);
    }
  }, []);

  const handleCreateBotApiKey = async () => {
    const name = (botApiKeyForm.name || "").trim();
    if (name.length < 2) {
      toast.error(language === "tr" ? "Key adı en az 2 karakter olmalı" : "Key name must be at least 2 chars");
      return;
    }
    setBotApiKeyCreating(true);
    try {
      const res = await api.post("/admin/bot/api-keys", {
        name,
        note: (botApiKeyForm.note || "").trim(),
      });
      setBotApiNewKeyResult(res.data);
      setBotApiKeyForm({ name: "", note: "" });
      await fetchBotApiKeys();
      toast.success(language === "tr" ? "Bot API key oluşturuldu" : "Bot API key created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setBotApiKeyCreating(false);
    }
  };

  const handleRevokeBotApiKey = async (keyId, keyName) => {
    if (!window.confirm(
      language === "tr"
        ? `"${keyName}" key'i iptal edilecek. Bot bu key ile bağlanamayacak. Devam?`
        : `"${keyName}" key will be revoked. The bot will no longer authenticate with it. Continue?`
    )) return;
    try {
      await api.delete(`/admin/bot/api-keys/${keyId}`);
      toast.success(language === "tr" ? "Bot API key iptal edildi" : "Bot API key revoked");
      await fetchBotApiKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handlePurgeBotApiKey = async (keyId, keyName) => {
    if (!window.confirm(
      language === "tr"
        ? `"${keyName}" key'i KALICI olarak silinecek (audit kaybolur). Geri alınamaz. Devam?`
        : `"${keyName}" key will be PERMANENTLY deleted (audit lost). Cannot be undone. Continue?`
    )) return;
    try {
      await api.delete(`/admin/bot/api-keys/${keyId}?purge=true`);
      toast.success(language === "tr" ? "Bot API key kalıcı olarak silindi" : "Bot API key permanently deleted");
      await fetchBotApiKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(language === "tr" ? "Kopyalandı" : "Copied");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  // Runs once on first mount; guarded by initialFetchDone to avoid duplicate bootstrap.
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    fetchInitialData();
    if (user?.role === "super_admin") {
      fetchSystemSettings();
    }
  }, [fetchInitialData, fetchSystemSettings, user]);

  // Bot API tab: fetch keys + stats + orders when tab becomes active
  useEffect(() => {
    if (activeTab !== "bot-api") return;
    if (user?.role !== "super_admin") return;
    setBotApiLoading(true);
    Promise.all([
      fetchBotApiKeys(),
      fetchBotApiStats(),
      fetchBotApiOrders(1, ""),
    ]).finally(() => setBotApiLoading(false));
  }, [activeTab, user, fetchBotApiKeys, fetchBotApiStats, fetchBotApiOrders]);

  // Save NOWPayments settings
  const handleSaveNowPaymentsSettings = async () => {
    if (!nowPaymentsForm.api_key || !nowPaymentsForm.api_key.trim()) {
      toast.error(language === "tr" ? "NOWPayments API Key gerekli" : "NOWPayments API Key required");
      return;
    }
    
    // Validate API key length
    if (nowPaymentsForm.api_key.trim().length < 10) {
      toast.error(language === "tr" ? "API Key çok kısa görünüyor. Lütfen kontrol edin." : "API Key seems too short. Please check.");
      return;
    }
    
    setSavingSystemSettings(true);
    try {
      const response = await api.put("/admin/system-settings/nowpayments", {
        api_key: nowPaymentsForm.api_key.trim(),
        ipn_secret: (nowPaymentsForm.ipn_secret || "").trim()
      });
      toast.success(language === "tr" ? "NOWPayments ayarları başarıyla kaydedildi" : "NOWPayments settings saved successfully");
      setNowPaymentsForm({ api_key: "", ipn_secret: "" });
      fetchSystemSettings();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || (language === "tr" ? "Kaydetme hatası" : "Save error");
      console.error("NOWPayments settings save error:", error);
      
      // Daha açıklayıcı hata mesajları
      if (error.response?.status === 503) {
        toast.error(language === "tr" 
          ? "Veritabanı bağlantı hatası. Lütfen sistem yöneticisine başvurun." 
          : "Database connection error. Please contact system administrator.");
      } else if (error.response?.status === 400) {
        toast.error(errorMessage);
      } else if (error.response?.status === 500) {
        toast.error(language === "tr" 
          ? `Sunucu hatası: ${errorMessage}` 
          : `Server error: ${errorMessage}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSavingSystemSettings(false);
    }
  };

  // Save Telegram settings (System level)
  const handleSaveSystemTelegram = async () => {
    if (!systemTelegramForm.bot_token || !systemTelegramForm.chat_id) {
      toast.error(t("common.telegramRequired"));
      return;
    }
    setSavingSystemSettings(true);
    try {
      const res = await api.put("/admin/system-settings/telegram", systemTelegramForm);
      toast.success(res.data.message);
      fetchSystemSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydetme hatası");
    } finally {
      setSavingSystemSettings(false);
    }
  };

  // Test Telegram connection
  const handleTestTelegram = async () => {
    try {
      const res = await api.post("/admin/system-settings/telegram/test");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test başarısız");
    }
  };

  // SEO Service handlers
  const handleSeoServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: seoServiceForm.name,
        service_type: seoServiceForm.service_type,
        credit_cost: parseInt(seoServiceForm.credit_cost),
        description: seoServiceForm.description,
        is_auto: seoServiceForm.is_auto,
        target_countries: seoServiceForm.target_countries ? seoServiceForm.target_countries.split(",").map(c => c.trim()).filter(c => c) : []
      };
      
      if (editingSeoService) {
        await api.put(`/seo-services/${editingSeoService.id}`, data);
        toast.success(language === "tr" ? "SEO hizmeti güncellendi" : "SEO service updated");
      } else {
        await api.post("/seo-services", data);
        toast.success(language === "tr" ? "SEO hizmeti eklendi" : "SEO service added");
      }
      setSeoServiceDialogOpen(false);
      setEditingSeoService(null);
      setSeoServiceForm({ name: "", service_type: "index", credit_cost: "5", description: "", is_auto: true, target_countries: "" });
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.errorOccurred"));
    }
  };

  const openEditSeoService = (service) => {
    setEditingSeoService(service);
    setSeoServiceForm({
      name: service.name,
      service_type: service.service_type,
      credit_cost: service.credit_cost?.toString() || "5",
      description: service.description || "",
      is_auto: service.is_auto ?? true,
      target_countries: service.target_countries?.join(", ") || ""
    });
    setSeoServiceDialogOpen(true);
  };

  const handleDeleteSeoService = async (serviceId) => {
    if (!window.confirm(t("common.deleteConfirm"))) return;
    try {
      await api.delete(`/seo-services/${serviceId}`);
      toast.success(language === "tr" ? "SEO hizmeti silindi" : "SEO service deleted");
      refreshAllData();
    } catch (error) {
      toast.error(t("common.deleteFailed"));
    }
  };

  // SEO API Settings handlers
  const handleSaveSeoApiSettings = async () => {
    setSavingSystemSettings(true);
    try {
      await api.put("/admin/system-settings/seo-api", seoApiForm);
      toast.success(language === "tr" ? "SEO API ayarları kaydedildi" : "SEO API settings saved");
      setSeoApiForm({
        moz_access_id: "", moz_secret_key: "",
        ahrefs_api_key: "", semrush_api_key: "",
        dataforseo_login: "", dataforseo_password: "",
        google_indexing_api_key: "", indexnow_api_key: ""
      });
      fetchSystemSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Kaydetme hatası" : "Save error"));
    } finally {
      setSavingSystemSettings(false);
    }
  };

  const handleDeleteSeoApiProvider = async (provider) => {
    if (!window.confirm(t("common.deleteConfirm"))) return;
    try {
      await api.delete(`/admin/system-settings/seo-api/${provider}`);
      toast.success(language === "tr" ? `${provider} ayarları silindi` : `${provider} settings deleted`);
      fetchSystemSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.deleteError"));
    }
  };

  const handleSaveConnectorSettings = async () => {
    setSavingSystemSettings(true);
    try {
      await api.put("/admin/system-settings/connector", connectorSettings);
      toast.success(language === "tr" ? "Connector ayarları kaydedildi" : "Connector settings saved");
      fetchSystemSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Kaydetme hatası" : "Save error"));
    } finally {
      setSavingSystemSettings(false);
    }
  };

  // Toplu ekleme: tek akış (async). Siteler eklenir; baglanti durumu pending listesi ve refresh/retry akislariyla yonetilir.
  const handleBulkAdd = async (family, rawText) => {
    if (!rawText.trim()) {
      toast.error(language === "tr" ? "Lütfen connector URL'lerini girin" : "Please enter connector URLs");
      return;
    }

    setBulkAddLoading(true);
    try {
      // Parse lines: her satır bir connector URL (eksikse https:// eklenir)
      const rawLines = rawText.split("\n").map(l => l.trim()).filter(l => l && /\./.test(l) && /[a-zA-Z0-9]/.test(l));
      const connector_urls = rawLines.map(url => {
        if (!url.startsWith("http://") && !url.startsWith("https://")) return "https://" + url;
        return url;
      });

      if (connector_urls.length === 0) {
        toast.error(language === "tr" ? "Geçerli URL bulunamadı" : "No valid URLs found");
        setBulkAddLoading(false);
        return;
      }

      // Use async endpoint with connector_urls (same format as sync)
      const res = await api.post("/admin/sites/bulk-add-async", { 
        connector_urls: connector_urls,
        skip_metrics: true,
        notify_telegram: true,
        connector_family: family,
        replace_existing: true
      });
      
      const operationId = res.data.operation_id;
      setActiveBulkOp({
        operation_id: operationId,
        total: res.data.total,
        status: "started",
        processed: 0,
        success: 0,
        failed: 0,
        progress_percent: 0,
        rate_per_second: 0
      });
      try { sessionStorage.setItem("bulkAddOperationId", operationId); } catch (_) {}
      
      toast.success(
        language === "tr" 
          ? `${connector_urls.length} ${family === "v5js" ? "JS" : "PHP"} connector arka planda ekleniyor...` 
          : `${connector_urls.length} ${family === "v5js" ? "JS" : "PHP"} connector sites being added in background...`
      );
      
      // Start polling for status
      setBulkOpPolling(true);
      pollBulkOperation(operationId);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Toplu ekleme hatası" : "Bulk add error"));
      setBulkAddLoading(false);
    }
  };

  // Poll bulk operation status
  const pollBulkOperation = useCallback(async (operationId) => {
    try {
      const res = await api.get(`/admin/bulk-operations/${operationId}`);
      setActiveBulkOp(res.data);
      
      if (res.data.status === "completed" || res.data.status === "failed") {
        setBulkOpPolling(false);
        setBulkAddLoading(false);
        try { sessionStorage.removeItem("bulkAddOperationId"); } catch (_) {}
        
        if (res.data.status === "completed") {
          const successCount = res.data.success || 0;
          const failedCount = res.data.failed || 0;
          toast.success(
            language === "tr"
              ? `✅ ${successCount} site eklendi! ${failedCount > 0 ? `(❌ ${failedCount} başarısız)` : ''}`
              : `✅ ${successCount} sites added! ${failedCount > 0 ? `(❌ ${failedCount} failed)` : ''}`
          );
          setBulkAddDialogOpen(false);
          setBulkAddText("");
          setBulkConnectorFamily("auto");
          
          // Refresh all data
          await refreshAllData();
          await fetchSitesStats();
          
          if (successCount > 0) {
            toast.info(
              language === "tr" 
                ? "Yeni siteler panele eklendi. Baglanmayanlar bekleyenler listesinde gorunur; gerekirse tum baglantilari yenile veya pending retry calistir."
                : "New sites are now visible in the panel. Unverified ones stay in pending sites; run refresh all connections or pending retry if needed."
            );
          }
        } else {
          toast.error(language === "tr" ? "İşlem başarısız oldu" : "Operation failed");
        }
      } else {
        // Continue polling
        setTimeout(() => pollBulkOperation(operationId), 1000);
      }
    } catch (error) {
      console.error("Polling error:", error);
      setBulkOpPolling(false);
      setBulkAddLoading(false);
      try { sessionStorage.removeItem("bulkAddOperationId"); } catch (_) {}
    }
  }, [fetchSitesStats, language, refreshAllData]);

  // Sayfa değişse bile: dönünce devam eden toplu eklemeyi göster ve poll et
  useEffect(() => {
    const opId = sessionStorage.getItem("bulkAddOperationId");
    if (!opId) return;
    let cancelled = false;
    const resume = async () => {
      try {
        const res = await api.get(`/admin/bulk-operations/${opId}`);
        if (cancelled) return;
        const status = res.data?.status;
        if (status === "running" || status === "started") {
          setActiveBulkOp(res.data);
          setBulkAddLoading(true);
          setBulkOpPolling(true);
          setBulkAddDialogOpen(true);
          pollBulkOperation(opId);
        } else {
          sessionStorage.removeItem("bulkAddOperationId");
          if (status === "completed") {
            setActiveBulkOp(res.data);
            toast.success(language === "tr" ? "Toplu ekleme tamamlandı (arka planda devam etti)." : "Bulk add completed (continued in background).");
            refreshAllData();
            fetchSitesStats();
          }
        }
      } catch (_) {
        if (!cancelled) sessionStorage.removeItem("bulkAddOperationId");
      }
    };
    resume();
    return () => { cancelled = true; };
  }, [fetchSitesStats, language, pollBulkOperation, refreshAllData]);

  // Fetch bulk operations list
  const fetchBulkOperations = async () => {
    try {
      const res = await api.get("/admin/bulk-operations");
      setBulkOperations(res.data || []);
    } catch (error) {
      console.error("Fetch bulk operations error:", error);
    }
  };

  const handleRetryPending = async (pendingId) => {
    try {
      toast.loading(language === "tr" ? "Tekrar deneniyor..." : "Retrying...", { id: "retry-pending" });
      const res = await api.post(`/admin/sites/pending/${pendingId}/retry`);
      toast.dismiss("retry-pending");
      
      if (res.data.success) {
        toast.success(language === "tr" ? "Bağlantı kuruldu!" : "Connection established!");
      } else {
        toast.error(res.data.message || (language === "tr" ? "Bağlantı başarısız" : "Connection failed"));
      }
      refreshAllData();
    } catch (error) {
      toast.dismiss("retry-pending");
      toast.error(error.response?.data?.detail || t("common.error"));
    }
  };

  const handleRetryAllPending = async () => {
    if (retryingAllPending) return;
    
    setRetryingAllPending(true);
    try {
      toast.loading(language === "tr" ? `${pendingSites.length} site deneniyor...` : `Retrying ${pendingSites.length} sites...`, { id: "retry-all" });
      const res = await api.post("/admin/sites/pending/retry-all");
      toast.dismiss("retry-all");
      
      if (res.data.success > 0 || res.data.failed > 0) {
        toast.success(
          language === "tr"
            ? `✅ ${res.data.success} başarılı, ❌ ${res.data.failed} başarısız`
            : `✅ ${res.data.success} success, ❌ ${res.data.failed} failed`
        );
      } else {
        toast.info(language === "tr" ? "İşlenecek bekleyen site yok" : "No pending sites to process");
      }
      
      // Refresh data after operation
      await refreshAllData();
      await fetchSitesStats();
    } catch (error) {
      toast.dismiss("retry-all");
      toast.error(error.response?.data?.detail || (language === "tr" ? "Bağlantı testi başarısız" : "Connection test failed"));
    } finally {
      setRetryingAllPending(false);
    }
  };

  const handleDeletePending = async (pendingId) => {
    try {
      await api.delete(`/admin/sites/pending/${pendingId}`);
      toast.success(t("common.deleted"));
      refreshAllData();
    } catch (error) {
      toast.error(language === "tr" ? "Silme hatası" : "Delete error");
    }
  };

  // Toplu bekleyen silme
  const handleDeleteAllPending = async () => {
    if (!window.confirm(language === "tr" 
      ? `Tüm ${pendingSites.length} bekleyen siteyi silmek istediğinize emin misiniz?` 
      : `Are you sure you want to delete all ${pendingSites.length} pending sites?`)) {
      return;
    }
    
    try {
      toast.loading(language === "tr" ? "Siliniyor..." : "Deleting...", { id: "delete-all-pending" });
      const res = await api.delete("/admin/sites/pending/all");
      toast.dismiss("delete-all-pending");
      toast.success(
        language === "tr" 
          ? `${res.data.deleted} bekleyen site silindi` 
          : `${res.data.deleted} pending sites deleted`
      );
      setPendingPage(1);
      refreshAllData();
    } catch (error) {
      toast.dismiss("delete-all-pending");
      toast.error(error.response?.data?.detail || (language === "tr" ? "Silme hatası" : "Delete error"));
    }
  };

  // Site handlers
  // Helper: URL'ye otomatik https:// ekle
  const normalizeUrl = (url) => {
    if (!url) return url;
    url = url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return 'https://' + url;
    }
    return url;
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: siteForm.name,
        url: normalizeUrl(siteForm.url),
        category: siteForm.category,
        language: siteForm.language,
        credit_cost: parseInt(siteForm.credit_cost),
        link_type: siteForm.link_type,
        placement_method: siteForm.placement_method
      };
      
      // Connector URL'i hem yeni site eklerken hem de düzenlerken gönder
      if (siteForm.connector_url) {
        data.connector_url = normalizeUrl(siteForm.connector_url);
      }
      
      if (editingSite) {
        data.da = parseInt(siteForm.da) || 0;
        data.pa = parseInt(siteForm.pa) || 0;
        data.spam_score = parseInt(siteForm.spam_score) || 0;
        data.traffic = parseInt(siteForm.traffic) || 0;
        await api.put(`/sites/${editingSite.id}`, data);
        toast.success(language === "tr" ? "Site güncellendi" : "Site updated");
      } else {
        await api.post("/sites", data);
        toast.success(language === "tr" ? "Site eklendi" : "Site added");
      }
      setSiteDialogOpen(false);
      setEditingSite(null);
      setSiteForm({ name: "", url: "", category: "", language: "", credit_cost: "1", link_type: "dofollow", placement_method: "php", da: "", pa: "", spam_score: "", traffic: "", connector_url: "" });
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.errorOccurred"));
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm("Bu siteyi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/sites/${siteId}`);
      toast.success("Site silindi");
      // Defensive local removal (backend does soft-delete; admins now won't list inactive by default)
      setSites(prev => prev.filter(s => s.id !== siteId));
      setSelectedSites(prev => prev.filter(id => id !== siteId));
      refreshAllData();
    } catch (error) {
      toast.error(t("common.deleteFailed"));
    }
  };

  // Toplu site silme
  const handleBulkDeleteSites = async () => {
    if (selectedSites.length === 0) return;
    if (!window.confirm(language === "tr" 
      ? `${selectedSites.length} siteyi silmek istediğinize emin misiniz?`
      : `Are you sure you want to delete ${selectedSites.length} sites?`)) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const siteId of selectedSites) {
        try {
          await api.delete(`/sites/${siteId}`);
          successCount++;
        } catch (e) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(language === "tr" 
          ? `${successCount} site silindi` 
          : `${successCount} sites deleted`);
      }
      if (failCount > 0) {
        toast.error(language === "tr" 
          ? `${failCount} site silinemedi` 
          : `${failCount} sites failed to delete`);
      }
      
      setSelectedSites([]);
      refreshAllData();
    } catch (error) {
      toast.error(t("common.deleteFailed"));
    }
  };

  // Bulk Key Register (Fix key_registered: false)
  const handleBulkKeySync = async () => {
    const ok = window.confirm(
      language === "tr"
        ? "Tüm sitelerin key'lerini connector'lara kaydetmek istediğinize emin misiniz?\n\nBu işlem 'key_registered: false' sorununu düzeltir ve siparişlerin başarılı olmasını sağlar."
        : "Are you sure you want to register keys to all connectors?\n\nThis fixes 'key_registered: false' issue and enables successful orders."
    );
    if (!ok) return;

    setSyncingKeys(true);
    try {
      const res = await api.post("/admin/sites/bulk-key-register");
      toast.success(res.data.message || (language === "tr" ? "Key kaydı başlatıldı" : "Key registration started"));
      setKeySyncOperationId(res.data.operation_id);
      pollKeySyncOperation(res.data.operation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Key kaydı başlatılamadı" : "Failed to start key registration"));
      setSyncingKeys(false);
    }
  };

  // Poll key sync operation
  const pollKeySyncOperation = async (operationId) => {
    try {
      const res = await api.get(`/admin/link-check/operation/${operationId}`);
      if (res.data.status === "completed" || res.data.status === "failed") {
        setSyncingKeys(false);
        setKeySyncOperationId(null);
        toast.success(
          language === "tr"
            ? `Key senkronizasyonu tamamlandı! ✓${res.data.success} başarılı, ✗${res.data.failed} başarısız`
            : `Key sync completed! ✓${res.data.success} success, ✗${res.data.failed} failed`
        );
        refreshAllData();
      } else {
        setTimeout(() => pollKeySyncOperation(operationId), 2000);
      }
    } catch (error) {
      setSyncingKeys(false);
      setKeySyncOperationId(null);
    }
  };

  // TÜM BAĞLANTILARI YENİLE - Tek buton: ping + key kayıt + bağlantı testi
  const handleRefreshAllConnections = async () => {
    const ok = window.confirm(
      language === "tr"
        ? "TÜM SİTELERİ YENİDEN BAĞLAMAK İSTİYOR MUSUNUZ?\n\nBu işlem:\n• Tüm connector'lara ping atar\n• V2/V3 sitelerde key senkron dener\n• V4/V5 sitelerde token ile kontrol eder\n• Bağlantı durumlarını günceller"
        : "REFRESH ALL SITE CONNECTIONS?\n\nThis will:\n• Ping all connectors\n• Sync keys for V2/V3 sites\n• Use token auth for V4/V5 sites\n• Update connection status"
    );
    if (!ok) return;

    setRefreshingConnections(true);
    const resetProgress = { percent: 0, processed: 0, total: 0, success: 0, failed: 0 };
    refreshProgressRef.current = resetProgress;
    setRefreshProgress(resetProgress);
    
    try {
      const res = await api.post("/admin/sites/refresh-all-connections");
      
      if (res.data.operation_id) {
        // Poll operation status with progress
        const pollRefresh = async (opId, retryCount = 0) => {
          try {
            const st = await api.get(`/admin/bulk-operations/${opId}`);
            const { status, total, processed, success, failed, results } = st.data;
            const pct = total ? Math.round((processed / total) * 100) : 0;
            
            // Never move progress backwards because another worker may briefly serve stale state.
            const prev = refreshProgressRef.current || resetProgress;
            const nextProgress = {
              percent: Math.max(pct || 0, prev.percent || 0),
              processed: Math.max(processed || 0, prev.processed || 0),
              total: Math.max(total || 0, prev.total || 0),
              success: Math.max(success || 0, prev.success || 0),
              failed: Math.max(failed || 0, prev.failed || 0),
            };
            refreshProgressRef.current = nextProgress;
            setRefreshProgress(nextProgress);
            
            if (status === "completed") {
              const failedSites = results || [];
              if (failedSites.length > 0 && failed > 0) {
                const failedList = failedSites.slice(0, 5).map(r => `• ${r.site}: ${r.msg || "Hata"}`).join("\n");
                toast.error(
                  `${language === "tr" ? "Başarısız" : "Failed"}: ${failed}\n${failedList}${failedSites.length > 5 ? `\n... +${failedSites.length - 5}` : ""}`,
                  { duration: 10000 }
                );
              }
              toast.success(
                language === "tr"
                  ? `✅ Yenileme tamamlandı: ${success} başarılı, ${failed} başarısız`
                  : `✅ Refresh complete: ${success} success, ${failed} failed`
              );
              setRefreshingConnections(false);
              const completedProgress = { percent: 100, processed: total, total, success, failed };
              refreshProgressRef.current = completedProgress;
              setRefreshProgress(completedProgress);
              await fetchSites(sitesPage, sitesSearch, sitesSortBy, sitesSortOrder);
              await fetchSitesStats();
              await refreshAllData();
            } else if (status === "failed") {
              toast.error(language === "tr" ? "Yenileme başarısız" : "Refresh failed");
              setRefreshingConnections(false);
              refreshProgressRef.current = resetProgress;
              setRefreshProgress(resetProgress);
            } else {
              setTimeout(() => pollRefresh(opId, 0), 1500);
            }
          } catch (e) {
            // Transient poll errors should not reset visible progress to zero.
            if (retryCount < 6) {
              setTimeout(() => pollRefresh(opId, retryCount + 1), 2000);
            } else {
              setRefreshingConnections(false);
            }
          }
        };
        pollRefresh(res.data.operation_id);
      } else {
        toast.success(res.data.message || (language === "tr" ? "Yenileme tamamlandı" : "Refresh complete"));
        setRefreshingConnections(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Yenileme başlatılamadı" : "Failed to start refresh"));
      setRefreshingConnections(false);
      refreshProgressRef.current = resetProgress;
      setRefreshProgress(resetProgress);
    }
  };

  const handleCleanupAllConnectorLinks = async () => {
    const ok = window.confirm(
      language === "tr"
        ? "TUM CONNECTOR SITELERINDEKI LINKLERI TEMIZLEMEK ISTIYOR MUSUNUZ?\n\nBu islem tum sitelerdeki aktif/eski backlink kayitlarini temizler."
        : "CLEAN ALL LINKS ON ALL CONNECTOR SITES?\n\nThis clears existing backlink records across connector sites."
    );
    if (!ok) return;

    setCleaningAllLinks(true);
    setCleanupProgress({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });

    try {
      const res = await api.post("/admin/sites/cleanup-links-all", { family: "all", include_inactive: false });
      const operationId = res.data?.operation_id;
      if (!operationId) {
        toast.error(language === "tr" ? "Cleanup operation_id donmedi" : "Cleanup operation_id missing");
        setCleaningAllLinks(false);
        return;
      }

      const pollCleanup = async (opId) => {
        try {
          const st = await api.get(`/admin/bulk-operations/${opId}`);
          const { status, total = 0, processed = 0, success = 0, failed = 0 } = st.data || {};
          const pct = total ? Math.round((processed / total) * 100) : 0;
          setCleanupProgress({ percent: pct, processed, total, success, failed });

          if (status === "completed") {
            toast.success(
              language === "tr"
                ? `Cleanup tamamlandi: ${success} basarili, ${failed} basarisiz`
                : `Cleanup completed: ${success} success, ${failed} failed`
            );
            setCleaningAllLinks(false);
            await fetchSites(sitesPage, sitesSearch, sitesSortBy, sitesSortOrder);
            await fetchSitesStats();
          } else if (status === "failed") {
            toast.error(language === "tr" ? "Cleanup basarisiz" : "Cleanup failed");
            setCleaningAllLinks(false);
            setCleanupProgress({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });
          } else {
            setTimeout(() => pollCleanup(opId), 1500);
          }
        } catch (err) {
          setCleaningAllLinks(false);
          setCleanupProgress({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });
        }
      };

      pollCleanup(operationId);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Cleanup baslatilamadi" : "Failed to start cleanup"));
      setCleaningAllLinks(false);
      setCleanupProgress({ percent: 0, processed: 0, total: 0, success: 0, failed: 0 });
    }
  };

  // Tüm siteleri seç/kaldır
  const toggleSelectAllSites = () => {
    if (selectedSites.length === sites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(sites.map(s => s.id));
    }
  };

  // Tek site seçimi
  const toggleSelectSite = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  // Toplu order silme
  const handleBulkDeleteOrders = async () => {
    if (selectedOrders.length === 0) return;
    if (!window.confirm(language === "tr" 
      ? `${selectedOrders.length} siparişi silmek istediğinize emin misiniz?`
      : `Are you sure you want to delete ${selectedOrders.length} orders?`)) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const orderId of selectedOrders) {
        try {
          await api.delete(`/admin/orders/${orderId}`);
          successCount++;
        } catch (e) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(language === "tr" 
          ? `${successCount} sipariş silindi` 
          : `${successCount} orders deleted`);
      }
      if (failCount > 0) {
        toast.error(language === "tr" 
          ? `${failCount} sipariş silinemedi` 
          : `${failCount} orders failed to delete`);
      }
      
      setSelectedOrders([]);
      refreshAllData();
    } catch (error) {
      toast.error(t("common.deleteFailed"));
    }
  };

  // Tüm orderları seç/kaldır
  const toggleSelectAllOrders = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  // Tek order seçimi
  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const openEditSite = async (site) => {
    try {
      const res = await api.get(`/sites/${site.id}`);
      setEditingSite(res.data);
      setSiteForm({
        name: res.data.name,
        url: res.data.url,
        category: res.data.category,
        language: res.data.language,
        credit_cost: res.data.credit_cost?.toString() || "1",
        link_type: res.data.link_type || "dofollow",
        placement_method: res.data.placement_method || "php",
        da: res.data.da?.toString() || "",
        pa: res.data.pa?.toString() || "",
        spam_score: res.data.spam_score?.toString() || "",
        traffic: res.data.traffic?.toString() || "",
        connector_url: res.data.connector_url || ""
      });
      setSiteDialogOpen(true);
    } catch (error) {
      toast.error("Site bilgileri alınamadı");
    }
  };

  const copySiteKey = async (siteId) => {
    try {
      const res = await api.get(`/sites/${siteId}`);
      if (res.data.site_key) {
        // Modern browsers with HTTPS support
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(res.data.site_key);
            toast.success(t("admin.siteKeyCopied"));
            return;
          } catch (clipboardError) {
            console.log("Clipboard API failed, trying fallback");
          }
        }
        
        // Fallback method for HTTP or older browsers
        const textArea = document.createElement("textarea");
        textArea.value = res.data.site_key;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.success(t("admin.siteKeyCopied"));
        } catch (execError) {
          // Show the key in a prompt as last resort
          prompt("Site anahtarı (kopyalayın):", res.data.site_key);
        }
        
        document.body.removeChild(textArea);
      } else {
        toast.error("Site anahtarı bulunamadı");
      }
    } catch (error) {
      toast.error("Anahtar kopyalanamadı");
    }
  };

  // Connector handlers
  const downloadConnector = async (site, artifact = "php") => {
    try {
      const siteId = site?.id;
      const connectorFamily = (site?.connector_family || "").toLowerCase();
      const versionParam = connectorFamily === "v5" ? "v5" : "legacy";
      const response = await api.get(`/admin/connector/download/${siteId}?version=${versionParam}&artifact=${artifact}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filePrefix = artifact === "js" ? "seo-connector-js" : (versionParam === "v5" ? "seo-connector-v5" : "seo-connector");
      link.setAttribute('download', `${filePrefix}-${siteId.substring(0, 8)}.${artifact === "js" ? "js" : "php"}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === "tr" ? "Connector dosyası indirildi" : "Connector file downloaded");
    } catch (error) {
      toast.error(language === "tr" ? "İndirme başarısız" : "Download failed");
    }
  };

  const testSiteConnection = async (site) => {
    if (!site.connector_url) {
      toast.error(language === "tr" ? "Connector URL tanımlı değil" : "Connector URL not defined");
      return;
    }
    
    try {
      toast.loading(language === "tr" ? "Bağlantı test ediliyor..." : "Testing connection...", { id: "connection-test" });
      
      const res = await api.post("/sites/" + site.id + "/ping");
      
      toast.dismiss("connection-test");
      
      if (res.data.success) {
        toast.success(language === "tr" ? "Bağlantı başarılı!" : "Connection successful!");
        refreshAllData();
      } else {
        const errorMsg = res.data.message || (language === "tr" ? "Bağlantı başarısız" : "Connection failed");
        
        // Check if it's a Cloudflare challenge
        if (res.data.cloudflare_challenge || errorMsg.includes("Cloudflare challenge") || errorMsg.includes("Cloudflare Challenge")) {
          toast.error(
            `${t("admin.site.cloudflareChallenge")}\n\n${t("admin.site.cloudflareSolution")}`,
            { duration: 12000 }
          );
        } else {
          toast.error(errorMsg, { duration: 5000 });
        }
      }
    } catch (error) {
      toast.dismiss("connection-test");
      toast.error(error.response?.data?.detail || (language === "tr" ? "Bağlantı hatası" : "Connection error"));
    }
  };

  // SEO Metrics handlers
  const fetchSiteMetrics = async (siteId) => {
    try {
      toast.loading(t("admin.seo.fetchingMetrics"), { id: "fetch-metrics" });
      
      const res = await api.post(`/admin/sites/${siteId}/fetch-metrics`);
      
      toast.dismiss("fetch-metrics");
      
      if (res.data.metrics?.source) {
        toast.success(t("admin.seo.metricsFetched"));
        refreshAllData();
      } else {
        toast.error(res.data.metrics?.error || t("admin.seo.noApiConfigured"));
      }
    } catch (error) {
      toast.dismiss("fetch-metrics");
      toast.error(error.response?.data?.detail || t("admin.seo.metricsError"));
    }
  };

  const fetchAllSitesMetrics = async () => {
    try {
      toast.loading(t("admin.seo.fetchingMetrics"), { id: "fetch-all-metrics" });
      
      // 2500+ site için bu işlem uzun sürebilir (default axios timeout 30s). Bu çağrıya özel timeout veriyoruz.
      const res = await api.post(
        "/admin/sites/fetch-all-metrics?details=0",
        {},
        { timeout: 10 * 60 * 1000 } // 10 minutes
      );
      
      toast.dismiss("fetch-all-metrics");
      
      toast.success(`${res.data.success}/${res.data.total} ${language === "tr" ? "site güncellendi" : "sites updated"}`);
      refreshAllData();
    } catch (error) {
      toast.dismiss("fetch-all-metrics");
      const msg =
        error?.code === "ECONNABORTED"
          ? (language === "tr"
              ? "Metrikler alınırken zaman aşımı oldu (çok fazla site). Biraz daha bekleyip tekrar deneyin."
              : "Timeout while fetching metrics (too many sites). Please retry later.")
          : (error.response?.data?.detail || t("admin.seo.metricsError"));
      toast.error(msg);
    }
  };

  const submitUrlForIndexing = async (url) => {
    try {
      toast.loading(language === "tr" ? "İndexlemeye gönderiliyor..." : "Submitting for indexing...", { id: "submit-indexing" });
      
      const res = await api.post(`/admin/indexing/submit-single?url=${encodeURIComponent(url)}`);
      
      toast.dismiss("submit-indexing");
      
      const googleSuccess = res.data.google_indexing?.success;
      const indexnowSuccess = res.data.indexnow?.success;
      
      if (googleSuccess || indexnowSuccess) {
        toast.success(t("admin.seo.indexingSubmitted"));
      } else {
        toast.error(t("admin.seo.noApiConfigured"));
      }
    } catch (error) {
      toast.dismiss("submit-indexing");
      toast.error(error.response?.data?.detail || t("admin.seo.indexingError"));
    }
  };

  // Package handlers
  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    try {
      const isSubscription = (packageForm.product_type || "subscription") === "subscription";
      const data = {
        name: packageForm.name,
        credits: isSubscription ? 0 : parseInt(packageForm.credits),
        price: parseFloat(packageForm.price),
        bonus_credits: isSubscription ? 0 : (parseInt(packageForm.bonus_credits) || 0),
        is_featured: packageForm.is_featured,
        template: packageForm.template,
        description: packageForm.description,
        product_type: packageForm.product_type || "subscription",
        days: isSubscription ? parseInt(packageForm.days) || 0 : 0
      };

      if (editingPackage) {
        await api.put(`/packages/${editingPackage.id}`, data);
        toast.success(language === "tr" ? "Paket güncellendi" : "Package updated");
      } else {
        await api.post("/packages", data);
        toast.success(language === "tr" ? "Paket eklendi" : "Package added");
      }
      setPackageDialogOpen(false);
      setEditingPackage(null);
      setPackageForm({ name: "", credits: "", price: "", bonus_credits: "0", is_featured: false, template: "default", description: "", product_type: "subscription", days: "" });
      
      // Fetch fresh packages directly (bypass cache, refreshAllData may use stale cache)
      // Small delay to ensure backend cache is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      const freshPackages = await api.get("/packages");
      setPackages(freshPackages.data);
    } catch (error) {
      toast.error(t("common.addFailed"));
    }
  };

  // Open package for editing
  const openEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name || "",
      credits: pkg.credits?.toString() || "",
      price: pkg.price?.toString() || "",
      bonus_credits: pkg.bonus_credits?.toString() || "0",
      is_featured: pkg.is_featured || false,
      template: pkg.template || "default",
      description: pkg.description || "",
      product_type: pkg.product_type || "subscription",
      days: pkg.days?.toString() || ""
    });
    setPackageDialogOpen(true);
  };

  // Duration handlers
  const handleDurationSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: durationForm.name,
        days: parseInt(durationForm.days),
        credit_multiplier: parseFloat(durationForm.credit_multiplier)
      };
      
      if (editingDuration) {
        await api.put(`/durations/${editingDuration.id}`, data);
        toast.success(t("admin.duration.updated"));
      } else {
        await api.post("/durations", data);
        toast.success(t("admin.duration.added"));
      }
      setDurationDialogOpen(false);
      setEditingDuration(null);
      setDurationForm({ name: "", days: "", credit_multiplier: "1.0" });
      refreshAllData();
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const openEditDuration = (duration) => {
    setEditingDuration(duration);
    setDurationForm({
      name: duration.name,
      days: duration.days?.toString() || "",
      credit_multiplier: duration.credit_multiplier?.toString() || "1.0"
    });
    setDurationDialogOpen(true);
  };

  const handleDeleteDuration = async () => {
    if (!durationToDelete) return;
    const durationIdToDelete = durationToDelete.id;
    try {
      await api.delete(`/durations/${durationIdToDelete}`);
      // Optimistic update: remove from state immediately
      setDurations(durations.filter(d => d.id !== durationIdToDelete));
      setDurationDeleteConfirmOpen(false);
      setDurationToDelete(null);
      toast.success(t("admin.duration.deleted"));
      // Don't refresh - optimistic update is enough, cache will be cleared on next page load
      // Refresh causes the issue because cache might not be cleared yet
    } catch (error) {
      // If delete failed, refresh to show current state
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || t("common.error");
      toast.error(errorMessage);
      // Refresh durations to ensure UI is in sync
      try {
        const res = await api.get("/durations");
        setDurations(res.data);
      } catch (refreshError) {
        console.error("Failed to refresh durations:", refreshError);
      }
    }
  };

  const openDurationDeleteConfirm = (duration) => {
    setDurationToDelete(duration);
    setDurationDeleteConfirmOpen(true);
  };

  // User handlers
  const handleUserUpdate = async () => {
    try {
      const data = {};
      if (userForm.name && userForm.name !== editingUser.name) data.name = userForm.name;
      if (userForm.email && userForm.email !== editingUser.email) data.email = userForm.email;
      if (userForm.credits !== "") {
        const bal = parseFloat(userForm.credits);
        data.api_balance = bal;
        data.credits = bal;
      }
      data.is_banned = userForm.is_banned;
      
      await api.put(`/admin/users/${editingUser.id}`, data);
      
      // Update password if provided
      if (userForm.newPassword && userForm.newPassword.length >= 6) {
        await api.put(`/admin/users/${editingUser.id}/password`, { password: userForm.newPassword });
        toast.success(t("admin.user.passwordUpdated"));
      }
      
      toast.success(t("admin.user.userUpdated"));
      setUserDialogOpen(false);
      setEditingUser(null);
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.user.updateFailed"));
    }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    const bal = u.api_balance ?? u.credits ?? 0;
    setUserForm({ 
      name: u.name || "", 
      email: u.email || "",
      credits: bal?.toString() || "0", 
      is_banned: u.is_banned || false,
      newPassword: ""
    });
    setUserDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/admin/users/${userToDelete.id}`);
      toast.success(t("admin.user.userDeleted"));
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.user.deleteFailed"));
    }
  };

  const openDeleteConfirm = (u) => {
    setUserToDelete(u);
    setDeleteConfirmOpen(true);
  };

  // Super Admin - Role management
  const openRoleDialog = (u) => {
    setRoleEditUser({ ...u, newRole: u.role });
    setRoleDialogOpen(true);
  };

  const handleRoleUpdate = async () => {
    if (!roleEditUser) return;
    try {
      await api.put(`/admin/users/${roleEditUser.id}/role`, { 
        role: roleEditUser.newRole,
        permissions: {}
      });
      toast.success(t("admin.user.roleUpdated"));
      setRoleDialogOpen(false);
      setRoleEditUser(null);
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.user.updateFailed"));
    }
  };

  // Super Admin - Permissions management
  const openPermissionsDialog = (u) => {
    setPermissionsEditUser({ ...u, permissions: u.permissions || {} });
    setPermissionsDialogOpen(true);
  };

  const handlePermissionsUpdate = async () => {
    if (!permissionsEditUser) return;
    try {
      await api.put(`/admin/users/${permissionsEditUser.id}/permissions`, permissionsEditUser.permissions);
      toast.success(t("admin.user.permissionsUpdated"));
      setPermissionsDialogOpen(false);
      setPermissionsEditUser(null);
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.user.updateFailed"));
    }
  };

  // Super Admin - Create admin
  const handleCreateAdmin = async () => {
    if (!adminForm.name || !adminForm.email || !adminForm.password) {
      toast.error(t("admin.user.fillAllFields"));
      return;
    }
    try {
      await api.post("/admin/create-admin", {
        name: adminForm.name,
        email: adminForm.email,
        password: adminForm.password,
        permissions: selectedPermissions
      });
      toast.success(t("admin.user.adminCreated"));
      setCreateAdminDialogOpen(false);
      setAdminForm({ name: "", email: "", password: "" });
      setSelectedPermissions({});
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.user.createFailed"));
    }
  };

  // Super Admin - Remove admin role
  const handleRemoveAdminRole = async (userId) => {
    if (!confirm(t("admin.user.removeAdminConfirm"))) return;
    try {
      await api.delete(`/admin/users/${userId}/admin-role`);
      toast.success(t("admin.user.adminRoleRemoved"));
      refreshAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  // Verify user
  const handleVerifyUser = async (targetUser) => {
    const newStatus = !targetUser.is_verified;
    try {
      await api.put(`/admin/users/${targetUser.id}/verify`, { is_verified: newStatus });
      toast.success(newStatus 
        ? (language === "tr" ? "Kullanıcı onaylandı" : "User verified") 
        : (language === "tr" ? "Onay kaldırıldı" : "Verification removed"));
      fetchUsers(usersPage, usersSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  // Badge dialog handlers
  const openBadgeDialog = (targetUser) => {
    setBadgeUser(targetUser);
    setSelectedBadge("");
    setBadgeDialogOpen(true);
  };

  const handleAddBadge = async () => {
    if (!selectedBadge || !badgeUser) return;
    try {
      await api.put(`/admin/users/${badgeUser.id}/badge`, { badge: selectedBadge, action: "add" });
      toast.success(language === "tr" ? "Rozet verildi" : "Badge given");
      setBadgeDialogOpen(false);
      fetchUsers(usersPage, usersSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleRemoveBadge = async (targetUser, badge) => {
    if (!confirm(language === "tr" ? "Bu rozeti kaldırmak istediğinizden emin misiniz?" : "Are you sure you want to remove this badge?")) return;
    try {
      await api.put(`/admin/users/${targetUser.id}/badge`, { badge, action: "remove" });
      toast.success(language === "tr" ? "Rozet kaldırıldı" : "Badge removed");
      fetchUsers(usersPage, usersSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  // Subscription dialog handlers
  const openSubscriptionDialog = (targetUser) => {
    setSubscriptionUser(targetUser);
    setSubscriptionDays("");
    setSubscriptionDialogOpen(true);
  };

  const handleAddSubscription = async () => {
    if (!subscriptionDays || parseInt(subscriptionDays) <= 0) {
      toast.error(language === "tr" ? "Geçerli bir gün sayısı girin" : "Enter a valid number of days");
      return;
    }
    setSubscriptionLoading(true);
    try {
      await api.put(`/admin/users/${subscriptionUser.id}/subscription`, { 
        days: parseInt(subscriptionDays), 
        action: "add" 
      });
      toast.success(language === "tr" ? `${subscriptionDays} günlük abonelik verildi` : `${subscriptionDays} days subscription given`);
      setSubscriptionDialogOpen(false);
      fetchUsers(usersPage, usersSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "İşlem başarısız" : "Operation failed"));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async (targetUser) => {
    if (!confirm(language === "tr" ? "Bu kullanıcının aboneliğini iptal etmek istediğinizden emin misiniz?" : "Are you sure you want to cancel this user's subscription?")) return;
    try {
      await api.put(`/admin/users/${targetUser.id}/subscription`, { days: 0, action: "cancel" });
      toast.success(language === "tr" ? "Abonelik iptal edildi" : "Subscription cancelled");
      fetchUsers(usersPage, usersSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "İşlem başarısız" : "Operation failed"));
    }
  };

  // Settings handlers
  const handleTelegramSave = async () => {
    try {
      await api.put("/admin/settings/telegram", { value: telegramForm });
      toast.success("Telegram ayarları kaydedildi");
    } catch (error) {
      toast.error("Kaydetme başarısız");
    }
  };

  const handleMaintenanceToggle = async (enabled) => {
    try {
      await api.post("/admin/maintenance", { enabled, message: "Sistem bakımda. Lütfen daha sonra tekrar deneyin." });
      setMaintenanceMode(enabled);
      toast.success(enabled ? "Bakım modu aktif" : "Bakım modu kapatıldı");
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleSendNotification = async () => {
    if (!notificationMessage.trim()) {
      toast.error("Mesaj boş olamaz");
      return;
    }
    try {
      await api.post(`/admin/notify?message=${encodeURIComponent(notificationMessage)}`);
      toast.success("Bildirim gönderildi");
      setNotificationMessage("");
    } catch (error) {
      toast.error("Gönderim başarısız");
    }
  };

  const getStatusBadge = (status) => {
    const labels = {
      active: language === "tr" ? "AKTİF" : "ACTIVE",
      pending: language === "tr" ? "BEKLEMEDE" : "PENDING",
      expired: language === "tr" ? "SÜRESİ DOLDU" : "EXPIRED",
      processing: language === "tr" ? "İŞLENİYOR" : "PROCESSING",
      completed: language === "tr" ? "TAMAMLANDI" : "COMPLETED"
    };
    switch (status) {
      case "active":
        return <span className="status-active px-2 py-1 text-xs font-mono">{labels.active}</span>;
      case "pending":
        return <span className="status-pending px-2 py-1 text-xs font-mono">{labels.pending}</span>;
      case "expired":
        return <span className="status-expired px-2 py-1 text-xs font-mono">{labels.expired}</span>;
      case "processing":
        return <span className="status-pending px-2 py-1 text-xs font-mono">{labels.processing}</span>;
      case "completed":
        return <span className="status-active px-2 py-1 text-xs font-mono">{labels.completed}</span>;
      default:
        return <span className="px-2 py-1 text-xs font-mono bg-muted">{status?.toUpperCase()}</span>;
    }
  };

  const navItems = [
    { id: "overview", icon: TrendingUp, label: t("admin.overview") },
    { id: "sites", icon: Globe, label: t("admin.sites") },
    { id: "orders", icon: Package, label: t("admin.orders") },
    { id: "linkcheck", icon: Link2, label: language === "tr" ? "LİNK KONTROL" : "LINK CHECK", badge: linkCheckStats.untested > 0 ? linkCheckStats.untested : null },
    { id: "users", icon: Users, label: t("admin.users") },
    { id: "packages", icon: Coins, label: t("admin.packages") },
    { id: "seo-services", icon: Sparkles, label: language === "tr" ? "SEO SERVİSLERİ" : "SEO SERVICES" },
    { id: "tickets", icon: HeadphonesIcon, label: language === "tr" ? "DESTEK" : "SUPPORT" },
    { id: "announcements", icon: Megaphone, label: language === "tr" ? "DUYURULAR" : "ANNOUNCEMENTS" },
    { id: "settings", icon: Settings, label: t("admin.settings") },
    // Super admin only
    ...(user?.role === "super_admin" ? [
      { id: "bot-api", icon: Bot, label: language === "tr" ? "BOT API" : "BOT API" },
      { id: "system", icon: Shield, label: t("admin.systemSettings") },
    ] : []),
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary font-mono animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-60 border-r border-white/10 bg-black hidden md:flex flex-col overflow-hidden">
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <DynamicLogo />
            <div className="sparkle-wrapper">
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="glow-credit font-mono font-bold tracking-normal">ROOT-SEO</span>
            </div>
          </div>
          <span className="text-xs font-mono text-primary">ADMIN PANEL</span>
        </div>

        <nav className="flex-1 overflow-y-auto px-6 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 font-mono text-sm transition-colors ${
                activeTab === item.id 
                  ? "bg-white/5 border-l-2 border-primary text-white" 
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
              {item.badge && (
                <span className="ml-auto bg-red-500 text-white text-xs font-mono px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="flex-shrink-0 p-6 border-t border-white/10">
          <div className="mb-4">
            <LanguageSwitcher />
          </div>
          <div className="mb-4">
            <p className="font-mono text-sm text-muted-foreground">Admin</p>
            <p className="font-mono font-bold truncate">{user?.name}</p>
          </div>
          <Button 
            variant="ghost" 
            onClick={logout} 
            className="w-full justify-start gap-2 text-muted-foreground hover:text-red-400 font-mono text-sm"
          >
            <LogOut className="w-4 h-4" />
            {language === "tr" ? "Çıkış Yap" : "Logout"}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden glass-strong sticky top-0 z-50 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DynamicLogo size="small" />
          <span className="font-mono font-bold text-sm">ADMIN</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu - scrollable body, Çıkış Yap her zaman altta görünür */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/95 flex flex-col min-h-screen">
          <div className="flex-1 min-h-0 overflow-y-auto pt-20 px-4 pb-4">
            <nav className="space-y-1">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { handleTabChange(item.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 p-4 font-mono rounded text-left ${activeTab === item.id ? "bg-white/5 text-white" : "text-muted-foreground"}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="p-4">
              <LanguageSwitcher />
            </div>
          </div>
          <div className="flex-shrink-0 min-h-[56px] p-4 pt-3 border-t border-white/10 bg-black pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button onClick={() => { setMobileMenuOpen(false); logout(); }} className="flex items-center justify-center gap-3 w-full py-3 px-4 text-red-400 font-mono rounded hover:bg-white/5 border border-red-400/30">
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {language === "tr" ? "Çıkış Yap" : "Logout"}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="md:ml-60 p-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <h1 className="text-2xl font-mono font-bold uppercase">{t("admin.overview")}</h1>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Globe, label: t("admin.totalSites"), value: stats ? (stats.total_sites || 0) : "...", sub: stats ? `${stats.active_sites || 0} ${t("admin.connected")}` : null },
                  { icon: Package, label: t("admin.totalOrders"), value: stats ? (stats.total_orders || 0) : "...", sub: stats ? `${stats.active_orders || 0} ${t("admin.active")}` : null },
                  { icon: Users, label: t("admin.totalUsers"), value: stats ? (stats.total_users || 0) : "..." },
                  { icon: DollarSign, label: t("admin.revenue"), value: stats ? `$${stats?.total_revenue?.toFixed(0) || 0}` : "...", primary: true },
                ].map((stat, i) => (
                  <div key={i} className="glass p-6">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <stat.icon className="w-4 h-4" />
                      <span className="text-xs font-mono uppercase">{stat.label}</span>
                    </div>
                    <div className={`text-3xl font-mono font-bold ${stat.primary ? "text-primary" : ""}`}>{stat.value}</div>
                    {stat.sub && <div className="text-xs text-primary font-mono mt-1">{stat.sub}</div>}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: language === "tr" ? "Eski Bağlı" : "Legacy Connected", value: sitesStats.legacy_connected || 0, tone: "text-orange-400" },
                  { label: language === "tr" ? "Eski Bağlı Değil" : "Legacy Disconnected", value: sitesStats.legacy_disconnected || 0, tone: "text-orange-300" },
                  { label: language === "tr" ? "Yeni Bağlı" : "Modern Connected", value: sitesStats.modern_connected || 0, tone: "text-cyan-400" },
                  { label: language === "tr" ? "Yeni Bağlı Değil" : "Modern Disconnected", value: sitesStats.modern_disconnected || 0, tone: "text-cyan-300" },
                  { label: language === "tr" ? "Bekleyen Site" : "Pending Sites", value: sitesStats.pending_sites || 0, tone: "text-yellow-400" },
                  { label: language === "tr" ? "Son Yenileme" : "Last Refresh", value: sitesStats.last_refresh_result ? `${sitesStats.last_refresh_result.success || 0}/${sitesStats.last_refresh_result.total || 0}` : "-", tone: "text-emerald-300" },
                ].map((item) => (
                  <div key={item.label} className="glass p-4">
                    <div className="text-xs font-mono uppercase text-muted-foreground mb-2">{item.label}</div>
                    <div className={`text-2xl font-mono font-bold ${item.tone}`}>{item.value}</div>
                    {item.label === (language === "tr" ? "Son Yenileme" : "Last Refresh") && sitesStats.last_refresh_result?.status ? (
                      <div className="text-xs text-muted-foreground font-mono mt-1">
                        {language === "tr" ? `Durum: ${sitesStats.last_refresh_result.status}` : `Status: ${sitesStats.last_refresh_result.status}`}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="glass overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="font-mono font-bold uppercase">
                      {language === "tr" ? "Yakinda Bitecek Abonelikler" : "Expiring Subscriptions"}
                    </h3>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      {language === "tr"
                        ? `Telegram uyari esikleri: ${(expiringSubscriptions.threshold_days || []).join("/") || "7/3/1"} gun`
                        : `Telegram warning thresholds: ${(expiringSubscriptions.threshold_days || []).join("/") || "7/3/1"} days`}
                    </p>
                  </div>
                  <div className="text-sm font-mono text-primary">
                    {expiringSubscriptions.total || 0}
                  </div>
                </div>
                {expiringSubscriptions.items?.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {expiringSubscriptions.items.map((item) => (
                      <div key={item.id} className="p-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-mono font-bold truncate">{item.name || "-"}</div>
                          <div className="text-xs text-muted-foreground font-mono truncate">{item.email || "-"}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-mono text-yellow-400">
                            {language === "tr"
                              ? `${item.days_remaining} gun kaldi`
                              : `${item.days_remaining} days left`}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {item.subscription_end_date
                              ? new Date(item.subscription_end_date).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")
                              : "-"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-sm text-muted-foreground font-mono">
                    {language === "tr" ? "Yakin tarihte bitecek aktif abonelik yok." : "No active subscriptions are expiring soon."}
                  </div>
                )}
                {expiringSubscriptionsTotalPages > 1 && (
                  <div className="p-4 border-t border-white/10 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const nextPage = Math.max(1, expiringSubscriptionsPage - 1);
                        setExpiringSubscriptionsPage(nextPage);
                      }}
                      disabled={expiringSubscriptionsPage <= 1}
                      className="font-mono text-xs"
                    >
                      {language === "tr" ? "Önceki" : "Previous"}
                    </Button>
                    <span className="text-xs font-mono text-muted-foreground px-2">
                      {expiringSubscriptionsPage} / {expiringSubscriptionsTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const nextPage = Math.min(expiringSubscriptionsTotalPages, expiringSubscriptionsPage + 1);
                        setExpiringSubscriptionsPage(nextPage);
                      }}
                      disabled={expiringSubscriptionsPage >= expiringSubscriptionsTotalPages}
                      className="font-mono text-xs"
                    >
                      {language === "tr" ? "Sonraki" : "Next"}
                    </Button>
                  </div>
                )}
              </div>

              {/* Recent Orders */}
              <div className="glass overflow-hidden">
                <div className="p-4 border-b border-white/10 flex justify-between items-center">
                  <h3 className="font-mono font-bold">{t("admin.recentOrders")}</h3>
                  <Button variant="ghost" size="sm" onClick={() => handleTabChange("orders")} className="font-mono text-xs">
                    {t("dashboard.viewAll")}
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="text-left p-3 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.customer")}</th>
                        <th className="text-left p-3 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.site")}</th>
                        <th className="text-left p-3 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.credits")}</th>
                        <th className="text-left p-3 font-mono text-xs uppercase text-muted-foreground">{t("common.status")}</th>
                        <th className="text-right p-3 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map(order => (
                        <tr key={order.id} className="border-b border-white/5">
                          <td className="p-3 font-mono text-sm">{order.user_email}</td>
                          <td className="p-3 font-mono text-sm">{order.site_name}</td>
                          <td className="p-3 font-mono font-bold">{order.credit_cost}</td>
                          <td className="p-3">{getStatusBadge(order.status)}</td>
                          <td className="p-3 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteOrder(order.id)}
                              disabled={!!deletingOrders[order.id]}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                              title={language === "tr" ? "Siparişi sil" : "Delete order"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Sites Tab */}
          {activeTab === "sites" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-mono font-bold uppercase">{t("admin.sites")}</h1>
                  {/* Site Stats */}
                  <div className="flex items-center gap-4 mt-2 text-xs font-mono">
                    <span className="text-muted-foreground">
                      {language === "tr" ? "Toplam" : "Total"}: <span className="text-primary">{sitesStats.total}</span>
                    </span>
                    <span className="text-orange-400">
                      {language === "tr" ? "Eski Grup" : "Legacy"}: {sitesStats.legacy_generation || 0}
                    </span>
                    <span className="text-cyan-400">
                      {language === "tr" ? "Yeni Grup" : "Modern"}: {sitesStats.modern_generation || 0}
                    </span>
                    <span className="text-emerald-400">
                      V5: {sitesStats.family_v5 || 0}
                    </span>
                    <span className="text-sky-400">
                      V5JS: {sitesStats.family_v5js || 0}
                    </span>
                    <span className="text-green-400">
                      <Wifi className="w-3 h-3 inline mr-1" />
                      {language === "tr" ? "Bağlı" : "Connected"}: {sitesStats.connected}
                    </span>
                    <span className="text-red-400">
                      <WifiOff className="w-3 h-3 inline mr-1" />
                      {language === "tr" ? "Bağlı Değil" : "Disconnected"}: {sitesStats.disconnected}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Sort Dropdown */}
                  <Select value={sitesSortBy} onValueChange={(val) => {
                    localStorage.setItem("rootseo_sites_sort_by", val);
                    setSitesSortBy(val);
                    fetchSites(1, sitesSearch, val, sitesSortOrder);
                    setSitesPage(1);
                  }}>
                    <SelectTrigger className="w-32 font-mono rounded-none text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="da_pa">{language === "tr" ? "DA+PA" : "DA+PA"}</SelectItem>
                      <SelectItem value="da">{language === "tr" ? "DA" : "DA"}</SelectItem>
                      <SelectItem value="pa">{language === "tr" ? "PA" : "PA"}</SelectItem>
                      <SelectItem value="traffic">{language === "tr" ? "Trafik" : "Traffic"}</SelectItem>
                      <SelectItem value="credit_cost">{language === "tr" ? "Kredi" : "Credit"}</SelectItem>
                      <SelectItem value="created_at">{language === "tr" ? "Tarih" : "Date"}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const newOrder = sitesSortOrder === "desc" ? "asc" : "desc";
                      setSitesSortOrder(newOrder);
                      fetchSites(1, sitesSearch, sitesSortBy, newOrder);
                      setSitesPage(1);
                    }}
                    className="font-mono rounded-none text-xs"
                  >
                    {sitesSortOrder === "desc" ? "↓" : "↑"}
                  </Button>
                  {/* Bulk Delete Button */}
                  {selectedSites.length > 0 && (
                    <>
                      <Button 
                        variant="destructive" 
                        onClick={handleBulkDeleteSites}
                        className="font-mono rounded-none"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> 
                        {language === "tr" ? `${selectedSites.length} SEÇİLİ SİL` : `DELETE ${selectedSites.length} SELECTED`}
                      </Button>
                    </>
                  )}
                  {/* TEK BUTON: TÜM BAĞLANTILARI YENİLE - ping + key + test hepsi */}
                  <Button 
                    variant="outline"
                    disabled={refreshingConnections}
                    onClick={handleRefreshAllConnections}
                    className="font-mono rounded-none text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/20 font-bold px-6"
                  >
                    {refreshingConnections ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {refreshingConnections 
                      ? (language === "tr" ? `YENİLENİYOR... %${refreshProgress.percent}` : `REFRESHING... %${refreshProgress.percent}`)
                      : (language === "tr" ? "TÜM BAĞLANTILARI YENİLE" : "REFRESH ALL CONNECTIONS")}
                  </Button>

                  <Button
                    variant="outline"
                    disabled={cleaningAllLinks}
                    onClick={handleCleanupAllConnectorLinks}
                    className="font-mono rounded-none text-red-400 border-red-500/50 hover:bg-red-500/20 font-bold px-6"
                  >
                    {cleaningAllLinks ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {cleaningAllLinks
                      ? (language === "tr" ? `CLEANUP... %${cleanupProgress.percent}` : `CLEANUP... %${cleanupProgress.percent}`)
                      : (language === "tr" ? "TÜM LINKLERI TEMIZLE" : "CLEAN ALL LINKS")}
                  </Button>
                  
                  {/* Progress Bar - Sadece işlem devam ederken göster */}
                  {refreshingConnections && refreshProgress.total > 0 && (
                    <div className="flex items-center gap-3 px-3 py-1 bg-black/30 rounded">
                      <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-500 transition-all duration-300" 
                          style={{ width: `${refreshProgress.percent}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-yellow-400">
                        {refreshProgress.processed}/{refreshProgress.total}
                      </span>
                      <span className="text-xs font-mono text-green-400">✓{refreshProgress.success}</span>
                      <span className="text-xs font-mono text-red-400">✗{refreshProgress.failed}</span>
                    </div>
                  )}

                  {cleaningAllLinks && cleanupProgress.total > 0 && (
                    <div className="flex items-center gap-3 px-3 py-1 bg-black/30 rounded">
                      <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500 transition-all duration-300"
                          style={{ width: `${cleanupProgress.percent}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-red-300">
                        {cleanupProgress.processed}/{cleanupProgress.total}
                      </span>
                      <span className="text-xs font-mono text-green-400">✓{cleanupProgress.success}</span>
                      <span className="text-xs font-mono text-red-400">✗{cleanupProgress.failed}</span>
                    </div>
                  )}
                  
                  {/* Fetch All Metrics Button */}
                  <Button 
                    variant="outline" 
                    onClick={fetchAllSitesMetrics}
                    className="font-mono rounded-none text-green-400 border-green-500/30 hover:bg-green-500/10"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" /> {t("admin.seo.fetchAllMetrics")}
                  </Button>
                  {/* Delete All Inactive Sites Button */}
                  <Button 
                    variant="outline"
                    disabled={deletingInactive || sitesStats.disconnected === 0}
                    onClick={async () => {
                      if (!window.confirm(language === "tr" 
                        ? `${sitesStats.disconnected} inaktif siteyi silmek istediğinize emin misiniz?` 
                        : `Are you sure you want to delete ${sitesStats.disconnected} inactive sites?`)) {
                        return;
                      }
                      setDeletingInactive(true);
                      toast.loading(language === "tr" ? "İnaktif siteler siliniyor..." : "Deleting inactive sites...", { id: "delete-inactive" });
                      try {
                        const res = await api.delete("/admin/sites/bulk-delete-inactive");
                        toast.dismiss("delete-inactive");
                        toast.success(`${res.data.deleted} ${language === "tr" ? "site silindi" : "sites deleted"}`);
                        refreshAllData();
                      } catch (err) {
                        toast.dismiss("delete-inactive");
                        toast.error(err.response?.data?.detail || (language === "tr" ? "Silme hatası" : "Delete error"));
                      } finally {
                        setDeletingInactive(false);
                      }
                    }}
                    className="font-mono rounded-none text-red-400 border-red-500/30 hover:bg-red-500/10"
                  >
                    {deletingInactive ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {language === "tr" ? `İNAKTİF SİL (${sitesStats.disconnected})` : `DELETE INACTIVE (${sitesStats.disconnected})`}
                  </Button>
                  {/* Bulk Add Dialog */}
                  <Dialog open={bulkAddDialogOpen} onOpenChange={(open) => {
                    if (!bulkAddLoading) setBulkAddDialogOpen(open);
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="font-mono rounded-none">
                        <Upload className="w-4 h-4 mr-2" /> {language === "tr" ? "TOPLU EKLE" : "BULK ADD"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-white/10 max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="font-mono">{language === "tr" ? "Toplu Site Ekleme" : "Bulk Site Add"}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {/* Progress: ekleme + ardından otomatik bağlantı (ping/key) */}
                        {activeBulkOp && bulkAddLoading && (
                          <div className="p-4 bg-black/50 border border-primary/30 rounded space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="font-mono text-sm text-primary">
                                {language === "tr" ? "İşlem Durumu" : "Operation Status"}
                              </span>
                              <span className="font-mono text-xs text-muted-foreground">
                                {activeBulkOp.status === "completed" ? "✅" : activeBulkOp.status === "failed" ? "❌" : "⏳"} {activeBulkOp.status}
                              </span>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="w-full bg-black/50 rounded-full h-3 overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-cyan-500 to-primary transition-all duration-500 ease-out"
                                style={{ width: `${activeBulkOp.progress_percent || 0}%` }}
                              />
                            </div>
                            
                            {/* Stats Grid */}
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <div className="p-2 bg-black/30 rounded">
                                <div className="font-mono text-lg text-white">{activeBulkOp.processed || 0}</div>
                                <div className="text-[10px] text-muted-foreground">{language === "tr" ? "İşlenen" : "Processed"}</div>
                              </div>
                              <div className="p-2 bg-black/30 rounded">
                                <div className="font-mono text-lg text-green-400">{activeBulkOp.success || 0}</div>
                                <div className="text-[10px] text-muted-foreground">{language === "tr" ? "Başarılı" : "Success"}</div>
                              </div>
                              <div className="p-2 bg-black/30 rounded">
                                <div className="font-mono text-lg text-red-400">{activeBulkOp.failed || 0}</div>
                                <div className="text-[10px] text-muted-foreground">{language === "tr" ? "Başarısız" : "Failed"}</div>
                              </div>
                              <div className="p-2 bg-black/30 rounded">
                                <div className="font-mono text-lg text-cyan-400">{(activeBulkOp.rate_per_second || 0).toFixed(0)}</div>
                                <div className="text-[10px] text-muted-foreground">{language === "tr" ? "Site/sn" : "Sites/sec"}</div>
                              </div>
                            </div>
                            
                            {/* ETA */}
                            {activeBulkOp.estimated_remaining_seconds && activeBulkOp.status === "running" && (
                              <div className="text-xs text-muted-foreground text-center font-mono">
                                {language === "tr" ? "Tahmini kalan süre:" : "Estimated remaining:"} {Math.ceil(activeBulkOp.estimated_remaining_seconds)}s
                              </div>
                            )}
                          </div>
                        )}

                        {/* Talimatlar */}
                        {!activeBulkOp && (
                          <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded text-xs">
                            <p className="text-cyan-400 font-bold mb-2">
                              {language === "tr" ? "Her satıra bir connector URL yazın:" : "Enter one connector URL per line:"}
                            </p>
                            <div className="p-2 bg-black/30 rounded font-mono">
                              <p className="text-primary">example.com/seo-config.php</p>
                              <p className="text-primary">http://site2.com/images/seo.php</p>
                              <p className="text-primary">https://site3.com/wp-content/c.php</p>
                            </div>
                            <p className="text-muted-foreground mt-2">
                              {language === "tr" 
                                ? "Once PHP listesini yukle. PHP ile eklenen domainler sisteme girer. Sonra JS listesini yukle; sistem PHP ile zaten bagli olanlari atlar, yalniz PHP'de basarisiz kalanlara JS fallback dener." 
                                : "Upload the PHP list first. Domains that connect through PHP are added immediately. Then upload the JS list; the system skips domains already connected through PHP and only uses JS as a fallback for PHP failures."}
                            </p>
                          </div>
                        )}

                        {/* Connector indir (sitelerde connector yüklü olmalı) */}
                        {!activeBulkOp && (
                          <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                            <div className="flex-1 text-xs">
                              <p className="text-yellow-400 font-bold">
                                {language === "tr" ? "Önce connector dosyasını indirip sitelere yükleyin!" : "First download connector file and upload to sites!"}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const response = await api.get('/admin/connector/download?version=v5&artifact=php', { responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', 'seo-connector.php');
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    toast.success(language === "tr" ? "PHP connector indirildi!" : "PHP connector downloaded!");
                                  } catch (err) {
                                    toast.error(language === "tr" ? "Hata" : "Error");
                                  }
                                }}
                                className="font-mono rounded-none text-xs"
                              >
                                <Download className="w-3 h-3 mr-1" />
                                PHP
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const response = await api.get('/admin/connector/download?version=v5&artifact=js', { responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', 'seo-connector.js');
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    toast.success(language === "tr" ? "JS connector indirildi!" : "JS connector downloaded!");
                                  } catch (err) {
                                    toast.error(language === "tr" ? "Hata" : "Error");
                                  }
                                }}
                                className="font-mono rounded-none text-xs"
                              >
                                <Code className="w-3 h-3 mr-1" />
                                JS
                              </Button>
                            </div>
                          </div>
                        )}

                        {!activeBulkOp && (
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              <div>
                                <Label className="font-mono text-xs">{language === "tr" ? "PHP Bulk Add" : "PHP Bulk Add"}</Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {language === "tr"
                                    ? "Önce PHP connector URL listesini yükle. Çalışan domainler modern PHP olarak eklenir."
                                    : "Upload the PHP connector URL list first. Working domains are added as modern PHP sites."}
                                </p>
                              </div>
                              <textarea
                                value={bulkPhpText}
                                onChange={(e) => setBulkPhpText(e.target.value)}
                                placeholder={language === "tr"
                                  ? "PHP connector URL'lerini buraya yapıştırın...\nhttps://site1.com/seo-config.php\nhttps://site2.com/images/seo.php"
                                  : "Paste PHP connector URLs here...\nhttps://site1.com/seo-config.php\nhttps://site2.com/images/seo.php"}
                                className="w-full h-48 p-4 bg-black/50 border border-white/10 rounded-none font-mono text-sm resize-none focus:outline-none focus:border-primary"
                                disabled={bulkAddLoading}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground font-mono">
                                  {bulkPhpText.trim().split("\n").filter(l => l.trim()).length} {language === "tr" ? "satır" : "lines"}
                                </span>
                                <Button
                                  onClick={() => handleBulkAdd("v5", bulkPhpText)}
                                  disabled={bulkAddLoading || !bulkPhpText.trim()}
                                  className="font-mono bg-primary text-black rounded-none"
                                >
                                  {language === "tr" ? "PHP EKLE" : "ADD PHP"}
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <Label className="font-mono text-xs">{language === "tr" ? "JS Fallback Bulk Add" : "JS Fallback Bulk Add"}</Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {language === "tr"
                                    ? "PHP ile daha önce başarılı eklenmiş domainler atlanır. Sadece PHP'de başarısız kalanlar için JS fallback kullanılır."
                                    : "Domains already connected through PHP are skipped. JS is used only as a fallback for PHP failures."}
                                </p>
                              </div>
                              <textarea
                                value={bulkJsText}
                                onChange={(e) => setBulkJsText(e.target.value)}
                                placeholder={language === "tr"
                                  ? "JS connector URL'lerini buraya yapıştırın...\nhttps://site1.com/assets/seo-connector.js\nhttps://site2.com/js/seo-connector.js"
                                  : "Paste JS connector URLs here...\nhttps://site1.com/assets/seo-connector.js\nhttps://site2.com/js/seo-connector.js"}
                                className="w-full h-48 p-4 bg-black/50 border border-white/10 rounded-none font-mono text-sm resize-none focus:outline-none focus:border-primary"
                                disabled={bulkAddLoading}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground font-mono">
                                  {bulkJsText.trim().split("\n").filter(l => l.trim()).length} {language === "tr" ? "satır" : "lines"}
                                </span>
                                <Button
                                  onClick={() => handleBulkAdd("v5js", bulkJsText)}
                                  disabled={bulkAddLoading || !bulkJsText.trim()}
                                  className="font-mono bg-cyan-600 text-white rounded-none hover:bg-cyan-700"
                                >
                                  {language === "tr" ? "JS EKLE" : "ADD JS"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-between items-center">
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setBulkAddDialogOpen(false);
                                setActiveBulkOp(null);
                                setBulkPhpText("");
                                setBulkJsText("");
                              }} 
                              className="font-mono rounded-none"
                              disabled={bulkAddLoading && activeBulkOp?.status === "running"}
                            >
                              {language === "tr" ? "KAPAT" : "CLOSE"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Single Site Add */}
                  <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="font-mono uppercase bg-primary text-black rounded-none"
                        onClick={() => { setEditingSite(null); setSiteForm({ name: "", url: "", category: "", language: "", credit_cost: "1", link_type: "dofollow", placement_method: "php", da: "", pa: "", spam_score: "", traffic: "", connector_url: "" }); }}
                      >
                        <Plus className="w-4 h-4 mr-2" /> {language === "tr" ? "YENİ SİTE" : "NEW SITE"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-white/10 max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="font-mono">{editingSite ? (language === "tr" ? "Site Düzenle" : "Edit Site") : (language === "tr" ? "Yeni Site Ekle" : "Add New Site")}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSiteSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="font-mono text-xs">{language === "tr" ? "Site Adı" : "Site Name"}</Label>
                            <Input value={siteForm.name} onChange={(e) => setSiteForm({...siteForm, name: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                          </div>
                          <div>
                            <Label className="font-mono text-xs">URL</Label>
                            <Input value={siteForm.url} onChange={(e) => setSiteForm({...siteForm, url: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="font-mono text-xs">{t("admin.category")}</Label>
                            <Input value={siteForm.category} onChange={(e) => setSiteForm({...siteForm, category: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                          </div>
                          <div>
                            <Label className="font-mono text-xs">{t("admin.language")}</Label>
                            <Input value={siteForm.language} onChange={(e) => setSiteForm({...siteForm, language: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                          </div>
                          <div>
                            <Label className="font-mono text-xs">{t("admin.creditCost")}</Label>
                            <Input type="number" value={siteForm.credit_cost} onChange={(e) => setSiteForm({...siteForm, credit_cost: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="font-mono text-xs">{t("admin.linkType")}</Label>
                            <Select value={siteForm.link_type} onValueChange={(v) => setSiteForm({...siteForm, link_type: v})}>
                              <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="dofollow">Dofollow</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="font-mono text-xs">{t("admin.placement")}</Label>
                            <Select value={siteForm.placement_method} onValueChange={(v) => setSiteForm({...siteForm, placement_method: v})}>
                              <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="php">PHP Agent</SelectItem>
                                <SelectItem value="js">JavaScript Agent</SelectItem>
                                <SelectItem value="wp_muplugins">WP mu-plugins</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {/* Connector URL - hem yeni ekleme hem düzenleme için */}
                        <div>
                          <Label className="font-mono text-xs">{t("admin.site.connectorUrl")}</Label>
                          <Input 
                            type="url"
                            value={siteForm.connector_url} 
                            onChange={(e) => setSiteForm({...siteForm, connector_url: e.target.value})} 
                            placeholder="https://site.com/images/seo-connector.php"
                            className="bg-black/50 border-white/10 rounded-none font-mono" 
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("admin.connectorUrlFull")}
                          </p>
                        </div>
                        {/* SEO Metrikleri - sadece düzenleme modunda */}
                        {editingSite && (
                          <div className="grid grid-cols-4 gap-4">
                            <div>
                              <Label className="font-mono text-xs">DA</Label>
                              <Input type="number" value={siteForm.da} onChange={(e) => setSiteForm({...siteForm, da: e.target.value})} className="bg-black/50 border-white/10 rounded-none font-mono" />
                            </div>
                            <div>
                              <Label className="font-mono text-xs">PA</Label>
                              <Input type="number" value={siteForm.pa} onChange={(e) => setSiteForm({...siteForm, pa: e.target.value})} className="bg-black/50 border-white/10 rounded-none font-mono" />
                            </div>
                            <div>
                              <Label className="font-mono text-xs">SS</Label>
                              <Input type="number" value={siteForm.spam_score} onChange={(e) => setSiteForm({...siteForm, spam_score: e.target.value})} className="bg-black/50 border-white/10 rounded-none font-mono" />
                            </div>
                            <div>
                              <Label className="font-mono text-xs">{t("admin.traffic")}</Label>
                              <Input type="number" value={siteForm.traffic} onChange={(e) => setSiteForm({...siteForm, traffic: e.target.value})} className="bg-black/50 border-white/10 rounded-none font-mono" />
                            </div>
                          </div>
                        )}
                        <Button type="submit" className="w-full font-mono uppercase bg-primary text-black rounded-none">
                          {editingSite ? (language === "tr" ? "GÜNCELLE" : "UPDATE") : (language === "tr" ? "EKLE" : "ADD")}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="glass p-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={includeInactiveSites}
                      onCheckedChange={(checked) => {
                        setIncludeInactiveSites(checked);
                        setSitesPage(1);
                      }}
                    />
                    <span className="text-xs font-mono">
                      {language === "tr" ? "İNATİFLERİ GÖSTER" : "SHOW INACTIVE"}
                    </span>
                  </div>
                </div>
                <div className="text-xs font-mono text-muted-foreground">
                  {language === "tr"
                    ? "Bu liste aktif siteleri ve baglanti durumunu birlikte gosterir."
                    : "This list shows active sites together with their connection status."}
                </div>
              </div>

              <div className="glass overflow-hidden">
                {/* Arama üstte */}
                <div className="p-4 border-b border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      value={sitesSearch || ""}
                      onChange={(e) => setSitesSearch(e.target.value)}
                      placeholder={language === "tr" ? "Site adı veya URL ara..." : "Search site name or URL..."}
                      className="pl-9 bg-black/50 border-white/10 rounded-none font-mono text-sm h-9"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                    {sitesTotal > 0 ? (language === "tr" ? `${(sitesPage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(sitesPage * ITEMS_PER_PAGE, sitesTotal)} / ${sitesTotal} kayıt` : `${(sitesPage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(sitesPage * ITEMS_PER_PAGE, sitesTotal)} of ${sitesTotal} items`) : (language === "tr" ? "Kayıt yok" : "No items")}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="text-left p-4 w-12">
                          <input 
                            type="checkbox" 
                            checked={selectedSites.length === sites.length && sites.length > 0}
                            onChange={toggleSelectAllSites}
                            className="w-4 h-4 rounded border-white/20 bg-black/50 text-primary focus:ring-primary"
                          />
                        </th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.site")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.metrics")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.credit")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.method")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.connection")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.map(site => (
                        <tr key={site.id} className={`border-b border-white/5 hover:bg-white/5 ${selectedSites.includes(site.id) ? 'bg-primary/10' : ''}`}>
                          <td className="p-4">
                            <input 
                              type="checkbox" 
                              checked={selectedSites.includes(site.id)}
                              onChange={() => toggleSelectSite(site.id)}
                              className="w-4 h-4 rounded border-white/20 bg-black/50 text-primary focus:ring-primary"
                            />
                          </td>
                          <td className="p-4">
                            <div className="font-mono font-bold">{site.name}</div>
                            <div className="text-xs text-muted-foreground">{site.url}</div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {site.is_active === false ? (
                                <span className="px-2 py-1 text-[10px] font-mono bg-white/10 text-muted-foreground">
                                  {language === "tr" ? "PASİF" : "INACTIVE"}
                                </span>
                              ) : null}
                              {site.connector_family ? (
                                <span className={`px-2 py-1 text-[10px] font-mono ${site.connector_family === "v5" ? "bg-emerald-500/20 text-emerald-400" : "bg-orange-500/20 text-orange-400"}`}>
                                  {(site.connector_family || "legacy").toUpperCase()}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-3 font-mono text-sm">
                              <span>DA: <span className="text-primary">{site.da}</span></span>
                              <span>PA: {site.pa}</span>
                              <span>SS: {site.spam_score}</span>
                            </div>
                          </td>
                          <td className="p-4 font-mono font-bold text-primary">{site.credit_cost}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 text-xs font-mono ${
                              site.placement_method === "wp_muplugins" ? "bg-blue-500/20 text-blue-400" :
                              site.placement_method === "js" ? "bg-yellow-500/20 text-yellow-400" :
                              "bg-purple-500/20 text-purple-400"
                            }`}>
                              {site.placement_method === "wp_muplugins" ? "WP" : site.placement_method?.toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4">
                            {site.is_connected ? (
                              <span className="flex items-center gap-1 text-primary text-xs font-mono">
                                <Wifi className="w-3 h-3" /> {t("admin.site.connectionConnected")}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-muted-foreground text-xs font-mono">
                                <WifiOff className="w-3 h-3" /> {t("admin.site.connectionNotConnected")}
                              </span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => fetchSiteMetrics(site.id)} title={t("admin.seo.fetchMetrics")} className="h-8 w-8 p-0 text-green-400 hover:text-green-300">
                                <TrendingUp className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => downloadConnector(site, "php")} title={t("admin.site.downloadConnector")} className="h-8 w-8 p-0">
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => downloadConnector(site, "js")} title={language === "tr" ? "JS Connector İndir" : "Download JS Connector"} className="h-8 w-8 p-0 text-cyan-400 hover:text-cyan-300">
                                <Code className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => testSiteConnection(site)} title={t("admin.site.testConnection")} className="h-8 w-8 p-0">
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => copySiteKey(site.id)} title={language === "tr" ? "Anahtar Kopyala" : "Copy Key"} className="h-8 w-8 p-0">
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openEditSite(site)} className="h-8 w-8 p-0">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteSite(site.id)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Sites Pagination - sadece sayfa navigasyonu, arama üstte */}
                <div className="p-4 border-t border-white/10">
                  <DataPagination
                    currentPage={sitesPage}
                    totalPages={sitesTotalPages}
                    totalItems={sitesTotal}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={(page) => { setSitesPage(page); fetchSites(page, sitesSearch); }}
                    showSearch={false}
                    language={language}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <h1 className="text-2xl font-mono font-bold uppercase">{t("admin.orders")}</h1>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleTransferFailedOrders()}
                    disabled={transferringFailed}
                    className="font-mono uppercase bg-blue-600 text-white rounded-none text-xs"
                  >
                    {transferringFailed ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {language === "tr" ? "BAŞARISIZLARI AKTAR" : "TRANSFER FAILED"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTabChange("overview")}
                    className="font-mono rounded-none"
                  >
                    {language === "tr" ? "← Geri" : "← Back"}
                  </Button>
                </div>
              </div>
              
              {/* Enhanced Order Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Package className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Toplam" : "Total"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-blue-400">{enhancedOrderStats.total_orders}</div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Aktif" : "Active"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-green-400">{enhancedOrderStats.active_orders}</div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Başarısız İşlem" : "Failed Trans."}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-red-400">{enhancedOrderStats.failed_orders}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {enhancedOrderStats.unique_failed_sites} {language === "tr" ? "benzersiz site" : "unique sites"}
                  </div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Coins className="w-4 h-4 text-yellow-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Kredi" : "Credits"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-yellow-400">{enhancedOrderStats.total_credits}</div>
                </div>
              </div>
              
              {/* Search */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Input
                    placeholder={language === "tr" ? "Müşteri email ara..." : "Search customer email..."}
                    value={ordersSearch}
                    onChange={(e) => setOrdersSearch(e.target.value)}
                    className="bg-black/50 border-white/10 rounded-none font-mono pl-10"
                  />
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground font-mono">
                  {ordersTotal} {language === "tr" ? "müşteri" : "customers"}
                </span>
              </div>

              {/* Customers List */}
              <div className="space-y-3">
                {groupedCustomers.length === 0 ? (
                  <div className="glass p-8 text-center">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground font-mono">
                      {language === "tr" ? "Sipariş bulunamadı" : "No orders found"}
                    </p>
                  </div>
                ) : (
                  groupedCustomers.map(customer => (
                    <div key={customer.user_id} className="glass overflow-hidden">
                      {/* Customer Header */}
                      <div 
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 cursor-pointer hover:bg-white/5"
                        onClick={() => toggleCustomerExpand(customer.user_id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-mono text-sm font-bold">{customer.user_email}</div>
                            <div className="text-xs text-muted-foreground">
                              {language === "tr" ? "Son sipariş" : "Last order"}: {new Date(customer.last_order_date).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 sm:gap-6">
                          {/* Stats */}
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-center">
                              <div className="font-mono font-bold text-primary">{customer.total_orders}</div>
                              <div className="text-xs text-muted-foreground">{language === "tr" ? "Toplam" : "Total"}</div>
                            </div>
                            <div className="text-center">
                              <div className="font-mono font-bold text-green-400">{customer.active_orders}</div>
                              <div className="text-xs text-muted-foreground">{language === "tr" ? "Aktif" : "Active"}</div>
                            </div>
                            {customer.failed_orders > 0 && (
                              <div className="text-center">
                                <div className="font-mono font-bold text-red-400">{customer.failed_orders}</div>
                                <div className="text-xs text-muted-foreground">{language === "tr" ? "Başarısız" : "Failed"}</div>
                              </div>
                            )}
                            <div className="text-center">
                              <div className="font-mono font-bold text-yellow-400">{customer.total_credits}</div>
                              <div className="text-xs text-muted-foreground">{language === "tr" ? "Kredi" : "Credits"}</div>
                            </div>
                          </div>

                          {/* Expand Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="font-mono text-xs rounded-none"
                          >
                            {expandedCustomers[customer.user_id] ? (
                              <><ChevronDown className="w-4 h-4 mr-1" /> {language === "tr" ? "Gizle" : "Hide"}</>
                            ) : (
                              <><ChevronRight className="w-4 h-4 mr-1" /> {language === "tr" ? "Göster" : "Show"}</>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Orders */}
                      {expandedCustomers[customer.user_id] && (
                        <div className="border-t border-white/10 p-4 bg-black/30">
                          {/* Search and Pagination Header */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                            <div className="relative flex-1 max-w-xs">
                              <Input
                                placeholder={language === "tr" ? "Site ara..." : "Search site..."}
                                value={customerOrdersSearch[customer.user_id] || ""}
                                onChange={(e) => handleCustomerOrdersSearch(customer.user_id, e.target.value)}
                                className="bg-black/50 border-white/10 rounded-none font-mono text-sm pl-8 h-8"
                              />
                              <Globe className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {language === "tr" ? "Sayfa" : "Page"} {customerOrdersPage[customer.user_id] || 1} / {customerOrdersTotalPages[customer.user_id] || 1}
                            </div>
                          </div>

                          {customerOrders[customer.user_id] ? (
                            customerOrders[customer.user_id].length > 0 ? (
                              <>
                                <div className="space-y-2">
                                  {customerOrders[customer.user_id].map((order, idx) => (
                                    <div key={order.id || idx} className="flex items-center justify-between p-3 bg-white/5 rounded gap-4">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-mono text-sm text-primary">{order.site_name}</span>
                                          <a 
                                            href={order.site_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-muted-foreground hover:text-primary"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </a>
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate">
                                          <span className="text-white">{order.anchor_text}</span> → {order.target_url}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-4 flex-shrink-0">
                                        <div className="text-xs text-muted-foreground text-right hidden sm:block">
                                          <div>{order.duration_name}</div>
                                          <div>{order.credit_cost} kr</div>
                                        </div>
                                        {getStatusBadge(order.status)}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.id); }}
                                          disabled={!!deletingOrders[order.id]}
                                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Pagination Controls */}
                                {(customerOrdersTotalPages[customer.user_id] || 1) > 1 && (
                                  <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-white/10">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleCustomerOrdersPageChange(customer.user_id, (customerOrdersPage[customer.user_id] || 1) - 1); }}
                                      disabled={(customerOrdersPage[customer.user_id] || 1) <= 1}
                                      className="font-mono text-xs"
                                    >
                                      {language === "tr" ? "Önceki" : "Previous"}
                                    </Button>
                                    <span className="text-sm font-mono text-muted-foreground">
                                      {customerOrdersPage[customer.user_id] || 1} / {customerOrdersTotalPages[customer.user_id] || 1}
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleCustomerOrdersPageChange(customer.user_id, (customerOrdersPage[customer.user_id] || 1) + 1); }}
                                      disabled={(customerOrdersPage[customer.user_id] || 1) >= (customerOrdersTotalPages[customer.user_id] || 1)}
                                      className="font-mono text-xs"
                                    >
                                      {language === "tr" ? "Sonraki" : "Next"}
                                    </Button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-center py-4 text-muted-foreground font-mono text-sm">
                                {customerOrdersSearch[customer.user_id]
                                  ? (language === "tr" ? "Sonuç bulunamadı" : "No results found")
                                  : (language === "tr" ? "Sipariş bulunamadı" : "No orders found")}
                              </div>
                            )
                          ) : (
                            <div className="text-center py-4 text-muted-foreground font-mono text-sm">
                              {language === "tr" ? "Yükleniyor..." : "Loading..."}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Main Pagination */}
              {ordersTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setOrdersPage(ordersPage - 1); fetchGroupedOrders(ordersPage - 1, ordersSearch); }}
                    disabled={ordersPage <= 1}
                    className="font-mono"
                  >
                    {language === "tr" ? "Önceki" : "Previous"}
                  </Button>
                  <span className="text-sm font-mono text-muted-foreground px-4">
                    {ordersPage} / {ordersTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => { setOrdersPage(ordersPage + 1); fetchGroupedOrders(ordersPage + 1, ordersSearch); }}
                    disabled={ordersPage >= ordersTotalPages}
                    className="font-mono"
                  >
                    {language === "tr" ? "Sonraki" : "Next"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Link Check Tab */}
          {activeTab === "linkcheck" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <h1 className="text-xl sm:text-2xl font-mono font-bold uppercase">
                  {language === "tr" ? "LİNK KONTROL" : "LINK CHECK"}
                </h1>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={() => handleTransferFailedOrders()}
                    disabled={transferringFailed}
                    className="font-mono uppercase bg-blue-600 text-white rounded-none text-xs"
                  >
                    {transferringFailed ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {language === "tr" ? "BAŞARISIZLARI AKTAR" : "TRANSFER FAILED"}
                  </Button>
                  <Button 
                    onClick={handleTestAllLinkChecks}
                    disabled={linkCheckTestingAll || linkCheckStats.untested === 0}
                    className="font-mono uppercase bg-primary text-black rounded-none text-xs"
                  >
                    {linkCheckTestingAll ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                    {language === "tr" ? `TEST ET (${linkCheckStats.untested})` : `TEST (${linkCheckStats.untested})`}
                  </Button>
                  <Button 
                    onClick={handleResetAndTestAll}
                    disabled={linkCheckTestingAll || linkCheckStats.total === 0}
                    variant="outline"
                    className="font-mono uppercase rounded-none text-xs border-orange-500 text-orange-400 hover:bg-orange-500/20"
                  >
                    {linkCheckTestingAll ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {language === "tr" ? `TÜMÜNÜ YENİDEN TEST ET (${linkCheckStats.total})` : `RETEST ALL (${linkCheckStats.total})`}
                  </Button>
                  {/* Tümünü Düzelt Butonu */}
                  {linkCheckStats.connector_ok > 0 && (
                    <Button 
                      onClick={handleFixAllLinkChecks}
                      disabled={linkCheckTestingAll}
                      className="font-mono uppercase bg-green-600 text-white rounded-none text-xs"
                    >
                      <Wrench className="w-4 h-4 mr-2" />
                      {language === "tr" ? `TÜMÜNÜ DÜZELT (${linkCheckStats.connector_ok})` : `FIX ALL (${linkCheckStats.connector_ok})`}
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress Bar - Test sırasında göster */}
              {linkCheckTestingAll && (
                <div className="glass p-4 border border-primary/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-mono text-primary">
                      {language === "tr" ? "TEST DEVAM EDİYOR..." : "TESTING IN PROGRESS..."}
                    </span>
                    <span className="text-sm font-mono font-bold text-primary">
                      %{linkCheckProgress.total > 0 ? ((linkCheckProgress.processed / linkCheckProgress.total) * 100).toFixed(1) : "0.0"}
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-white/10 h-3 mb-3 overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300 ease-out"
                      style={{ width: `${linkCheckProgress.total > 0 ? (linkCheckProgress.processed / linkCheckProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-lg font-mono font-bold text-white">
                        {linkCheckProgress.processed}/{linkCheckProgress.total}
                      </div>
                      <div className="text-xs text-muted-foreground">{language === "tr" ? "İşlenen" : "Processed"}</div>
                    </div>
                    <div>
                      <div className="text-lg font-mono font-bold text-green-400">
                        {linkCheckProgress.success}
                      </div>
                      <div className="text-xs text-muted-foreground">{language === "tr" ? "Başarılı" : "Success"}</div>
                    </div>
                    <div>
                      <div className="text-lg font-mono font-bold text-red-400">
                        {linkCheckProgress.failed}
                      </div>
                      <div className="text-xs text-muted-foreground">{language === "tr" ? "Başarısız" : "Failed"}</div>
                    </div>
                    <div>
                      <div className="text-lg font-mono font-bold text-yellow-400">
                        {linkCheckProgress.rate_per_second.toFixed(1)}/s
                      </div>
                      <div className="text-xs text-muted-foreground">{language === "tr" ? "Hız" : "Speed"}</div>
                    </div>
                  </div>
                  
                  {/* Tahmini süre */}
                  {linkCheckProgress.estimated_remaining_seconds && linkCheckProgress.estimated_remaining_seconds > 0 && (
                    <div className="mt-2 text-center text-xs text-muted-foreground font-mono">
                      {language === "tr" ? "Tahmini kalan süre: " : "Est. remaining: "}
                      {linkCheckProgress.estimated_remaining_seconds > 60 
                        ? `${Math.ceil(linkCheckProgress.estimated_remaining_seconds / 60)} dk`
                        : `${Math.ceil(linkCheckProgress.estimated_remaining_seconds)} sn`
                      }
                    </div>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Package className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Toplam" : "Total"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-blue-400">{linkCheckStats.total}</div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Test Edilmedi" : "Untested"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-yellow-400">{linkCheckStats.untested}</div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Connector OK" : "Connector OK"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-green-400">{linkCheckStats.success}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {linkCheckStats.link_active || 0} {language === "tr" ? "link aktif" : "links active"}
                  </div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Wrench className="w-4 h-4 text-orange-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Link Yok (Düzeltilebilir)" : "No Link (Fixable)"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-orange-400">{linkCheckStats.connector_ok || 0}</div>
                </div>
                <div className="glass p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-xs font-mono uppercase">{language === "tr" ? "Bozuk" : "Broken"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-red-400">{linkCheckStats.unfixable}</div>
                </div>
              </div>

              {/* Error Type Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="glass p-3">
                  <div className="text-xs font-mono text-muted-foreground uppercase mb-1">404 Hatası</div>
                  <div className="text-lg font-mono font-bold text-red-400">{linkCheckStats.failed_404}</div>
                </div>
                <div className="glass p-3">
                  <div className="text-xs font-mono text-muted-foreground uppercase mb-1">İzin Hatası</div>
                  <div className="text-lg font-mono font-bold text-orange-400">{linkCheckStats.failed_permission}</div>
                </div>
                <div className="glass p-3">
                  <div className="text-xs font-mono text-muted-foreground uppercase mb-1">Connector Silinmiş</div>
                  <div className="text-lg font-mono font-bold text-purple-400">{linkCheckStats.failed_connector}</div>
                </div>
                <div className="glass p-3">
                  <div className="text-xs font-mono text-muted-foreground uppercase mb-1">Timeout/Bağlantı</div>
                  <div className="text-lg font-mono font-bold text-gray-400">{(linkCheckStats.failed_timeout || 0) + (linkCheckStats.failed_connection || 0)}</div>
                </div>
              </div>

              {/* Filter & Actions */}
              <div className="flex flex-wrap items-center gap-4">
                <Select value={linkCheckStatusFilter || "all"} onValueChange={(v) => { 
                  setLinkCheckStatusFilter(v === "all" ? "" : v); 
                  fetchLinkCheckItems(1, v === "all" ? "" : v, linkCheckSearch); 
                  setLinkCheckPage(1);
                }}>
                  <SelectTrigger className="w-44 bg-black/50 border-white/10 rounded-none font-mono">
                    <SelectValue placeholder={language === "tr" ? "Tümü" : "All"} />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10">
                    <SelectItem value="all">{language === "tr" ? "Tümü" : "All"}</SelectItem>
                    <SelectItem value="untested">{language === "tr" ? "Test Edilmedi" : "Untested"}</SelectItem>
                    <SelectItem value="404">404 {language === "tr" ? "Hatası" : "Error"}</SelectItem>
                    <SelectItem value="permission">{language === "tr" ? "İzin Hatası" : "Permission Error"}</SelectItem>
                    <SelectItem value="deleted">{language === "tr" ? "Connector Silinmiş" : "Connector Deleted"}</SelectItem>
                    <SelectItem value="fixable">{language === "tr" ? "Düzeltilebilir" : "Fixable"}</SelectItem>
                    <SelectItem value="unfixable">{language === "tr" ? "Umutsuz" : "Unfixable"}</SelectItem>
                  </SelectContent>
                </Select>
                
                <Input
                  placeholder={language === "tr" ? "Site ara..." : "Search site..."}
                  value={linkCheckSearch}
                  onChange={(e) => setLinkCheckSearch(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && fetchLinkCheckItems(1, linkCheckStatusFilter, linkCheckSearch)}
                  className="w-48 bg-black/50 border-white/10 rounded-none font-mono"
                />
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchLinkCheckItems(linkCheckPage, linkCheckStatusFilter, linkCheckSearch)}
                  className="font-mono"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === "tr" ? "Yenile" : "Refresh"}
                </Button>
                
                {linkCheckStats.unfixable > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkDeleteUnfixable}
                    className="font-mono text-red-400 border-red-400/50 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {language === "tr" ? `UMUTSUZLARI SİL (${linkCheckStats.unfixable})` : `DELETE UNFIXABLE (${linkCheckStats.unfixable})`}
                  </Button>
                )}
                
                {/* Tümünü Temizle Butonu */}
                {linkCheckStats.total > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleClearAllLinkChecks}
                    className="font-mono text-gray-400 border-gray-500/50 hover:bg-gray-500/20"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {language === "tr" ? `TÜMÜNÜ TEMİZLE (${linkCheckStats.total})` : `CLEAR ALL (${linkCheckStats.total})`}
                  </Button>
                )}
              </div>

              {/* Link Check Items List */}
              <div className="space-y-3">
                {linkCheckItems.length === 0 ? (
                  <div className="glass p-8 text-center text-muted-foreground font-mono">
                    {language === "tr" ? "Link kontrol öğesi bulunamadı" : "No link check items found"}
                  </div>
                ) : (
                  linkCheckItems.map(item => (
                    <div key={item.id} className={`glass p-4 border ${
                      item.test_result === "success" ? "border-green-500/30" :
                      item.can_fix ? "border-orange-500/30" :
                      item.tested ? "border-red-500/30" : "border-white/10"
                    }`}>
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            {/* Status Badge */}
                            <span className={`text-xs font-mono px-2 py-1 ${
                              item.test_result === "success" ? "bg-green-500/20 text-green-400 border border-green-500/30" :
                              !item.tested ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" :
                              item.can_fix ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" :
                              "bg-red-500/20 text-red-400 border border-red-500/30"
                            }`}>
                              {item.test_result === "success" ? (language === "tr" ? "BAŞARILI" : "SUCCESS") :
                               !item.tested ? (language === "tr" ? "BEKLİYOR" : "PENDING") :
                               item.can_fix ? (language === "tr" ? "DÜZELTİLEBİLİR" : "FIXABLE") :
                               (language === "tr" ? "UMUTSUZ" : "UNFIXABLE")}
                            </span>
                            
                            {/* Error Type Badge */}
                            {item.error_type && (
                              <span className={`text-xs font-mono px-2 py-1 ${
                                item.error_type === "404" ? "bg-red-500/20 text-red-400" :
                                item.error_type === "connector_deleted" ? "bg-purple-500/20 text-purple-400" :
                                item.error_type === "permission" ? "bg-orange-500/20 text-orange-400" :
                                "bg-gray-500/20 text-gray-400"
                              }`}>
                                {item.error_type === "404" ? "404" :
                                 item.error_type === "connector_deleted" ? (language === "tr" ? "CONNECTOR SİLİNMİŞ" : "CONNECTOR DELETED") :
                                 item.error_type === "permission" ? (language === "tr" ? "İZİN HATASI" : "PERMISSION") :
                                 item.error_type === "link_missing" ? (language === "tr" ? "LİNK YOK" : "LINK MISSING") :
                                 item.error_type.toUpperCase()}
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <span className="font-mono text-sm truncate">{item.site_name || item.site_url}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Link2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <span className="font-mono text-xs text-muted-foreground truncate">{item.target_url}</span>
                            </div>
                            {item.error_detail && (
                              <div className="text-xs text-red-400 font-mono mt-1">{item.error_detail}</div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 lg:flex-col lg:w-36">
                          {item.can_fix && (
                            <Button
                              size="sm"
                              onClick={() => handleFixLinkCheck(item.id)}
                              disabled={processingLinkCheck[item.id]}
                              className="bg-orange-600 hover:bg-orange-700 text-white font-mono text-xs"
                            >
                              {processingLinkCheck[item.id] === "fixing" ? (
                                <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Wrench className="w-3 h-3 mr-1" />
                              )}
                              {language === "tr" ? "DÜZELT" : "FIX"}
                            </Button>
                          )}
                          
                          {item.tested && item.test_result !== "success" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteSiteFromCheck(item.id, item.site_name || item.site_url)}
                              disabled={processingLinkCheck[item.id]}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20 font-mono text-xs"
                            >
                              {processingLinkCheck[item.id] === "deleting" ? (
                                <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Trash2 className="w-3 h-3 mr-1" />
                              )}
                              {language === "tr" ? "SİL" : "DELETE"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {linkCheckTotal > ITEMS_PER_PAGE && (
                <div className="mt-6">
                  <DataPagination
                    currentPage={linkCheckPage}
                    totalPages={linkCheckTotalPages}
                    totalItems={linkCheckTotal}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={(page) => { setLinkCheckPage(page); fetchLinkCheckItems(page, linkCheckStatusFilter, linkCheckSearch); }}
                    language={language}
                  />
                </div>
              )}

            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <h1 className="text-xl sm:text-2xl font-mono font-bold uppercase">{t("admin.users")}</h1>
                {user?.role === "super_admin" && (
                  <Dialog open={createAdminDialogOpen} onOpenChange={setCreateAdminDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="font-mono uppercase bg-primary text-black rounded-none text-xs sm:text-sm">
                        <UserPlus className="w-4 h-4 mr-2" /> {t("admin.user.createAdmin")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-white/10 max-w-lg mx-2">
                      <DialogHeader>
                        <DialogTitle className="font-mono flex items-center gap-2">
                          <Crown className="w-5 h-5 text-primary" />
                          {t("admin.user.createAdmin")}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label className="font-mono text-xs">{t("admin.user.name")}</Label>
                          <Input
                            value={adminForm.name}
                            onChange={(e) => setAdminForm({...adminForm, name: e.target.value})}
                            placeholder="Admin adı"
                            className="bg-black/50 border-white/10 rounded-none font-mono"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs">Email</Label>
                          <Input
                            type="email"
                            value={adminForm.email}
                            onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                            placeholder="admin@example.com"
                            className="bg-black/50 border-white/10 rounded-none font-mono"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs">Şifre</Label>
                          <Input
                            type="password"
                            value={adminForm.password}
                            onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                            placeholder="••••••••"
                            className="bg-black/50 border-white/10 rounded-none font-mono"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs mb-2 block">Yetkiler</Label>
                          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-black/30 border border-white/10">
                            {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                              <label key={key} className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={selectedPermissions[key] || false}
                                  onChange={(e) => setSelectedPermissions({...selectedPermissions, [key]: e.target.checked})}
                                  className="rounded"
                                />
                                <span className="font-mono">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <Button 
                          onClick={handleCreateAdmin} 
                          className="w-full font-mono uppercase bg-primary text-black rounded-none"
                        >
                          ADMİN OLUŞTUR
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {/* Kullanıcı arama - tüm ekranlarda üstte */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    value={usersSearch || ""}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    placeholder={language === "tr" ? "İsim veya email ara..." : "Search name or email..."}
                    className="pl-9 bg-black/50 border-white/10 rounded-none font-mono text-sm h-9"
                  />
                </div>
                <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                  {usersTotal > 0 ? (language === "tr" ? `${usersTotal} kayıt` : `${usersTotal} items`) : (language === "tr" ? "Kayıt yok" : "No items")}
                </span>
              </div>
              
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-3">
                {users.map(u => (
                  <div key={u.id} className="glass p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono font-bold text-sm">{u.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{u.email}</div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-mono flex items-center gap-1 ${
                        u.role === "super_admin" ? "bg-yellow-500/20 text-yellow-400" : 
                        u.role === "admin" ? "bg-primary/20 text-primary" : "bg-white/10"
                      }`}>
                        {u.role === "super_admin" && <Crown className="w-3 h-3" />}
                        {u.role === "admin" && <ShieldCheck className="w-3 h-3" />}
                        {u.role?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-primary font-bold">${(u.api_balance ?? u.credits ?? 0).toFixed(0)} API</span>
                        {u.is_banned ? (
                          <span className="status-expired px-2 py-1 text-xs font-mono">{language === "tr" ? "BANLI" : "BANNED"}</span>
                        ) : (
                          <span className="status-active px-2 py-1 text-xs font-mono">{language === "tr" ? "AKTİF" : "ACTIVE"}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditUser(u)} className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user?.role === "super_admin" && u.role !== "super_admin" && u.id !== user?.id && (
                          <Button size="sm" variant="ghost" onClick={() => openDeleteConfirm(u)} className="h-8 w-8 p-0 text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View - arama yukarıda (ortak) */}
              <div className="glass overflow-hidden hidden sm:block">
                <div className="overflow-x-auto min-w-0">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground w-[18%] min-w-0">{t("admin.table.name")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground w-[22%] min-w-0">{t("admin.table.email")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground w-[10%]">{t("admin.table.role")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground w-[12%]">{language === "tr" ? "API BAKİYE" : "API BALANCE"}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground w-[14%]">{t("admin.table.status")}</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground w-[24%]">{t("admin.table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-4 font-mono font-bold min-w-0 max-w-0 truncate" title={u.name}>{u.name}</td>
                          <td className="p-4 font-mono text-sm min-w-0 max-w-0 truncate" title={u.email}>{u.email}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 text-xs font-mono flex items-center gap-1 w-fit ${
                              u.role === "super_admin" ? "bg-yellow-500/20 text-yellow-400" : 
                              u.role === "admin" ? "bg-primary/20 text-primary" : "bg-white/10"
                            }`}>
                              {u.role === "super_admin" && <Crown className="w-3 h-3" />}
                              {u.role === "admin" && <ShieldCheck className="w-3 h-3" />}
                              {u.role?.toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4">
                            {/* Show subscription status or credits */}
                            <div className="flex flex-col gap-1">
                              <span className="font-mono font-bold text-primary">${(u.api_balance ?? u.credits ?? 0).toFixed(2)}</span>
                              {u.is_subscribed && u.subscription_end_date && new Date(u.subscription_end_date) > new Date() && (
                                <span className="text-[10px] font-mono text-emerald-400">
                                  ∞ {language === "tr" ? "ABONE" : "SUB"} · {new Date(u.subscription_end_date).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col items-center gap-1">
                              {u.is_banned ? (
                                <span className="status-expired px-2 py-1 text-xs font-mono">{language === "tr" ? "BANLI" : "BANNED"}</span>
                              ) : (
                                <span className="status-active px-2 py-1 text-xs font-mono">{language === "tr" ? "AKTİF" : "ACTIVE"}</span>
                              )}
                              {/* User badges - 3'erli grid */}
                              {u.badges?.length > 0 && (
                                <div className="grid grid-cols-3 gap-1 mt-1">
                                  {u.badges.map((badge, idx) => {
                                    const badgeConfig = BADGE_OPTIONS.find(b => b.id === badge);
                                    if (!badgeConfig) return null;
                                    const BadgeIcon = badgeConfig.Icon;
                                    return (
                                      <span 
                                        key={idx}
                                        className={`inline-flex items-center justify-center p-1 text-[10px] font-mono rounded bg-${badgeConfig.color}-500/20 text-${badgeConfig.color}-400`}
                                        title={language === "tr" ? badgeConfig.name_tr : badgeConfig.name_en}
                                      >
                                        <BadgeIcon className="w-3 h-3" />
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEditUser(u)} title={t("common.edit")}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {/* Verify/Badge buttons for customer users */}
                              {u.role === "customer" && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className={u.is_verified ? "text-emerald-400" : "text-muted-foreground"}
                                    onClick={() => handleVerifyUser(u)}
                                    title={u.is_verified ? (language === "tr" ? "Onayı Kaldır" : "Remove Verification") : (language === "tr" ? "Onayla" : "Verify")}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="text-yellow-400"
                                    onClick={() => openBadgeDialog(u)}
                                    title={language === "tr" ? "Rozet Ver" : "Give Badge"}
                                  >
                                    <Award className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {/* Subscription button - Super Admin only, for customers */}
                              {user?.role === "super_admin" && u.role === "customer" && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className={u.is_subscribed && u.subscription_end_date && new Date(u.subscription_end_date) > new Date() ? "text-emerald-400" : "text-muted-foreground"}
                                  onClick={() => u.is_subscribed && u.subscription_end_date && new Date(u.subscription_end_date) > new Date() ? handleCancelSubscription(u) : openSubscriptionDialog(u)}
                                  title={u.is_subscribed && u.subscription_end_date && new Date(u.subscription_end_date) > new Date() ? (language === "tr" ? "Abonelik İptal" : "Cancel Subscription") : (language === "tr" ? "Abonelik Ver" : "Give Subscription")}
                                >
                                  <Clock className="w-4 h-4" />
                                </Button>
                              )}
                              {user?.role === "super_admin" && u.id !== user?.id && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => openRoleDialog(u)}
                                    title={t("admin.changeRole")}
                                  >
                                    <Crown className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => openPermissionsDialog(u)}
                                    title={t("admin.permissions")}
                                  >
                                    <Key className="w-4 h-4" />
                                  </Button>
                                  {u.role === "admin" && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      className="text-red-400 hover:text-red-300"
                                      onClick={() => handleRemoveAdminRole(u.id)}
                                      title={t("admin.user.removeAdminRole")}
                                    >
                                      <UserX className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {/* Delete button - can delete other super admins except the first one */}
                                  {u.id !== user?.id && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      className="text-red-400 hover:text-red-300"
                                      onClick={() => openDeleteConfirm(u)}
                                      title={t("admin.user.deleteUser")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Users Pagination - sadece sayfa navigasyonu, arama üstte */}
                <div className="p-4 border-t border-white/10">
                  <DataPagination
                    currentPage={usersPage}
                    totalPages={usersTotalPages}
                    totalItems={usersTotal}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={(page) => { setUsersPage(page); fetchUsers(page, usersSearch); }}
                    showSearch={false}
                    language={language}
                  />
                </div>
              </div>

              {/* User Edit Dialog */}
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogContent className="glass border-white/10 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2">
                      <Edit className="w-5 h-5 text-primary" />
                      {t("admin.user.editUser")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label className="font-mono text-xs">{t("admin.user.name")}</Label>
                      <Input
                        value={userForm.name}
                        onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                        placeholder={t("admin.user.name")}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{t("admin.user.email")}</Label>
                      <Input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                        placeholder="email@example.com"
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">
                        {language === "tr" ? "API Bakiyesi ($)" : "API Balance ($)"}
                      </Label>
                      <Input
                        type="number"
                        value={userForm.credits}
                        onChange={(e) => setUserForm({...userForm, credits: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{t("admin.user.currentPassword")}</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={editingUser?.plain_password || t("admin.user.currentPasswordNoRecord")}
                          readOnly
                          className="bg-black/50 border-white/10 rounded-none font-mono text-primary"
                        />
                        {editingUser?.plain_password && (
                          <Button 
                            type="button"
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(editingUser.plain_password);
                              toast.success(t("admin.user.passwordCopied"));
                            }}
                            className="rounded-none"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{t("admin.user.newPassword")}</Label>
                      <Input
                        type="text"
                        value={userForm.newPassword}
                        onChange={(e) => setUserForm({...userForm, newPassword: e.target.value})}
                        placeholder={t("admin.user.newPasswordPlaceholder")}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded">
                      <div>
                        <Label className="font-mono text-xs text-red-400">{t("admin.user.banAccount")}</Label>
                        <p className="text-xs text-muted-foreground">{t("admin.user.banHelp")}</p>
                      </div>
                      <Switch
                        checked={userForm.is_banned}
                        onCheckedChange={(v) => setUserForm({...userForm, is_banned: v})}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleUserUpdate} className="flex-1 font-mono uppercase bg-primary text-black rounded-none">
                        {t("admin.user.update")}
                      </Button>
                      {user?.role === "super_admin" && editingUser?.role !== "super_admin" && (
                        <Button 
                          variant="destructive" 
                          onClick={() => {
                            setUserDialogOpen(false);
                            openDeleteConfirm(editingUser);
                          }}
                          className="font-mono uppercase rounded-none"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Badge Dialog */}
              <Dialog open={badgeDialogOpen} onOpenChange={setBadgeDialogOpen}>
                <DialogContent className="glass border-white/10 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2">
                      <Award className="w-5 h-5 text-yellow-400" />
                      {language === "tr" ? "Rozet Yönetimi" : "Badge Management"}: {badgeUser?.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    {/* Mevcut rozetler */}
                    {badgeUser?.badges?.length > 0 && (
                      <div>
                        <Label className="font-mono text-xs mb-2 block">{language === "tr" ? "Mevcut Rozetler" : "Current Badges"}</Label>
                        <div className="flex flex-wrap gap-2">
                          {badgeUser.badges.map((badge, idx) => {
                            const badgeConfig = BADGE_OPTIONS.find(b => b.id === badge);
                            if (!badgeConfig) return null;
                            const BadgeIcon = badgeConfig.Icon;
                            return (
                              <span 
                                key={idx}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono rounded bg-${badgeConfig.color}-500/20 text-${badgeConfig.color}-400 border border-${badgeConfig.color}-500/30 cursor-pointer hover:bg-${badgeConfig.color}-500/30`}
                                onClick={() => handleRemoveBadge(badgeUser, badge)}
                                title={language === "tr" ? "Kaldırmak için tıklayın" : "Click to remove"}
                              >
                                <BadgeIcon className="w-3 h-3" />
                                {language === "tr" ? badgeConfig.name_tr : badgeConfig.name_en}
                                <XCircle className="w-3 h-3 ml-1 opacity-60" />
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Yeni rozet ekle */}
                    <div>
                      <Label className="font-mono text-xs mb-2 block">{language === "tr" ? "Rozet Ekle" : "Add Badge"}</Label>
                      <Select value={selectedBadge} onValueChange={setSelectedBadge}>
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                          <SelectValue placeholder={language === "tr" ? "Rozet seçin..." : "Select badge..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {BADGE_OPTIONS.filter(b => !badgeUser?.badges?.includes(b.id)).map(badge => {
                            const BadgeIcon = badge.Icon;
                            return (
                              <SelectItem key={badge.id} value={badge.id}>
                                <div className="flex items-center gap-2">
                                  <BadgeIcon className={`w-4 h-4 text-${badge.color}-400`} />
                                  {language === "tr" ? badge.name_tr : badge.name_en}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setBadgeDialogOpen(false)}
                        className="flex-1 font-mono rounded-none"
                      >
                        {language === "tr" ? "İptal" : "Cancel"}
                      </Button>
                      <Button 
                        onClick={handleAddBadge}
                        disabled={!selectedBadge}
                        className="flex-1 font-mono uppercase rounded-none bg-yellow-500 text-black hover:bg-yellow-400"
                      >
                        <Award className="w-4 h-4 mr-2" />
                        {language === "tr" ? "Rozet Ver" : "Give Badge"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Subscription Dialog */}
              <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
                <DialogContent className="glass border-white/10 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2">
                      <Clock className="w-5 h-5 text-emerald-400" />
                      {language === "tr" ? "Abonelik Ver" : "Give Subscription"}: {subscriptionUser?.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded">
                      <p className="text-sm text-emerald-400 font-mono mb-2">
                        ℹ️ {language === "tr" ? "Abonelik verildiğinde:" : "When subscription is given:"}
                      </p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• {language === "tr" ? "Kullanıcı sınırsız link satın alabilir" : "User can buy unlimited links"}</li>
                        <li>• {language === "tr" ? "Kredi düşürülmez" : "No credits deducted"}</li>
                        <li>• {language === "tr" ? "Süre dolunca normal hesaba döner" : "Returns to normal account when expired"}</li>
                      </ul>
                    </div>
                    
                    <div>
                      <Label className="font-mono text-xs mb-2 block">{language === "tr" ? "Abonelik Süresi (Gün)" : "Subscription Duration (Days)"}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={subscriptionDays}
                        onChange={(e) => setSubscriptionDays(e.target.value)}
                        placeholder={language === "tr" ? "Örn: 30" : "E.g: 30"}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setSubscriptionDialogOpen(false)}
                        className="flex-1 font-mono rounded-none"
                      >
                        {language === "tr" ? "İptal" : "Cancel"}
                      </Button>
                      <Button 
                        onClick={handleAddSubscription}
                        disabled={!subscriptionDays || parseInt(subscriptionDays) <= 0 || subscriptionLoading}
                        className="flex-1 font-mono uppercase rounded-none bg-emerald-500 text-black hover:bg-emerald-400"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        {subscriptionLoading ? (language === "tr" ? "Veriliyor..." : "Giving...") : (language === "tr" ? "Abonelik Ver" : "Give Subscription")}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Delete Confirmation Dialog */}
              <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="glass border-white/10 max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2 text-red-400">
                      <AlertTriangle className="w-5 h-5" />
                      {t("admin.user.deleteUser")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
                      <p className="text-sm text-red-400 font-mono mb-2">
                        ⚠️ {t("admin.user.deleteWarning")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <strong>{userToDelete?.name}</strong> ({userToDelete?.email}) {t("admin.user.deleteUserDescription")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setDeleteConfirmOpen(false)}
                        className="flex-1 font-mono rounded-none"
                      >
                        {t("admin.user.cancel")}
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleDeleteUser}
                        className="flex-1 font-mono uppercase rounded-none"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t("admin.user.delete")}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Role Edit Dialog - Super Admin Only */}
              <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
                <DialogContent className="glass border-white/10">
                  <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2">
                      <Crown className="w-5 h-5 text-primary" />
                      {t("admin.user.changeRole")}: {roleEditUser?.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label className="font-mono text-xs">{t("admin.user.newRole")}</Label>
                      <Select 
                        value={roleEditUser?.newRole || roleEditUser?.role} 
                        onValueChange={(v) => setRoleEditUser({...roleEditUser, newRole: v})}
                      >
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">{t("admin.user.roleCustomer")}</SelectItem>
                          <SelectItem value="admin">{t("admin.user.roleAdmin")}</SelectItem>
                          <SelectItem value="super_admin">{t("admin.user.roleSuperAdmin")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-400 font-mono">
                      ⚠️ {t("admin.user.superAdminWarning")}
                    </div>
                    <Button 
                      onClick={handleRoleUpdate} 
                      className="w-full font-mono uppercase bg-primary text-black rounded-none"
                    >
                      {t("admin.user.updateRole")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Permissions Edit Dialog - Super Admin Only */}
              <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
                <DialogContent className="glass border-white/10 max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2">
                      <Key className="w-5 h-5 text-primary" />
                      {t("admin.permissions")}: {permissionsEditUser?.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="text-xs text-muted-foreground font-mono mb-2">
                      {t("admin.table.role")}: {permissionsEditUser?.role?.toUpperCase()}
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-black/30 border border-white/10">
                      {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={permissionsEditUser?.permissions?.[key] || false}
                            onChange={(e) => setPermissionsEditUser({
                              ...permissionsEditUser, 
                              permissions: {...(permissionsEditUser?.permissions || {}), [key]: e.target.checked}
                            })}
                            className="rounded"
                            disabled={permissionsEditUser?.role === "super_admin"}
                          />
                          <span className="font-mono">{t(`permission.${key}`)}</span>
                        </label>
                      ))}
                    </div>
                    <Button 
                      onClick={handlePermissionsUpdate} 
                      className="w-full font-mono uppercase bg-primary text-black rounded-none"
                      disabled={permissionsEditUser?.role === "super_admin"}
                    >
                      {t("admin.savePermissions")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === "packages" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-mono font-bold uppercase">{t("admin.packages")}</h1>
                <Dialog open={packageDialogOpen} onOpenChange={(open) => {
                  setPackageDialogOpen(open);
                  if (!open) {
                    setEditingPackage(null);
                    setPackageForm({ name: "", credits: "", price: "", bonus_credits: "0", is_featured: false, template: "default", description: "", product_type: "subscription", days: "" });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      className="font-mono uppercase bg-primary text-black rounded-none"
                      onClick={() => {
                        setEditingPackage(null);
                        setPackageForm({ name: "", credits: "", price: "", bonus_credits: "0", is_featured: false, template: "default", description: "", product_type: "subscription", days: "" });
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" /> {t("admin.newPackage")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass border-white/10">
                    <DialogHeader>
                      <DialogTitle className="font-mono">
                        {editingPackage
                          ? (language === "tr" ? "Paketi Düzenle" : "Edit Package")
                          : (language === "tr" ? "Yeni Abonelik Paketi" : "New Subscription Package")}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handlePackageSubmit} className="space-y-4">
                      <div>
                        <Label className="font-mono text-xs">{language === "tr" ? "Ürün tipi" : "Product type"}</Label>
                        <Select value={packageForm.product_type || "subscription"} onValueChange={(v) => setPackageForm({...packageForm, product_type: v})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="subscription">{language === "tr" ? "Abonelik paketi" : "Subscription package"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-mono text-xs">{language === "tr" ? "Paket Adı" : "Package Name"}</Label>
                        <Input value={packageForm.name} onChange={(e) => setPackageForm({...packageForm, name: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-mono text-xs">{language === "tr" ? "Gün sayısı" : "Days"}</Label>
                          <Input type="number" min="1" value={packageForm.days} onChange={(e) => setPackageForm({...packageForm, days: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                        </div>
                        <div>
                          <Label className="font-mono text-xs">{language === "tr" ? "Fiyat ($)" : "Price ($)"}</Label>
                          <Input type="number" step="0.01" value={packageForm.price} onChange={(e) => setPackageForm({...packageForm, price: e.target.value})} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                        </div>
                      </div>
                      <div>
                        <Label className="font-mono text-xs">{language === "tr" ? "Açıklama" : "Description"}</Label>
                        <Input value={packageForm.description} onChange={(e) => setPackageForm({...packageForm, description: e.target.value})} className="bg-black/50 border-white/10 rounded-none font-mono" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-mono text-xs">{language === "tr" ? "Şablon" : "Template"}</Label>
                          <Select value={packageForm.template} onValueChange={(v) => setPackageForm({...packageForm, template: v})}>
                            <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">{language === "tr" ? "Varsayılan" : "Default"}</SelectItem>
                              <SelectItem value="popular">{language === "tr" ? "Popüler (Yeşil)" : "Popular (Green)"}</SelectItem>
                              <SelectItem value="premium">{language === "tr" ? "Premium (Mor)" : "Premium (Purple)"}</SelectItem>
                              <SelectItem value="gold">{language === "tr" ? "Altın (Lüks)" : "Gold (Luxury)"}</SelectItem>
                              <SelectItem value="silver">{language === "tr" ? "Gümüş (Profesyonel)" : "Silver (Professional)"}</SelectItem>
                              <SelectItem value="platinum">{language === "tr" ? "Platin (Elit)" : "Platinum (Elite)"}</SelectItem>
                              <SelectItem value="startup">{language === "tr" ? "Startup (Mavi)" : "Startup (Blue)"}</SelectItem>
                              <SelectItem value="agency">{language === "tr" ? "Ajans (Turuncu)" : "Agency (Orange)"}</SelectItem>
                              <SelectItem value="unlimited">{language === "tr" ? "Sınırsız (Gökkuşağı)" : "Unlimited (Rainbow)"}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                          <Switch checked={packageForm.is_featured} onCheckedChange={(v) => setPackageForm({...packageForm, is_featured: v})} />
                          <Label className="font-mono text-xs">{language === "tr" ? "Öne Çıkar" : "Featured"}</Label>
                        </div>
                      </div>
                      <Button type="submit" className="w-full font-mono uppercase bg-primary text-black rounded-none">
                        {editingPackage ? (language === "tr" ? "GÜNCELLE" : "UPDATE") : t("admin.add")}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {packages.filter((p) => p.product_type === "subscription").map(pkg => {
                  const getPackageClass = () => {
                    if (pkg.is_featured || pkg.template === "popular") return "package-popular";
                    if (pkg.template === "premium") return "package-premium";
                    if (pkg.template === "gold") return "package-gold";
                    if (pkg.template === "silver") return "package-silver";
                    if (pkg.template === "platinum") return "package-platinum";
                    if (pkg.template === "startup") return "package-startup";
                    if (pkg.template === "agency") return "package-agency";
                    if (pkg.template === "unlimited") return "package-unlimited";
                    return "package-default";
                  };
                  
                  const getTemplateBadge = () => {
                    if (pkg.is_featured || pkg.template === "popular") return { text: language === "tr" ? "POPÜLER" : "POPULAR", color: "text-primary" };
                    if (pkg.template === "premium") return { text: "PREMIUM", color: "text-purple-400" };
                    if (pkg.template === "gold") return { text: language === "tr" ? "ALTIN" : "GOLD", color: "text-yellow-400" };
                    if (pkg.template === "silver") return { text: language === "tr" ? "GÜMÜŞ" : "SILVER", color: "text-gray-300" };
                    if (pkg.template === "platinum") return { text: language === "tr" ? "PLATİN" : "PLATINUM", color: "text-slate-200" };
                    if (pkg.template === "startup") return { text: "STARTUP", color: "text-blue-400" };
                    if (pkg.template === "agency") return { text: language === "tr" ? "AJANS" : "AGENCY", color: "text-orange-400" };
                    if (pkg.template === "unlimited") return { text: language === "tr" ? "SINIRSIZ" : "UNLIMITED", color: "text-pink-400" };
                    return null;
                  };
                  
                  const badge = getTemplateBadge();
                  
                  return (
                  <div key={pkg.id} className={`p-6 ${getPackageClass()}`}>
                    {badge && (
                      <div className={`text-xs font-mono ${badge.color} mb-2 flex items-center gap-1`}>
                        <Sparkles className="w-3 h-3" /> {badge.text}
                      </div>
                    )}
                    <h3 className="font-mono font-bold text-lg">{language === "tr" ? (pkg.name_tr || pkg.name) : (pkg.name_en || pkg.name)}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{language === "tr" ? (pkg.description_tr || pkg.description) : (pkg.description_en || pkg.description)}</p>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-3xl font-mono font-bold text-primary">${pkg.price}</span>
                      <span className="text-muted-foreground">
                        / {pkg.product_type === "subscription" ? `${pkg.days} ${language === "tr" ? "gün abonelik" : "days subscription"}` : `${pkg.total_credits} ${t("admin.credits")}`}
                      </span>
                    </div>
                    {pkg.product_type !== "subscription" && pkg.bonus_credits > 0 && (
                      <div className="text-sm text-primary mb-4">+{pkg.bonus_credits} {t("admin.bonusCredits")}!</div>
                    )}
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditPackage(pkg)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Edit className="w-4 h-4 mr-2" /> {language === "tr" ? "Düzenle" : "Edit"}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={async () => {
                          try {
                            await api.delete(`/packages/${pkg.id}`);
                            // Optimistic update: remove from state immediately
                            setPackages(packages.filter(p => p.id !== pkg.id));
                            toast.success(language === "tr" ? "Paket silindi" : "Package deleted");
                            // Fetch fresh packages directly (bypass cache, refreshAllData may use stale cache)
                            const freshPackages = await api.get("/packages");
                            setPackages(freshPackages.data);
                          } catch (error) {
                            // On error, refresh from server to get actual state
                            try {
                              const freshPackages = await api.get("/packages");
                              setPackages(freshPackages.data);
                            } catch (refreshError) {
                              console.error("Failed to refresh packages:", refreshError);
                            }
                            toast.error(error.response?.data?.detail || error.response?.data?.message || (language === "tr" ? "Paket silinemedi" : "Failed to delete package"));
                          }
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> {t("common.delete")}
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Durations */}
              <div className="glass p-6 mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-mono font-bold">{t("admin.linkDurations")}</h3>
                  <Dialog open={durationDialogOpen} onOpenChange={(open) => {
                    setDurationDialogOpen(open);
                    if (!open) { setEditingDuration(null); setDurationForm({ name: "", days: "", credit_multiplier: "1.0" }); }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="font-mono bg-primary text-black rounded-none">
                        <Plus className="w-4 h-4 mr-2" /> {t("admin.add")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-white/10">
                      <DialogHeader>
                        <DialogTitle className="font-mono">{editingDuration ? t("admin.duration.edit") : t("admin.duration.add")}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleDurationSubmit} className="space-y-4">
                        <div>
                          <Label className="font-mono text-xs">{t("admin.duration.name")}</Label>
                          <Input value={durationForm.name} onChange={(e) => setDurationForm({...durationForm, name: e.target.value})} placeholder={language === "tr" ? "3 Ay" : "3 Months"} required className="bg-black/50 border-white/10 rounded-none font-mono" />
                        </div>
                        <div>
                          <Label className="font-mono text-xs">{t("admin.duration.days")}</Label>
                          <Input type="number" value={durationForm.days} onChange={(e) => setDurationForm({...durationForm, days: e.target.value})} placeholder="90" required className="bg-black/50 border-white/10 rounded-none font-mono" />
                        </div>
                        <div>
                          <Label className="font-mono text-xs">{t("admin.duration.multiplier")}</Label>
                          <Input type="number" step="0.1" value={durationForm.credit_multiplier} onChange={(e) => setDurationForm({...durationForm, credit_multiplier: e.target.value})} placeholder="2.5" required className="bg-black/50 border-white/10 rounded-none font-mono" />
                          <p className="text-xs text-muted-foreground mt-1">{t("admin.duration.multiplierHelp")}</p>
                        </div>
                        <Button type="submit" className="w-full font-mono uppercase bg-primary text-black rounded-none">
                          {editingDuration ? t("common.update") : t("admin.add")}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="space-y-2">
                  {durations.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/10">
                      <div className="flex-1">
                        <span className="font-mono font-bold">{language === "tr" ? (d.name_tr || d.name) : (d.name_en || d.name)}</span>
                        <span className="text-muted-foreground font-mono text-sm ml-4">{d.days} {t("admin.days")}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-primary">×{d.credit_multiplier}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDuration(d)}
                          className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDurationDeleteConfirm(d)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Duration Delete Confirmation Dialog */}
                <Dialog open={durationDeleteConfirmOpen} onOpenChange={setDurationDeleteConfirmOpen}>
                  <DialogContent className="glass border-white/10">
                    <DialogHeader>
                      <DialogTitle className="font-mono text-red-500">{t("admin.user.deleteConfirm")}</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground">{t("admin.duration.deleteConfirm")}</p>
                    <p className="text-sm text-muted-foreground">{t("admin.user.deleteWarning")}</p>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" onClick={() => setDurationDeleteConfirmOpen(false)} className="flex-1 rounded-none font-mono">
                        {t("common.cancel")}
                      </Button>
                      <Button onClick={handleDeleteDuration} className="flex-1 bg-red-600 hover:bg-red-700 rounded-none font-mono">
                        {t("common.delete")}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          )}

          {/* SEO Services Tab */}
          {activeTab === "seo-services" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-mono font-bold uppercase flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-primary" />
                  {t("admin.seoServices.title")}
                </h1>
                <Dialog open={seoServiceDialogOpen} onOpenChange={(open) => { 
                  setSeoServiceDialogOpen(open); 
                  if (!open) { setEditingSeoService(null); setSeoServiceForm({ name: "", service_type: "index", credit_cost: "5", description: "", is_auto: true, target_countries: "" }); }
                }}>
                  <DialogTrigger asChild>
                    <Button className="font-mono bg-primary text-black rounded-none">
                      <Plus className="w-4 h-4 mr-2" /> {t("admin.seoServices.newService")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass border-white/10">
                    <DialogHeader>
                      <DialogTitle className="font-mono">{editingSeoService ? t("admin.seoServices.editService") : t("admin.seoServices.addService")}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSeoServiceSubmit} className="space-y-4 pt-4">
                      <div>
                        <Label className="font-mono text-xs uppercase">{t("admin.seoServices.serviceName")}</Label>
                        <Input
                          required
                          value={seoServiceForm.name}
                          onChange={(e) => setSeoServiceForm({...seoServiceForm, name: e.target.value})}
                          placeholder={language === "tr" ? "Hızlı Index" : "Fast Index"}
                          className="bg-black/50 border-white/10 rounded-none font-mono"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">{t("admin.seoServices.serviceType")}</Label>
                        <Select value={seoServiceForm.service_type} onValueChange={(v) => setSeoServiceForm({...seoServiceForm, service_type: v})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="index">{t("admin.seoServices.typeIndex")}</SelectItem>
                            <SelectItem value="backlink_check">{t("admin.seoServices.typeBacklink")}</SelectItem>
                            <SelectItem value="ranking">{t("admin.seoServices.typeRanking")}</SelectItem>
                            <SelectItem value="audit">{t("admin.seoServices.typeAudit")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">{t("admin.seoServices.creditCost")}</Label>
                        <Input
                          type="number"
                          required
                          min="1"
                          value={seoServiceForm.credit_cost}
                          onChange={(e) => setSeoServiceForm({...seoServiceForm, credit_cost: e.target.value})}
                          className="bg-black/50 border-white/10 rounded-none font-mono"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">{t("admin.seoServices.description")}</Label>
                        <Input
                          value={seoServiceForm.description}
                          onChange={(e) => setSeoServiceForm({...seoServiceForm, description: e.target.value})}
                          placeholder={language === "tr" ? "Hizmet açıklaması" : "Service description"}
                          className="bg-black/50 border-white/10 rounded-none font-mono"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">{t("admin.seoServices.targetCountries")}</Label>
                        <Input
                          value={seoServiceForm.target_countries}
                          onChange={(e) => setSeoServiceForm({...seoServiceForm, target_countries: e.target.value})}
                          placeholder="TR, US, DE, UK"
                          className="bg-black/50 border-white/10 rounded-none font-mono"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={seoServiceForm.is_auto}
                          onCheckedChange={(checked) => setSeoServiceForm({...seoServiceForm, is_auto: checked})}
                        />
                        <Label className="font-mono text-xs">{t("admin.seoServices.isAuto")}</Label>
                      </div>
                      <Button type="submit" className="w-full font-mono bg-primary text-black rounded-none">
                        {editingSeoService ? t("common.update") : t("admin.add")}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* SEO Services List */}
              <div className="glass overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.seoServices.serviceName")}</th>
                      <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.seoServices.serviceType")}</th>
                      <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.credits")}</th>
                      <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{language === "tr" ? "Mod" : "Mode"}</th>
                      <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("common.status")}</th>
                      <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">{t("admin.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seoServices.map((service) => (
                      <tr key={service.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <div>
                            <span className="font-mono font-bold">{language === "tr" ? (service.name_tr || service.name) : (service.name_en || service.name)}</span>
                            {(service.description || service.description_tr || service.description_en) && (
                              <p className="text-xs text-muted-foreground mt-1">{language === "tr" ? (service.description_tr || service.description) : (service.description_en || service.description)}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 font-mono text-sm">
                          {service.service_type === "index" && (language === "tr" ? "İndexleme" : "Indexing")}
                          {service.service_type === "backlink_check" && (language === "tr" ? "Backlink" : "Backlink")}
                          {service.service_type === "ranking" && (language === "tr" ? "Sıralama" : "Ranking")}
                          {service.service_type === "audit" && "SEO Audit"}
                        </td>
                        <td className="p-4">
                          <span className="font-mono font-bold text-primary">{service.credit_cost}</span>
                        </td>
                        <td className="p-4">
                          {service.is_auto ? (
                            <span className="text-xs bg-primary/20 text-primary px-2 py-1 font-mono">{language === "tr" ? "OTOMATİK" : "AUTO"}</span>
                          ) : (
                            <span className="text-xs bg-secondary/20 text-secondary px-2 py-1 font-mono">{language === "tr" ? "MANUEL" : "MANUAL"}</span>
                          )}
                        </td>
                        <td className="p-4">
                          {service.is_active !== false ? (
                            <span className="status-active px-2 py-1 text-xs font-mono">{language === "tr" ? "AKTİF" : "ACTIVE"}</span>
                          ) : (
                            <span className="status-expired px-2 py-1 text-xs font-mono">{language === "tr" ? "PASİF" : "INACTIVE"}</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditSeoService(service)} className="h-8 w-8 text-muted-foreground hover:text-white">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteSeoService(service.id)} className="h-8 w-8 text-muted-foreground hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Info Box */}
              <div className="glass p-6 border-l-4 border-blue-500">
                <h3 className="font-mono font-bold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-blue-500" />
                  {language === "tr" ? "SEO Hizmetleri Hakkında" : "About SEO Services"}
                </h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• <strong>{language === "tr" ? "İndexleme:" : "Indexing:"}</strong> {language === "tr" ? "Google ve Bing'e URL gönderimi için Google Indexing API veya IndexNow API gereklidir." : "Requires Google Indexing API or IndexNow API for URL submission to Google and Bing."}</li>
                  <li>• <strong>{language === "tr" ? "Backlink Analizi:" : "Backlink Analysis:"}</strong> {language === "tr" ? "Moz, Ahrefs veya SEMrush API ile çalışır." : "Works with Moz, Ahrefs or SEMrush API."}</li>
                  <li>• <strong>{language === "tr" ? "Sıralama Takibi:" : "Ranking Tracking:"}</strong> {language === "tr" ? "DataForSEO veya SEMrush API ile çalışır." : "Works with DataForSEO or SEMrush API."}</li>
                  <li>• <strong>{language === "tr" ? "SEO Audit:" : "SEO Audit:"}</strong> {language === "tr" ? "Kapsamlı site analizi için birden fazla API kullanılabilir." : "Multiple APIs can be used for comprehensive site analysis."}</li>
                  <li>• {language === "tr" ? "API ayarlarını Sistem Ayarları > SEO API bölümünden yapılandırın." : "Configure API settings from System Settings > SEO API section."}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === "tickets" && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h1 className="text-2xl font-mono font-bold uppercase flex items-center gap-2">
                  <HeadphonesIcon className="w-6 h-6 text-primary" />
                  {language === "tr" ? "DESTEK TALEPLERİ" : "SUPPORT TICKETS"} ({ticketsTotal})
                </h1>
              </div>

              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  placeholder={language === "tr" ? "Ara (konu, email, isim)..." : "Search (subject, email, name)..."}
                  value={ticketsSearch}
                  onChange={(e) => setTicketsSearch(e.target.value)}
                  className="md:max-w-sm font-mono"
                />
                <Select value={ticketsStatusFilter || "all"} onValueChange={(val) => setTicketsStatusFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="md:w-40">
                    <SelectValue placeholder={language === "tr" ? "Tüm Durumlar" : "All Status"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === "tr" ? "Tümü" : "All"}</SelectItem>
                    <SelectItem value="open">{language === "tr" ? "Açık" : "Open"}</SelectItem>
                    <SelectItem value="in_progress">{language === "tr" ? "İşlemde" : "In Progress"}</SelectItem>
                    <SelectItem value="resolved">{language === "tr" ? "Çözüldü" : "Resolved"}</SelectItem>
                    <SelectItem value="closed">{language === "tr" ? "Kapatıldı" : "Closed"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tickets Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 font-mono text-xs text-muted-foreground">{language === "tr" ? "Talep" : "Ticket"}</th>
                      <th className="text-left p-3 font-mono text-xs text-muted-foreground">{language === "tr" ? "Müşteri" : "Customer"}</th>
                      <th className="text-left p-3 font-mono text-xs text-muted-foreground">{language === "tr" ? "Durum" : "Status"}</th>
                      <th className="text-left p-3 font-mono text-xs text-muted-foreground">{language === "tr" ? "Öncelik" : "Priority"}</th>
                      <th className="text-left p-3 font-mono text-xs text-muted-foreground">{language === "tr" ? "Tarih" : "Date"}</th>
                      <th className="text-left p-3 font-mono text-xs text-muted-foreground">{language === "tr" ? "İşlem" : "Action"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr key={ticket.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3">
                          <div className="font-mono text-sm font-semibold">{ticket.subject}</div>
                          <div className="text-xs text-muted-foreground">
                            {ticket.category === "general" && (language === "tr" ? "Genel" : "General")}
                            {ticket.category === "technical" && (language === "tr" ? "Teknik" : "Technical")}
                            {ticket.category === "billing" && (language === "tr" ? "Ödeme/Fatura" : "Billing")}
                            {ticket.category === "other" && (language === "tr" ? "Diğer" : "Other")}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">{ticket.user_name}</div>
                          <div className="text-xs text-muted-foreground">{ticket.user_email}</div>
                        </td>
                        <td className="p-3">
                          {ticket.status === "open" && (
                            <span className="px-2 py-1 text-xs font-mono bg-blue-500/20 text-blue-400 rounded">{language === "tr" ? "Açık" : "Open"}</span>
                          )}
                          {ticket.status === "in_progress" && (
                            <span className="px-2 py-1 text-xs font-mono bg-yellow-500/20 text-yellow-400 rounded">{language === "tr" ? "İşlemde" : "In Progress"}</span>
                          )}
                          {ticket.status === "resolved" && (
                            <span className="px-2 py-1 text-xs font-mono bg-green-500/20 text-green-400 rounded">{language === "tr" ? "Çözüldü" : "Resolved"}</span>
                          )}
                          {ticket.status === "closed" && (
                            <span className="px-2 py-1 text-xs font-mono bg-gray-500/20 text-gray-400 rounded">{language === "tr" ? "Kapatıldı" : "Closed"}</span>
                          )}
                        </td>
                        <td className="p-3">
                          {ticket.priority === "low" && <span className="text-xs text-gray-400">{language === "tr" ? "Düşük" : "Low"}</span>}
                          {ticket.priority === "medium" && <span className="text-xs text-blue-400">{language === "tr" ? "Orta" : "Medium"}</span>}
                          {ticket.priority === "high" && <span className="text-xs text-orange-400">{language === "tr" ? "Yüksek" : "High"}</span>}
                          {ticket.priority === "urgent" && <span className="text-xs text-red-400">{language === "tr" ? "Acil" : "Urgent"}</span>}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {new Date(ticket.updated_at).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                        </td>
                        <td className="p-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/tickets/${ticket.id}`);
                                setSelectedTicket(res.data);
                                setTicketDetailOpen(true);
                              } catch (error) {
                                toast.error("Ticket yüklenemedi");
                              }
                            }}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {language === "tr" ? "Görüntüle" : "View"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {tickets.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          {language === "tr" ? "Henüz destek talebi yok" : "No support tickets yet"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {ticketsTotalPages > 1 && (
                <DataPagination
                  currentPage={ticketsPage}
                  totalPages={ticketsTotalPages}
                  onPageChange={(page) => {
                    setTicketsPage(page);
                    fetchTickets(page, ticketsSearch, ticketsStatusFilter);
                  }}
                />
              )}
            </div>
          )}

          {/* Announcements Tab */}
          {activeTab === "announcements" && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h1 className="text-2xl font-mono font-bold uppercase flex items-center gap-2">
                  <Megaphone className="w-6 h-6 text-primary" />
                  {language === "tr" ? "DUYURULAR" : "ANNOUNCEMENTS"} ({announcementsTotal})
                </h1>
                <Button 
                  onClick={() => {
                    setEditingAnnouncement(null);
                    setAnnouncementForm({
                      title: "", title_en: "", content: "", content_en: "",
                      type: "info", is_active: true, show_on_dashboard: true, expires_at: ""
                    });
                    setAnnouncementDialogOpen(true);
                  }}
                  className="font-mono"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {language === "tr" ? "YENİ DUYURU" : "NEW ANNOUNCEMENT"}
                </Button>
              </div>

              {/* Announcements List */}
              <div className="space-y-4">
                {announcements.map((announcement) => (
                  <div 
                    key={announcement.id} 
                    className={`glass p-4 border-l-4 ${
                      announcement.type === "info" ? "border-blue-500" :
                      announcement.type === "warning" ? "border-yellow-500" :
                      announcement.type === "success" ? "border-green-500" :
                      "border-red-500"
                    } ${!announcement.is_active ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-mono font-bold">{announcement.title}</h3>
                          {!announcement.is_active && (
                            <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">
                              {language === "tr" ? "Pasif" : "Inactive"}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            announcement.type === "info" ? "bg-blue-500/20 text-blue-400" :
                            announcement.type === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                            announcement.type === "success" ? "bg-green-500/20 text-green-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {announcement.type.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{announcement.content}</p>
                        {announcement.title_en && (
                          <p className="text-sm text-muted-foreground mt-2 italic">EN: {announcement.title_en}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>{new Date(announcement.created_at).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}</span>
                          {announcement.expires_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {language === "tr" ? "Bitiş:" : "Expires:"} {new Date(announcement.expires_at).toLocaleDateString()}
                            </span>
                          )}
                          {announcement.show_on_dashboard && (
                            <span className="text-primary">{language === "tr" ? "Dashboard'da gösteriliyor" : "Shown on dashboard"}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            setEditingAnnouncement(announcement);
                            setAnnouncementForm({
                              title: announcement.title,
                              title_en: announcement.title_en || "",
                              content: announcement.content,
                              content_en: announcement.content_en || "",
                              type: announcement.type,
                              is_active: announcement.is_active,
                              show_on_dashboard: announcement.show_on_dashboard,
                              expires_at: announcement.expires_at || ""
                            });
                            setAnnouncementDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={async () => {
                            if (confirm(language === "tr" ? "Bu duyuruyu silmek istediğinize emin misiniz?" : "Are you sure you want to delete this announcement?")) {
                              try {
                                await api.delete(`/admin/announcements/${announcement.id}`);
                                toast.success(language === "tr" ? "Duyuru silindi" : "Announcement deleted");
                                fetchAnnouncements(announcementsPage);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Error");
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {announcements.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground glass">
                    <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-mono">{language === "tr" ? "Henüz duyuru yok" : "No announcements yet"}</p>
                  </div>
                )}
              </div>

              {announcementsTotalPages > 1 && (
                <DataPagination
                  currentPage={announcementsPage}
                  totalPages={announcementsTotalPages}
                  onPageChange={(page) => {
                    setAnnouncementsPage(page);
                    fetchAnnouncements(page);
                  }}
                />
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              <h1 className="text-2xl font-mono font-bold uppercase">{t("admin.settings")}</h1>
              
              {/* Theme Settings */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  {t("admin.themeSettings")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{t("admin.themeDesc")}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {themeOptions.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => {
                        setTheme(theme.id);
                        toast.success(language === "tr" ? `${theme.name} teması aktif` : `${theme.name} theme activated`);
                      }}
                      className={`p-4 border text-center transition-all ${
                        currentTheme === theme.id
                          ? "border-primary bg-primary/20 ring-2 ring-primary"
                          : "border-white/10 hover:border-white/30 bg-black/50"
                      }`}
                    >
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full overflow-hidden">
                        {theme.id === "default" && (
                          <div className="w-full h-full bg-gradient-to-br from-gray-900 to-black" />
                        )}
                        {theme.id === "cosmic" && (
                          <div className="w-full h-full bg-gradient-to-br from-purple-900 via-blue-900 to-green-900" />
                        )}
                        {theme.id === "nebulaflow" && (
                          <div className="w-full h-full bg-[#05050f] relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/40 via-fuchsia-400/20 to-cyan-400/40" />
                            <div className="absolute left-1 top-2 h-5 w-5 rounded-full bg-violet-400/70 blur-md" />
                            <div className="absolute right-1 bottom-1 h-5 w-5 rounded-full bg-cyan-300/70 blur-md" />
                          </div>
                        )}
                        {theme.id === "aurora" && (
                          <div className="w-full h-full bg-gradient-to-b from-green-500/30 via-blue-500/30 to-purple-500/30" />
                        )}
                        {theme.id === "matrix" && (
                          <div className="w-full h-full bg-black flex items-center justify-center text-green-500 font-mono text-xs">01</div>
                        )}
                        {theme.id === "neoncity" && (
                          <div className="w-full h-full bg-gradient-to-b from-purple-900 to-pink-900" />
                        )}
                        {theme.id === "christmas" && (
                          <div className="w-full h-full bg-gradient-to-b from-red-900 to-green-900 flex items-center justify-center text-white">❄</div>
                        )}
                      </div>
                      <p className="font-mono text-xs font-bold">{t(`admin.theme.${theme.id}`) || theme.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{t(`admin.theme.${theme.id}Desc`) || theme.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo Settings */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-primary" />
                  {t("admin.logoSettings")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{t("admin.logoDesc")}</p>
                <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
                  {logoOptions.map((logo) => (
                    <button
                      key={logo.id}
                      onClick={() => {
                        setLogo(logo.id);
                        toast.success(language === "tr" ? `${logo.name} logosu aktif` : `${logo.name} logo activated`);
                      }}
                      className={`p-3 border transition-all ${
                        currentLogo === logo.id
                          ? "border-primary bg-primary/20 ring-2 ring-primary"
                          : "border-white/10 hover:border-white/30 bg-black/50"
                      }`}
                      title={t(`admin.logo.${logo.id}`) || logo.name}
                    >
                      <div className="w-10 h-10 mx-auto bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                        <div 
                          className="w-7 h-7 text-black"
                          dangerouslySetInnerHTML={{ __html: logo.svg }}
                        />
                      </div>
                      <p className="font-mono text-[10px] text-center mt-2 truncate">{logo.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Site Content Settings */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  {t("admin.siteContentSettings")}
                </h3>
                
                {/* Footer Settings */}
                <div className="space-y-4 mb-6">
                  <h4 className="font-mono text-sm text-primary">{t("admin.footerSettings")}</h4>
                  <div>
                    <Label className="font-mono text-xs">{t("admin.footerText")}</Label>
                    <Input
                      value={footerCredit}
                      onChange={(e) => setFooterCredit(e.target.value)}
                      placeholder="moderated by AnubisROOT-NoxShell"
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs">{t("admin.telegramLink")}</Label>
                    <Input
                      value={telegramLink}
                      onChange={(e) => setTelegramLink(e.target.value)}
                      placeholder="https://t.me/kanaliniz"
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t("admin.telegramLinkDesc")}</p>
                  </div>
                </div>
                
                {/* Features Settings */}
                <div className="space-y-4">
                  <h4 className="font-mono text-sm text-primary">{t("admin.featureCards")}</h4>
                  <p className="text-xs text-muted-foreground">{t("admin.featureCardsDesc")}</p>
                  
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {editingFeatures.map((feature, idx) => (
                      <div key={idx} className="p-4 bg-black/30 border border-white/10 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-primary">{t("admin.feature")} {idx + 1}</span>
                        </div>
                        <Input
                          value={feature.title}
                          onChange={(e) => {
                            const updated = [...editingFeatures];
                            updated[idx] = { ...updated[idx], title: e.target.value };
                            setEditingFeatures(updated);
                          }}
                          placeholder={language === "tr" ? "Başlık" : "Title"}
                          className="bg-black/50 border-white/10 rounded-none font-mono text-sm"
                        />
                        <Input
                          value={feature.description}
                          onChange={(e) => {
                            const updated = [...editingFeatures];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setEditingFeatures(updated);
                          }}
                          placeholder={language === "tr" ? "Açıklama" : "Description"}
                          className="bg-black/50 border-white/10 rounded-none font-mono text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={() => {
                    updateSiteContent({
                      features: editingFeatures,
                      footer: {
                        credit: footerCredit,
                        telegramLink: telegramLink
                      }
                    });
                    toast.success(language === "tr" ? "Site içeriği kaydedildi" : "Site content saved");
                  }}
                  className="w-full mt-4 font-mono bg-primary text-black rounded-none"
                >
                  {language === "tr" ? "SİTE İÇERİĞİNİ KAYDET" : "SAVE SITE CONTENT"}
                </Button>
              </div>
            </div>
          )}

          {/* BOT API Tab - Super Admin Only (Telegram bot satış kanalı) */}
          {activeTab === "bot-api" && user?.role === "super_admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-mono font-bold uppercase flex items-center gap-2">
                  <Bot className="w-6 h-6 text-primary" />
                  {language === "tr" ? "Bot API (Telegram Bot Kanalı)" : "Bot API (Telegram Bot Channel)"}
                </h1>
                <Button
                  onClick={() => {
                    setBotApiNewKeyResult(null);
                    setBotApiKeyForm({ name: "", note: "" });
                    setBotApiKeyDialogOpen(true);
                  }}
                  className="font-mono bg-primary text-black rounded-none"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {language === "tr" ? "YENİ API KEY" : "NEW API KEY"}
                </Button>
              </div>

              {/* Info banner */}
              <div className="glass p-4 border-l-4 border-primary">
                <p className="text-sm text-muted-foreground">
                  {language === "tr"
                    ? "Telegram bot, bu API üzerinden müşterilerinin link satışı siparişlerini sisteme iletir. Bot kendi tarafında paket (tek-basım / 1g / 7g / 30g) ve ödeme (NOWPayments) yönetimini yapar. Bu panel sadece bot kimlik doğrulaması (API key) ve geçmiş takibi içindir."
                    : "Telegram bot forwards customer link placement orders to the system via this API. The bot manages its own packages and payments. This panel is for bot authentication (API keys) and audit only."}
                </p>
                <p className="text-xs text-yellow-400 mt-2 font-mono">
                  {language === "tr"
                    ? "⚠️ Yeni oluşturulan token YALNIZCA bir kez gösterilir. Bot tarafına kopyalamayı unutmayın."
                    : "⚠️ Newly created tokens are shown ONLY ONCE. Make sure to copy to the bot side."}
                </p>
              </div>

              {/* Stats grid */}
              {botApiStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="glass p-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{language === "tr" ? "Aktif Key" : "Active Keys"}</p>
                    <p className="text-2xl font-mono font-bold mt-1">
                      {botApiStats.active_keys}
                      <span className="text-sm text-muted-foreground"> / {botApiStats.total_keys}</span>
                    </p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{language === "tr" ? "24s Sipariş" : "24h Orders"}</p>
                    <p className="text-2xl font-mono font-bold mt-1 text-primary">{botApiStats.orders_last_24h}</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{language === "tr" ? "7g Sipariş" : "7d Orders"}</p>
                    <p className="text-2xl font-mono font-bold mt-1 text-blue-400">{botApiStats.orders_last_7d}</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{language === "tr" ? "Toplam" : "Total"}</p>
                    <p className="text-2xl font-mono font-bold mt-1">{botApiStats.total_bot_orders}</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{language === "tr" ? "Müşteri (TOP)" : "Customers (TOP)"}</p>
                    <p className="text-2xl font-mono font-bold mt-1 text-green-400">{(botApiStats.top_customers || []).length}</p>
                  </div>
                </div>
              )}

              {/* Status breakdown */}
              {botApiStats?.by_status && Object.keys(botApiStats.by_status).length > 0 && (
                <div className="glass p-4">
                  <h3 className="font-mono text-sm uppercase mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" />
                    {language === "tr" ? "Sipariş Durumları" : "Order Status Breakdown"}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(botApiStats.by_status).map(([status, count]) => (
                      <div key={status} className="px-3 py-1 bg-white/5 border border-white/10 font-mono text-xs">
                        <span className="text-muted-foreground uppercase">{status}:</span>{" "}
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* API Keys list */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  {language === "tr" ? "API Anahtarları" : "API Keys"}
                </h3>
                {botApiKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground font-mono">
                    {language === "tr" ? "Henüz key yok. Sağ üstten yeni key oluşturun." : "No keys yet. Create one from top right."}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm font-mono">
                      <thead className="text-xs uppercase text-muted-foreground border-b border-white/10">
                        <tr>
                          <th className="text-left py-2">{language === "tr" ? "Ad" : "Name"}</th>
                          <th className="text-left py-2">Token</th>
                          <th className="text-right py-2">{language === "tr" ? "İstek" : "Requests"}</th>
                          <th className="text-right py-2">{language === "tr" ? "Bulk" : "Bulk"}</th>
                          <th className="text-right py-2">{language === "tr" ? "Sipariş" : "Orders"}</th>
                          <th className="text-left py-2">{language === "tr" ? "Son Kullanım" : "Last Used"}</th>
                          <th className="text-left py-2">{language === "tr" ? "Durum" : "Status"}</th>
                          <th className="text-right py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {botApiKeys.map((k) => (
                          <tr key={k.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3">
                              <div className="font-bold">{k.name}</div>
                              {k.note && <div className="text-xs text-muted-foreground">{k.note}</div>}
                            </td>
                            <td className="py-3 text-xs">{k.token_preview}</td>
                            <td className="py-3 text-right">{k.requests_count}</td>
                            <td className="py-3 text-right">{k.bulk_orders_count}</td>
                            <td className="py-3 text-right">{k.orders_created_count}</td>
                            <td className="py-3 text-xs text-muted-foreground">
                              {k.last_used_at ? new Date(k.last_used_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US") : "-"}
                            </td>
                            <td className="py-3">
                              {k.is_active ? (
                                <span className="text-green-400 text-xs flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> {language === "tr" ? "AKTİF" : "ACTIVE"}
                                </span>
                              ) : (
                                <span className="text-red-400 text-xs flex items-center gap-1">
                                  <XCircle className="w-3 h-3" /> {language === "tr" ? "İPTAL" : "REVOKED"}
                                </span>
                              )}
                            </td>
                            <td className="py-3 text-right">
                              {k.is_active ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevokeBotApiKey(k.id, k.name)}
                                  className="text-red-400 hover:text-red-300 font-mono text-xs"
                                >
                                  <Ban className="w-3 h-3 mr-1" />
                                  {language === "tr" ? "İPTAL" : "REVOKE"}
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePurgeBotApiKey(k.id, k.name)}
                                  className="text-red-500 hover:text-red-400 font-mono text-xs"
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  {language === "tr" ? "KALICI SİL" : "DELETE"}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Top customers */}
              {botApiStats?.top_customers && botApiStats.top_customers.length > 0 && (
                <div className="glass p-6">
                  <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-500" />
                    {language === "tr" ? "En Aktif Müşteriler (TOP 20)" : "Top Customers (TOP 20)"}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm font-mono">
                      <thead className="text-xs uppercase text-muted-foreground border-b border-white/10">
                        <tr>
                          <th className="text-left py-2">Telegram ID</th>
                          <th className="text-left py-2">Username</th>
                          <th className="text-right py-2">{language === "tr" ? "Sipariş" : "Orders"}</th>
                          <th className="text-left py-2">{language === "tr" ? "Son" : "Last"}</th>
                          <th className="text-right py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {botApiStats.top_customers.map((c) => (
                          <tr key={c.telegram_user_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-2 text-xs">{c.telegram_user_id}</td>
                            <td className="py-2">{c.telegram_username || "-"}</td>
                            <td className="py-2 text-right">{c.orders}</td>
                            <td className="py-2 text-xs text-muted-foreground">
                              {c.last_order_at ? new Date(c.last_order_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US") : "-"}
                            </td>
                            <td className="py-2 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setBotApiFilterUserId(String(c.telegram_user_id));
                                  fetchBotApiOrders(1, String(c.telegram_user_id));
                                }}
                                className="text-xs font-mono"
                              >
                                <Search className="w-3 h-3 mr-1" />
                                {language === "tr" ? "FİLTRELE" : "FILTER"}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent bot orders */}
              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-mono font-bold flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-400" />
                    {language === "tr" ? "Son Bot Siparişleri" : "Recent Bot Orders"}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Telegram user_id"
                      value={botApiFilterUserId}
                      onChange={(e) => setBotApiFilterUserId(e.target.value)}
                      className="w-48 bg-black/50 border-white/10 rounded-none font-mono text-xs"
                    />
                    <Button
                      onClick={() => fetchBotApiOrders(1, botApiFilterUserId.trim())}
                      size="sm"
                      className="font-mono rounded-none"
                    >
                      <Search className="w-4 h-4 mr-1" />
                      {language === "tr" ? "FİLTRELE" : "FILTER"}
                    </Button>
                    {botApiFilterUserId && (
                      <Button
                        onClick={() => { setBotApiFilterUserId(""); fetchBotApiOrders(1, ""); }}
                        size="sm"
                        variant="ghost"
                        className="font-mono rounded-none"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      onClick={() => fetchBotApiOrders(botApiOrdersPage, botApiFilterUserId.trim())}
                      size="sm"
                      variant="outline"
                      className="font-mono rounded-none"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {botApiOrders.length === 0 ? (
                  <p className="text-sm text-muted-foreground font-mono">
                    {language === "tr" ? "Henüz bot siparişi yok." : "No bot orders yet."}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm font-mono">
                      <thead className="text-xs uppercase text-muted-foreground border-b border-white/10">
                        <tr>
                          <th className="text-left py-2">{language === "tr" ? "Tarih" : "Date"}</th>
                          <th className="text-left py-2">Telegram</th>
                          <th className="text-left py-2">Target URL</th>
                          <th className="text-left py-2">Anchors</th>
                          <th className="text-left py-2">{language === "tr" ? "Paket" : "Package"}</th>
                          <th className="text-right py-2">{language === "tr" ? "Toplam" : "Total"}</th>
                          <th className="text-right py-2 text-green-400">OK</th>
                          <th className="text-right py-2 text-red-400">FAIL</th>
                          <th className="text-right py-2 text-yellow-400">PROC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {botApiOrders.map((o) => (
                          <tr key={o.batch_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-2 text-xs text-muted-foreground">
                              {o.created_at ? new Date(o.created_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US") : "-"}
                            </td>
                            <td className="py-2 text-xs">
                              <div className="font-bold">{o.telegram_username || "-"}</div>
                              <div className="text-muted-foreground">{o.telegram_user_id}</div>
                            </td>
                            <td className="py-2 text-xs max-w-xs truncate">{o.target_url}</td>
                            <td className="py-2 text-xs max-w-xs truncate">{(o.anchors || []).join(", ")}</td>
                            <td className="py-2 text-xs uppercase">{o.package_label || "-"}</td>
                            <td className="py-2 text-right">{o.totals?.total || 0}</td>
                            <td className="py-2 text-right text-green-400">{o.totals?.active || 0}</td>
                            <td className="py-2 text-right text-red-400">{o.totals?.failed || 0}</td>
                            <td className="py-2 text-right text-yellow-400">{(o.totals?.pending || 0) + (o.totals?.processing || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {botApiOrdersTotal > 50 && (
                  <div className="mt-4 flex justify-center">
                    <DataPagination
                      currentPage={botApiOrdersPage}
                      totalPages={Math.ceil(botApiOrdersTotal / 50)}
                      onPageChange={(p) => fetchBotApiOrders(p, botApiFilterUserId.trim())}
                    />
                  </div>
                )}
              </div>

              {/* API doc link */}
              <div className="glass p-4 border-l-4 border-blue-500">
                <p className="text-sm font-mono">
                  {language === "tr"
                    ? "📘 Tam API dökümantasyonu için "
                    : "📘 Full API documentation: "}
                  <code className="text-blue-400">/var/www/root-seo/BOT_API.md</code>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "tr"
                    ? "Base URL: https://api.root-seo.com/api  •  Auth header: X-Bot-API-Key"
                    : "Base URL: https://api.root-seo.com/api  •  Auth header: X-Bot-API-Key"}
                </p>
              </div>

              {/* New API Key Dialog */}
              <Dialog open={botApiKeyDialogOpen} onOpenChange={(open) => {
                setBotApiKeyDialogOpen(open);
                if (!open) setBotApiNewKeyResult(null);
              }}>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-mono">
                      {botApiNewKeyResult
                        ? (language === "tr" ? "Yeni Bot API Key" : "New Bot API Key")
                        : (language === "tr" ? "Yeni Bot API Key Oluştur" : "Create Bot API Key")}
                    </DialogTitle>
                  </DialogHeader>

                  {!botApiNewKeyResult ? (
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="font-mono text-xs">{language === "tr" ? "Key Adı" : "Key Name"} *</Label>
                        <Input
                          value={botApiKeyForm.name}
                          onChange={(e) => setBotApiKeyForm({ ...botApiKeyForm, name: e.target.value })}
                          placeholder="Telegram Bot v1"
                          className="bg-black/50 border-white/10 rounded-none font-mono"
                          maxLength={80}
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs">{language === "tr" ? "Not (opsiyonel)" : "Note (optional)"}</Label>
                        <textarea
                          value={botApiKeyForm.note}
                          onChange={(e) => setBotApiKeyForm({ ...botApiKeyForm, note: e.target.value })}
                          rows={2}
                          maxLength={400}
                          placeholder={language === "tr" ? "Hangi bot, hangi sürüm vs." : "Which bot, version etc."}
                          className="w-full p-3 bg-black/50 border border-white/10 rounded-none font-mono text-sm resize-none focus:outline-none focus:border-primary"
                        />
                      </div>
                      <Button
                        onClick={handleCreateBotApiKey}
                        disabled={botApiKeyCreating || !botApiKeyForm.name.trim()}
                        className="w-full font-mono bg-primary text-black rounded-none"
                      >
                        {botApiKeyCreating ? (language === "tr" ? "OLUŞTURULUYOR…" : "CREATING…") : (language === "tr" ? "OLUŞTUR" : "CREATE")}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 py-4">
                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
                        <p className="text-sm text-yellow-300 font-mono mb-2">
                          ⚠️ {botApiNewKeyResult.warning}
                        </p>
                      </div>
                      <div>
                        <Label className="font-mono text-xs">{language === "tr" ? "Ad" : "Name"}</Label>
                        <div className="font-mono text-sm mt-1">{botApiNewKeyResult.name}</div>
                      </div>
                      <div>
                        <Label className="font-mono text-xs">Token</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            value={botApiNewKeyResult.token}
                            readOnly
                            className="bg-black/50 border-white/10 rounded-none font-mono text-xs"
                          />
                          <Button
                            onClick={() => copyToClipboard(botApiNewKeyResult.token)}
                            className="font-mono bg-primary text-black rounded-none whitespace-nowrap"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            COPY
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 font-mono">
                          {language === "tr" ? "Bota şu header ile gönderilir:" : "Send to bot with header:"} <code className="text-primary">X-Bot-API-Key: {botApiNewKeyResult.token_preview}</code>
                        </p>
                      </div>
                      <Button
                        onClick={() => { setBotApiKeyDialogOpen(false); setBotApiNewKeyResult(null); }}
                        className="w-full font-mono rounded-none"
                      >
                        {language === "tr" ? "KAPAT" : "CLOSE"}
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* System Settings Tab - Super Admin Only */}
          {activeTab === "system" && user?.role === "super_admin" && (
            <div className="space-y-6">
              <h1 className="text-2xl font-mono font-bold uppercase flex items-center gap-2">
                <Shield className="w-6 h-6 text-primary" />
                {t("admin.systemSettings")}
              </h1>
              <p className="text-muted-foreground">{t("admin.systemSettings.warning")}</p>

              {/* NOWPayments (Crypto) Settings */}
              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-mono font-bold flex items-center gap-2">
                    <Coins className="w-5 h-5 text-orange-500" />
                    {language === "tr" ? "Kripto Ödemeler (NOWPayments)" : "Crypto Payments (NOWPayments)"}
                  </h3>
                  <a 
                    href="https://nowpayments.io/payment-tools/api" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                  >
                    <HelpCircle className="w-3 h-3" />
                    {language === "tr" ? "API Key Al" : "Get API Key"}
                  </a>
                </div>
                
                <div className="mb-4 p-4 bg-white/5 rounded">
                  <p className="text-sm text-muted-foreground mb-2">{t("admin.seoApi.currentStatus")}:</p>
                  {systemSettings?.nowpayments?.is_configured ? (
                    <div className="flex items-center gap-2 text-green-500">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-mono">{t("admin.configured")} - {systemSettings.nowpayments.api_key}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-500">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-mono">{t("admin.notConfigured")} - {language === "tr" ? "Ödemeler devre dışı" : "Payments disabled"}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="font-mono text-xs">API Key</Label>
                    <Input 
                      type="password"
                      placeholder="XXXXXXXX-XXXXXXX-XXXXXXX-XXXXXXX"
                      value={nowPaymentsForm.api_key}
                      onChange={(e) => setNowPaymentsForm({...nowPaymentsForm, api_key: e.target.value})}
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      NOWPayments Dashboard → API Keys
                    </p>
                  </div>
                  <div>
                    <Label className="font-mono text-xs">IPN Secret Key</Label>
                    <Input 
                      type="password"
                      placeholder="your-ipn-secret-key"
                      value={nowPaymentsForm.ipn_secret}
                      onChange={(e) => setNowPaymentsForm({...nowPaymentsForm, ipn_secret: e.target.value})}
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === "tr" ? "NOWPayments Dashboard → Payment Settings → IPN Secret Key" : "NOWPayments Dashboard → Payment Settings → IPN Secret Key"}
                    </p>
                  </div>
                  <Button 
                    onClick={handleSaveNowPaymentsSettings}
                    disabled={savingSystemSettings || !nowPaymentsForm.api_key}
                    className="font-mono uppercase bg-orange-600 hover:bg-orange-700 text-white rounded-none"
                  >
                    {savingSystemSettings ? t("admin.seoApi.saving") : (language === "tr" ? "ÖDEME AYARLARINI KAYDET" : "SAVE PAYMENT SETTINGS")}
                  </Button>
                  
                  <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded mt-4">
                    <p className="text-xs text-orange-300">
                      <strong>{language === "tr" ? "Desteklenen:" : "Supported:"}</strong> USDT (TRC20), TRX, BTC, ETH, LTC, BUSD<br/>
                      <strong>{language === "tr" ? "Otomatik ödeme sayfası:" : "Automatic payment page:"}</strong> {language === "tr" ? "Kullanıcı NOWPayments sayfasına yönlendirilir" : "User is redirected to NOWPayments page"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Telegram Settings */}
              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-mono font-bold flex items-center gap-2">
                    <Bell className="w-5 h-5 text-blue-500" />
                    {t("admin.telegram.title")}
                  </h3>
                  <a 
                    href="https://core.telegram.org/bots#botfather" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <HelpCircle className="w-3 h-3" />
                    {t("admin.telegram.getKeyHere")}
                  </a>
                </div>
                
                <div className="mb-4 p-4 bg-white/5 rounded">
                  <p className="text-sm text-muted-foreground mb-2">{t("admin.seoApi.currentStatus")}:</p>
                  {systemSettings?.telegram?.is_configured ? (
                    <div className="flex items-center gap-2 text-green-500">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-mono">{t("admin.configured")} - Chat ID: {systemSettings.telegram.chat_id}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-500">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-mono">{t("admin.notConfigured")} - {language === "tr" ? "Bildirimler gönderilmeyecek" : "Notifications will not be sent"}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="font-mono text-xs">{t("admin.telegram.botToken")}</Label>
                    <Input 
                      type="password"
                      placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                      value={systemTelegramForm.bot_token}
                      onChange={(e) => setSystemTelegramForm({...systemTelegramForm, bot_token: e.target.value})}
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      @BotFather {language === "tr" ? "ile alınan bot token" : "bot token"}
                    </p>
                  </div>
                  <div>
                    <Label className="font-mono text-xs">{t("admin.telegram.chatId")}</Label>
                    <Input 
                      placeholder="-1001234567890 veya 123456789"
                      value={systemTelegramForm.chat_id}
                      onChange={(e) => setSystemTelegramForm({...systemTelegramForm, chat_id: e.target.value})}
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === "tr" ? "Bildirimlerin gönderileceği grup veya kullanıcı ID si" : "Group or user ID to send notifications to"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={systemTelegramForm.notifications_enabled}
                      onCheckedChange={(checked) => setSystemTelegramForm({...systemTelegramForm, notifications_enabled: checked})}
                    />
                    <Label className="font-mono text-xs">{t("admin.telegram.enabled")}</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSaveSystemTelegram}
                      disabled={savingSystemSettings || !systemTelegramForm.bot_token || !systemTelegramForm.chat_id}
                      className="font-mono uppercase bg-blue-600 hover:bg-blue-700 text-white rounded-none"
                    >
                      {savingSystemSettings ? t("admin.seoApi.saving") : t("admin.telegram.saveButton")}
                    </Button>
                    {systemSettings?.telegram?.is_configured && (
                      <Button 
                        variant="outline"
                        onClick={handleTestTelegram}
                        className="font-mono uppercase rounded-none"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Test Mesajı Gönder
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Send Notification */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-primary" />
                  {t("admin.telegram.sendNotification")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{t("admin.telegram.sendDesc")}</p>
                <div className="flex gap-4">
                  <Input
                    value={notificationMessage}
                    onChange={(e) => setNotificationMessage(e.target.value)}
                    placeholder={t("admin.telegram.placeholder")}
                    className="bg-black/50 border-white/10 rounded-none font-mono flex-1"
                  />
                  <Button 
                    onClick={handleSendNotification} 
                    disabled={!notificationMessage || !systemSettings?.telegram?.is_configured}
                    className="font-mono bg-primary text-black rounded-none"
                  >
                    <Send className="w-4 h-4 mr-2" /> {t("admin.telegram.send")}
                  </Button>
                </div>
                {!systemSettings?.telegram?.is_configured && (
                  <p className="text-xs text-yellow-500 mt-2">{t("admin.telegram.configureFirst")}</p>
                )}
              </div>

              {/* Maintenance Mode */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-red-500" />
                  {t("admin.maintenance.title")}
                </h3>
                
                <div className="mb-4 p-4 bg-white/5 rounded">
                  <p className="text-sm text-muted-foreground mb-2">{t("admin.maintenance.currentStatus")}:</p>
                  {maintenanceMode ? (
                    <div className="flex items-center gap-2 text-red-500">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-mono font-bold">{t("admin.maintenance.active")}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-500">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-mono">{t("admin.maintenance.inactive")}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-white/10 rounded">
                    <div>
                      <p className="font-mono font-bold">{t("admin.maintenance.enable")}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("admin.maintenance.enableDesc")}
                      </p>
                    </div>
                    <Switch
                      checked={maintenanceMode}
                      onCheckedChange={handleMaintenanceToggle}
                    />
                  </div>
                  
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
                    <p className="text-sm text-red-400">
                      <strong>{language === "tr" ? "Uyarı" : "Warning"}:</strong> {t("admin.maintenance.warning")}
                    </p>
                    <ul className="text-xs text-red-400/80 mt-2 space-y-1">
                      <li>• {t("admin.maintenance.warningItem1")}</li>
                      <li>• {t("admin.maintenance.warningItem2")}</li>
                      <li>• {t("admin.maintenance.warningItem3")}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* SEO API Settings */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  {t("admin.seoApi.title")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("admin.seoApi.description")}
                </p>

                {/* Current Status */}
                <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: "moz", label: "Moz", configured: seoApiSettings?.moz?.is_configured },
                    { key: "ahrefs", label: "Ahrefs", configured: seoApiSettings?.ahrefs?.is_configured },
                    { key: "semrush", label: "SEMrush", configured: seoApiSettings?.semrush?.is_configured },
                    { key: "dataforseo", label: "DataForSEO", configured: seoApiSettings?.dataforseo?.is_configured },
                    { key: "google_indexing", label: "Google Indexing", configured: seoApiSettings?.google_indexing?.is_configured },
                    { key: "indexnow", label: "IndexNow", configured: seoApiSettings?.indexnow?.is_configured },
                  ].map(item => (
                    <div key={item.key} className={`p-3 rounded border ${item.configured ? 'border-green-500/30 bg-green-500/10' : 'border-white/10 bg-white/5'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm">{item.label}</span>
                        {item.configured ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Moz */}
                <div className="space-y-4 mb-6 p-4 border border-white/10 rounded">
                  <div className="flex items-center justify-between">
                    <h4 className="font-mono font-bold text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      {t("admin.seoApi.mozTitle")}
                    </h4>
                    <a 
                      href="https://moz.com/products/api" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                      <HelpCircle className="w-3 h-3" />
                      {t("admin.seoApi.getKeyHere")}
                    </a>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("admin.seoApi.mozDesc")}</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs">{t("admin.seoApi.accessId")}</Label>
                      <Input 
                        type="text"
                        placeholder="mozscape-xxxxxxxx"
                        value={seoApiForm.moz_access_id}
                        onChange={(e) => setSeoApiForm({...seoApiForm, moz_access_id: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{t("admin.seoApi.secretKey")}</Label>
                      <Input 
                        type="password"
                        placeholder="••••••••"
                        value={seoApiForm.moz_secret_key}
                        onChange={(e) => setSeoApiForm({...seoApiForm, moz_secret_key: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Ahrefs & SEMrush */}
                <div className="space-y-4 mb-6 p-4 border border-white/10 rounded">
                  <div className="flex items-center justify-between">
                    <h4 className="font-mono font-bold text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      {t("admin.seoApi.ahrefsSemrushTitle")}
                    </h4>
                    <div className="flex gap-3">
                      <a 
                        href="https://ahrefs.com/api" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                      >
                        <HelpCircle className="w-3 h-3" />
                        Ahrefs
                      </a>
                      <a 
                        href="https://www.semrush.com/api-analytics/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                      >
                        <HelpCircle className="w-3 h-3" />
                        SEMrush
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("admin.seoApi.ahrefsSemrushDesc")}</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs">Ahrefs {t("admin.seoApi.apiKey")}</Label>
                      <Input 
                        type="password"
                        placeholder="••••••••"
                        value={seoApiForm.ahrefs_api_key}
                        onChange={(e) => setSeoApiForm({...seoApiForm, ahrefs_api_key: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">SEMrush {t("admin.seoApi.apiKey")}</Label>
                      <Input 
                        type="password"
                        placeholder="••••••••"
                        value={seoApiForm.semrush_api_key}
                        onChange={(e) => setSeoApiForm({...seoApiForm, semrush_api_key: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* DataForSEO */}
                <div className="space-y-4 mb-6 p-4 border border-white/10 rounded">
                  <div className="flex items-center justify-between">
                    <h4 className="font-mono font-bold text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      {t("admin.seoApi.dataforseoTitle")}
                    </h4>
                    <a 
                      href="https://dataforseo.com/apis" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                    >
                      <HelpCircle className="w-3 h-3" />
                      {t("admin.seoApi.getKeyHere")}
                    </a>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("admin.seoApi.dataforseoDesc")}</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs">{t("admin.seoApi.login")}</Label>
                      <Input 
                        type="text"
                        placeholder="your@email.com"
                        value={seoApiForm.dataforseo_login}
                        onChange={(e) => setSeoApiForm({...seoApiForm, dataforseo_login: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{t("admin.seoApi.password")}</Label>
                      <Input 
                        type="password"
                        placeholder="••••••••"
                        value={seoApiForm.dataforseo_password}
                        onChange={(e) => setSeoApiForm({...seoApiForm, dataforseo_password: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Google Indexing & IndexNow */}
                <div className="space-y-4 mb-6 p-4 border border-white/10 rounded">
                  <div className="flex items-center justify-between">
                    <h4 className="font-mono font-bold text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      {t("admin.seoApi.indexingTitle")}
                    </h4>
                    <div className="flex gap-3">
                      <a 
                        href="https://console.cloud.google.com/apis/library/indexing.googleapis.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                      >
                        <HelpCircle className="w-3 h-3" />
                        Google
                      </a>
                      <a 
                        href="https://www.indexnow.org/documentation" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                      >
                        <HelpCircle className="w-3 h-3" />
                        IndexNow
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("admin.seoApi.indexingDesc")}</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs">{t("admin.seoApi.googleIndexingKey")}</Label>
                      <Input 
                        type="password"
                        placeholder={t("admin.seoApi.googleIndexingPlaceholder")}
                        value={seoApiForm.google_indexing_api_key}
                        onChange={(e) => setSeoApiForm({...seoApiForm, google_indexing_api_key: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{t("admin.seoApi.indexnowKey")}</Label>
                      <Input 
                        type="text"
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        value={seoApiForm.indexnow_api_key}
                        onChange={(e) => setSeoApiForm({...seoApiForm, indexnow_api_key: e.target.value})}
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveSeoApiSettings}
                  disabled={savingSystemSettings}
                  className="font-mono uppercase bg-purple-600 hover:bg-purple-700 text-white rounded-none"
                >
                  {savingSystemSettings ? t("admin.seoApi.saving") : t("admin.seoApi.saveButton")}
                </Button>
              </div>

              {/* Connector Settings */}
              <div className="glass p-6">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Code className="w-5 h-5 text-cyan-500" />
                  {language === "tr" ? "Connector Dosyası" : "Connector File"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {language === "tr" 
                    ? "Bu PHP dosyasını sitelerinize yükleyin. Sistem otomatik olarak site bilgilerini, DA/PA değerlerini ve footer'ı tespit edecek." 
                    : "Upload this PHP file to your sites. The system will automatically detect site information, DA/PA values and footer."}
                </p>

                {/* Download Button */}
                <div className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <h4 className="font-mono font-bold text-cyan-400 mb-1">
                        {language === "tr" ? "1. Connector Dosyasını İndirin" : "1. Download Connector File"}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {language === "tr" 
                          ? "Bu dosyayı sitenizin herhangi bir klasörüne yükleyin (örn: /images/, /js/, /wp-content/)" 
                          : "Upload this file to any folder on your site (e.g., /images/, /js/, /wp-content/)"}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={async () => {
                          try {
                            const response = await api.get('/admin/connector/download?version=v5&artifact=php', { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', connectorSettings.connector_filename || 'seo-connector.php');
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            toast.success(language === "tr" ? "PHP connector indirildi!" : "PHP connector downloaded!");
                          } catch (err) {
                            toast.error(language === "tr" ? "İndirme başarısız" : "Download failed");
                          }
                        }}
                        className="font-mono rounded-none bg-cyan-600 hover:bg-cyan-700 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PHP
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            const response = await api.get('/admin/connector/download?version=v5&artifact=js', { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', 'seo-connector.js');
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            toast.success(language === "tr" ? "JS connector indirildi!" : "JS connector downloaded!");
                          } catch (err) {
                            toast.error(language === "tr" ? "İndirme başarısız" : "Download failed");
                          }
                        }}
                        className="font-mono rounded-none bg-slate-700 hover:bg-slate-600 text-white"
                      >
                        <Code className="w-4 h-4 mr-2" />
                        JS
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Filename Setting */}
                <div className="mb-4">
                  <Label className="font-mono text-xs">{language === "tr" ? "İndirilen Dosya Adı" : "Downloaded File Name"}</Label>
                  <Input 
                    type="text"
                    placeholder="seo-config.php"
                    value={connectorSettings.connector_filename}
                    onChange={(e) => setConnectorSettings({...connectorSettings, connector_filename: e.target.value})}
                    className="bg-black/50 border-white/10 rounded-none font-mono max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {language === "tr" ? "Şüphe çekmemesi için farklı isimler kullanabilirsiniz" : "Use different names to avoid suspicion"}
                  </p>
                </div>

                <Button 
                  onClick={handleSaveConnectorSettings}
                  disabled={savingSystemSettings}
                  size="sm"
                  className="font-mono uppercase bg-cyan-600 hover:bg-cyan-700 text-white rounded-none"
                >
                  {savingSystemSettings ? t("admin.seoApi.saving") : t("common.save")}
                </Button>

                {/* Instructions */}
                <div className="mt-6 p-4 bg-white/5 rounded">
                  <h4 className="font-mono font-bold text-sm mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    {language === "tr" ? "Nasıl Çalışır?" : "How It Works?"}
                  </h4>
                  <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                    <li>{language === "tr" ? "Connector dosyasını indirin" : "Download the connector file"}</li>
                    <li>{language === "tr" ? "Sitenize yükleyin (herhangi bir klasöre)" : "Upload to your site (any folder)"}</li>
                    <li>{language === "tr" ? "URL'yi sisteme ekleyin (örn: site.com/images/seo.php)" : "Add URL to system (e.g., site.com/images/seo.php)"}</li>
                    <li>{language === "tr" ? "Sistem otomatik olarak:" : "System automatically:"}</li>
                  </ol>
                  <ul className="text-xs text-muted-foreground mt-2 ml-4 space-y-1">
                    <li>✓ {language === "tr" ? "Bağlantı kurar ve doğrular" : "Connects and verifies"}</li>
                    <li>✓ {language === "tr" ? "Site adı, dil, ülke tespit eder" : "Detects site name, language, country"}</li>
                    <li>✓ {language === "tr" ? "DA/PA/SS/Traffic değerlerini çeker (API'ler yapılandırıldıysa)" : "Fetches DA/PA/SS/Traffic values (if APIs configured)"}</li>
                    <li>✓ {language === "tr" ? "WordPress/PHP/Static türünü belirler" : "Determines WordPress/PHP/Static type"}</li>
                    <li>✓ {language === "tr" ? "Footer'ı tespit eder" : "Detects footer"}</li>
                    <li>✓ {language === "tr" ? "Satış listesine ekler" : "Adds to sales list"}</li>
                  </ul>
                </div>
              </div>

              {/* Info Box */}
              <div className="glass p-6 border-l-4 border-yellow-500">
                <h3 className="font-mono font-bold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  {language === "tr" ? "Önemli Bilgiler" : "Important Information"}
                </h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• <strong>NOWPayments:</strong> {language === "tr" ? "API Key ve IPN Secret için nowpayments.io dashboard'dan alın." : "Get API Key and IPN Secret from nowpayments.io dashboard."}</li>
                  <li>• <strong>Telegram Bot:</strong> {language === "tr" ? "@BotFather ile /newbot yazarak yeni bot oluşturabilirsiniz." : "Create a new bot by typing /newbot to @BotFather."}</li>
                  <li>• <strong>Chat ID:</strong> {language === "tr" ? "@userinfobot ile mesaj atarak kendi ID'nizi öğrenebilirsiniz. Grup için botu gruba ekleyip mesaj atın." : "Get your ID by messaging @userinfobot. For groups, add the bot to the group and send a message."}</li>
                  <li>• <strong>{language === "tr" ? "Güvenlik" : "Security"}:</strong> {language === "tr" ? "Bu ayarlar veritabanında şifrelenmiş olarak saklanır." : "These settings are stored encrypted in the database."}</li>
                </ul>
              </div>

              {/* Support Settings */}
              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-mono font-bold flex items-center gap-2">
                    <HeadphonesIcon className="w-5 h-5 text-cyan-500" />
                    {language === "tr" ? "Destek Ayarları" : "Support Settings"}
                  </h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  {language === "tr" 
                    ? "Müşterilerin Telegram üzerinden ulaşabileceği admin hesapları ve çalışma saatleri ayarları." 
                    : "Admin accounts customers can reach via Telegram and working hours settings."}
                </p>

                <div className="space-y-4">
                  <div>
                    <Label className="font-mono text-xs">
                      {language === "tr" ? "Telegram Kullanıcı Adları (Destek için)" : "Telegram Usernames (For Support)"}
                    </Label>
                    <textarea
                      value={supportTelegramInput}
                      onChange={(e) => setSupportTelegramInput(e.target.value)}
                      placeholder={language === "tr" ? "Her satıra bir kullanıcı adı (@ olmadan)\nadmin1\nadmin2" : "One username per line (without @)\nadmin1\nadmin2"}
                      rows={3}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded font-mono text-sm resize-none focus:outline-none focus:border-primary"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === "tr" 
                        ? "Bu kullanıcı adları müşteri destek sayfasında gösterilecek" 
                        : "These usernames will be shown on customer support page"}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs">{language === "tr" ? "Destek Email" : "Support Email"}</Label>
                      <Input
                        value={supportSettings.support_email || ""}
                        onChange={(e) => setSupportSettings(prev => ({ ...prev, support_email: e.target.value }))}
                        placeholder="support@example.com"
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs">{language === "tr" ? "Çalışma Saatleri" : "Working Hours"}</Label>
                      <Input
                        value={supportSettings.working_hours || ""}
                        onChange={(e) => setSupportSettings(prev => ({ ...prev, working_hours: e.target.value }))}
                        placeholder="09:00 - 18:00"
                        className="bg-black/50 border-white/10 rounded-none font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-mono text-xs">{language === "tr" ? "Tahmini Yanıt Süresi" : "Estimated Response Time"}</Label>
                    <Input
                      value={supportSettings.response_time || ""}
                      onChange={(e) => setSupportSettings(prev => ({ ...prev, response_time: e.target.value }))}
                      placeholder={language === "tr" ? "24 saat içinde" : "Within 24 hours"}
                      className="bg-black/50 border-white/10 rounded-none font-mono"
                    />
                  </div>
                  <Button 
                    onClick={async () => {
                      try {
                        const usernames = supportTelegramInput.split("\n").map(u => u.trim()).filter(u => u);
                        await api.put("/admin/system-settings/support", {
                          telegram_usernames: usernames,
                          support_email: supportSettings.support_email || "",
                          working_hours: supportSettings.working_hours || "09:00 - 18:00",
                          response_time: supportSettings.response_time || (language === "tr" ? "24 saat içinde" : "Within 24 hours")
                        });
                        toast.success(language === "tr" ? "Destek ayarları kaydedildi" : "Support settings saved");
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Error");
                      }
                    }}
                    className="font-mono uppercase bg-cyan-600 hover:bg-cyan-700 text-white rounded-none"
                  >
                    {language === "tr" ? "DESTEK AYARLARINI KAYDET" : "SAVE SUPPORT SETTINGS"}
                  </Button>
                </div>
              </div>

              {false && (
              <div className="glass p-6 border-l-4 border-purple-500">
                <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-purple-500" />
                  {language === "tr" ? "Yedekleme & Taşıma" : "Backup & Migration"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {language === "tr" 
                    ? "Tüm siteleri, siparişleri, ayarları ve site key'leri yedekleyin. Yeni sunucuya taşırken connector'lar çalışmaya devam eder." 
                    : "Backup all sites, orders, settings and site keys. Connectors will continue working when migrating to new server."}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Full Backup */}
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded">
                    <h4 className="font-mono font-bold text-purple-400 mb-2">
                      {language === "tr" ? "Tam Yedek" : "Full Backup"}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      {language === "tr" 
                        ? "Tüm veriler: siteler, kullanıcılar, siparişler, ayarlar, API key'ler" 
                        : "All data: sites, users, orders, settings, API keys"}
                    </p>
                    <Button
                      onClick={async () => {
                        try {
                          toast.loading(language === "tr" ? "Yedek hazırlanıyor..." : "Preparing backup...", { id: "backup" });
                          const res = await api.get("/admin/backup/export");
                          const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `rootseo-backup-${new Date().toISOString().split('T')[0]}.json`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.dismiss("backup");
                          toast.success(`${language === "tr" ? "Yedek indirildi!" : "Backup downloaded!"} (${res.data.stats?.total_sites || 0} site)`);
                        } catch (err) {
                          toast.dismiss("backup");
                          toast.error(err.response?.data?.detail || (language === "tr" ? "Yedekleme hatası" : "Backup error"));
                        }
                      }}
                      className="w-full font-mono rounded-none bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {language === "tr" ? "TAM YEDEK İNDİR" : "DOWNLOAD FULL BACKUP"}
                    </Button>
                  </div>

                  {/* Sites Only Backup */}
                  <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded">
                    <h4 className="font-mono font-bold text-cyan-400 mb-2">
                      {language === "tr" ? "Sadece Siteler" : "Sites Only"}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      {language === "tr" 
                        ? "Hızlı yedek: sadece siteler ve site key'ler" 
                        : "Quick backup: only sites and site keys"}
                    </p>
                    <Button
                      onClick={async () => {
                        try {
                          toast.loading(language === "tr" ? "Hazırlanıyor..." : "Preparing...", { id: "backup-sites" });
                          const res = await api.get("/admin/backup/sites-only");
                          const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `rootseo-sites-${new Date().toISOString().split('T')[0]}.json`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.dismiss("backup-sites");
                          toast.success(`${res.data.total_sites} ${language === "tr" ? "site yedeklendi" : "sites backed up"}`);
                        } catch (err) {
                          toast.dismiss("backup-sites");
                          toast.error(language === "tr" ? "Hata" : "Error");
                        }
                      }}
                      className="w-full font-mono rounded-none bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {language === "tr" ? "SİTELERİ YEDEKLE" : "BACKUP SITES"}
                    </Button>
                  </div>
                </div>

                {/* Restore Section */}
                <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded">
                  <h4 className="font-mono font-bold text-orange-400 mb-2">
                    {language === "tr" ? "Yedeği Geri Yükle" : "Restore Backup"}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    {language === "tr" 
                      ? "JSON yedek dosyasını seçin. Site key'ler korunur, connector'lar çalışmaya devam eder." 
                      : "Select JSON backup file. Site keys are preserved, connectors continue working."}
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept=".json"
                      id="backup-file-input"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        try {
                          toast.loading(language === "tr" ? "Yükleniyor..." : "Restoring...", { id: "restore" });
                          const text = await file.text();
                          const backupData = JSON.parse(text);
                          
                          // Determine restore type
                          if (backupData.type === "sites_only") {
                            const res = await api.post("/admin/backup/sites-only", backupData);
                            toast.dismiss("restore");
                            toast.success(`${res.data.imported} ${language === "tr" ? "site eklendi" : "sites added"}, ${res.data.updated} ${language === "tr" ? "güncellendi" : "updated"}`);
                          } else {
                            const res = await api.post("/admin/backup/restore", { 
                              backup_data: backupData,
                              clear_existing: false,
                              skip_users: false
                            });
                            toast.dismiss("restore");
                            if (res.data.errors?.length > 0) {
                              toast.success(`${language === "tr" ? "Geri yüklendi" : "Restored"} (${res.data.errors.length} ${language === "tr" ? "hata" : "errors"})`);
                            } else {
                              toast.success(language === "tr" ? "Başarıyla geri yüklendi!" : "Successfully restored!");
                            }
                          }
                          refreshAllData();
                        } catch (err) {
                          toast.dismiss("restore");
                          toast.error(err.response?.data?.detail || (language === "tr" ? "Geri yükleme hatası" : "Restore error"));
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button
                      onClick={() => document.getElementById('backup-file-input')?.click()}
                      variant="outline"
                      className="flex-1 font-mono rounded-none border-orange-500 text-orange-400 hover:bg-orange-500/20"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {language === "tr" ? "YEDEK DOSYASI SEÇ" : "SELECT BACKUP FILE"}
                    </Button>
                  </div>
                </div>

                {/* Migration Instructions */}
                <div className="mt-4 p-3 bg-white/5 rounded text-xs text-muted-foreground">
                  <p className="font-bold mb-2">{language === "tr" ? "Yeni Sunucuya Taşıma:" : "Migration to New Server:"}</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>{language === "tr" ? "Eski sunucudan TAM YEDEK indir" : "Download FULL BACKUP from old server"}</li>
                    <li>{language === "tr" ? "Yeni sunucuya ROOT-SEO kur" : "Install ROOT-SEO on new server"}</li>
                    <li>{language === "tr" ? "Yedek dosyasını yükle" : "Upload backup file"}</li>
                    <li>{language === "tr" ? "Connector'lar otomatik çalışır (site key'ler aynı)" : "Connectors work automatically (same site keys)"}</li>
                  </ol>
                </div>
              </div>
              )}
            </div>
          )}

        </div>
      </main>

      {/* Ticket Detail Dialog */}
      <Dialog open={ticketDetailOpen} onOpenChange={setTicketDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              {selectedTicket?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="py-4">
              {/* Ticket Info */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground font-mono mb-4 p-3 bg-white/5 rounded">
                <span>
                  <strong>{language === "tr" ? "Müşteri:" : "Customer:"}</strong> {selectedTicket.user_name} ({selectedTicket.user_email})
                </span>
                <span>
                  <strong>{language === "tr" ? "Kategori:" : "Category:"}</strong> {selectedTicket.category}
                </span>
                <span>
                  <strong>{language === "tr" ? "Oluşturma:" : "Created:"}</strong> {new Date(selectedTicket.created_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US")}
                </span>
              </div>

              {/* Status Update */}
              <div className="flex items-center gap-4 mb-4">
                <Select 
                  value={selectedTicket.status} 
                  onValueChange={async (status) => {
                    try {
                      await api.put(`/admin/tickets/${selectedTicket.id}`, { status });
                      setSelectedTicket(prev => ({ ...prev, status }));
                      toast.success(language === "tr" ? "Durum güncellendi" : "Status updated");
                      fetchTickets(ticketsPage, ticketsSearch, ticketsStatusFilter);
                    } catch (error) {
                      toast.error("Error");
                    }
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">{language === "tr" ? "Açık" : "Open"}</SelectItem>
                    <SelectItem value="in_progress">{language === "tr" ? "İşlemde" : "In Progress"}</SelectItem>
                    <SelectItem value="resolved">{language === "tr" ? "Çözüldü" : "Resolved"}</SelectItem>
                    <SelectItem value="closed">{language === "tr" ? "Kapatıldı" : "Closed"}</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={selectedTicket.priority} 
                  onValueChange={async (priority) => {
                    try {
                      await api.put(`/admin/tickets/${selectedTicket.id}`, { priority });
                      setSelectedTicket(prev => ({ ...prev, priority }));
                      toast.success(language === "tr" ? "Öncelik güncellendi" : "Priority updated");
                      fetchTickets(ticketsPage, ticketsSearch, ticketsStatusFilter);
                    } catch (error) {
                      toast.error("Error");
                    }
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{language === "tr" ? "Düşük" : "Low"}</SelectItem>
                    <SelectItem value="medium">{language === "tr" ? "Orta" : "Medium"}</SelectItem>
                    <SelectItem value="high">{language === "tr" ? "Yüksek" : "High"}</SelectItem>
                    <SelectItem value="urgent">{language === "tr" ? "Acil" : "Urgent"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Messages */}
              <div className="space-y-4 mb-6 max-h-[35vh] overflow-y-auto">
                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      ["admin", "super_admin"].includes(msg.sender_role)
                        ? "bg-primary/10 border border-primary/20 ml-8"
                        : "bg-white/5 border border-white/10 mr-8"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        ["admin", "super_admin"].includes(msg.sender_role) ? "bg-primary/20" : "bg-white/10"
                      }`}>
                        <User className="w-3 h-3" />
                      </div>
                      <span className="font-mono text-sm font-semibold">{msg.sender_name || "User"}</span>
                      {["admin", "super_admin"].includes(msg.sender_role) && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded font-mono">ADMIN</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(msg.created_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US")}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== "closed" && (
                <div className="border-t border-white/10 pt-4">
                  <textarea
                    value={ticketReplyMessage}
                    onChange={(e) => setTicketReplyMessage(e.target.value)}
                    placeholder={language === "tr" ? "Yanıtınızı yazın..." : "Type your reply..."}
                    rows={3}
                    className="w-full p-3 bg-white/5 border border-white/10 rounded font-mono text-sm resize-none focus:outline-none focus:border-primary"
                  />
                  <Button 
                    onClick={async () => {
                      if (!ticketReplyMessage.trim()) return;
                      setSendingTicketReply(true);
                      try {
                        await api.post(`/tickets/${selectedTicket.id}/reply`, { message: ticketReplyMessage });
                        toast.success(language === "tr" ? "Yanıt gönderildi" : "Reply sent");
                        setTicketReplyMessage("");
                        const res = await api.get(`/tickets/${selectedTicket.id}`);
                        setSelectedTicket(res.data);
                        fetchTickets(ticketsPage, ticketsSearch, ticketsStatusFilter);
                      } catch (error) {
                        toast.error("Error");
                      } finally {
                        setSendingTicketReply(false);
                      }
                    }}
                    disabled={sendingTicketReply || !ticketReplyMessage.trim()}
                    className="mt-3 font-mono"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingTicketReply ? "..." : (language === "tr" ? "YANITLA" : "REPLY")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {editingAnnouncement 
                ? (language === "tr" ? "Duyuru Düzenle" : "Edit Announcement")
                : (language === "tr" ? "Yeni Duyuru" : "New Announcement")
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-mono text-xs">{language === "tr" ? "Başlık (TR)" : "Title (TR)"}</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
                className="font-mono"
              />
            </div>
            <div>
              <Label className="font-mono text-xs">{language === "tr" ? "Başlık (EN)" : "Title (EN)"}</Label>
              <Input
                value={announcementForm.title_en}
                onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title_en: e.target.value }))}
                className="font-mono"
                placeholder={language === "tr" ? "Opsiyonel" : "Optional"}
              />
            </div>
            <div>
              <Label className="font-mono text-xs">{language === "tr" ? "İçerik (TR)" : "Content (TR)"}</Label>
              <textarea
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm(prev => ({ ...prev, content: e.target.value }))}
                rows={3}
                className="w-full p-3 bg-white/5 border border-white/10 rounded font-mono text-sm resize-none focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <Label className="font-mono text-xs">{language === "tr" ? "İçerik (EN)" : "Content (EN)"}</Label>
              <textarea
                value={announcementForm.content_en}
                onChange={(e) => setAnnouncementForm(prev => ({ ...prev, content_en: e.target.value }))}
                rows={3}
                className="w-full p-3 bg-white/5 border border-white/10 rounded font-mono text-sm resize-none focus:outline-none focus:border-primary"
                placeholder={language === "tr" ? "Opsiyonel" : "Optional"}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-xs">{language === "tr" ? "Tip" : "Type"}</Label>
                <Select 
                  value={announcementForm.type} 
                  onValueChange={(val) => setAnnouncementForm(prev => ({ ...prev, type: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">{language === "tr" ? "Bilgi" : "Info"}</SelectItem>
                    <SelectItem value="warning">{language === "tr" ? "Uyarı" : "Warning"}</SelectItem>
                    <SelectItem value="success">{language === "tr" ? "Başarı" : "Success"}</SelectItem>
                    <SelectItem value="error">{language === "tr" ? "Hata" : "Error"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono text-xs">{language === "tr" ? "Bitiş Tarihi" : "Expiration"}</Label>
                <Input
                  type="datetime-local"
                  value={announcementForm.expires_at}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, expires_at: e.target.value }))}
                  className="font-mono"
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={announcementForm.is_active}
                  onCheckedChange={(val) => setAnnouncementForm(prev => ({ ...prev, is_active: val }))}
                />
                <Label className="font-mono text-xs">{language === "tr" ? "Aktif" : "Active"}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={announcementForm.show_on_dashboard}
                  onCheckedChange={(val) => setAnnouncementForm(prev => ({ ...prev, show_on_dashboard: val }))}
                />
                <Label className="font-mono text-xs">{language === "tr" ? "Dashboard'da Göster" : "Show on Dashboard"}</Label>
              </div>
            </div>
            <Button 
              onClick={async () => {
                if (!announcementForm.title.trim() || !announcementForm.content.trim()) {
                  toast.error(language === "tr" ? "Başlık ve içerik zorunlu" : "Title and content required");
                  return;
                }
                try {
                  if (editingAnnouncement) {
                    await api.put(`/admin/announcements/${editingAnnouncement.id}`, announcementForm);
                    toast.success(language === "tr" ? "Duyuru güncellendi" : "Announcement updated");
                  } else {
                    await api.post("/admin/announcements", announcementForm);
                    toast.success(language === "tr" ? "Duyuru oluşturuldu" : "Announcement created");
                  }
                  setAnnouncementDialogOpen(false);
                  fetchAnnouncements(announcementsPage);
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Error");
                }
              }}
              className="w-full font-mono"
            >
              {editingAnnouncement 
                ? (language === "tr" ? "GÜNCELLE" : "UPDATE")
                : (language === "tr" ? "OLUŞTUR" : "CREATE")
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
